/**
 * ================================================================================================
 *  EVERY STRING THE PRODUCT SHOWS, IN BOTH LANGUAGES, IN ONE PLACE. **Master §28.**
 * ================================================================================================
 *
 * §28, verbatim: *"Hebrew default RTL. English toggle LTR. Language is user-level. ILS default
 * display currency; display/report currency can change only with explicit FX conversion/source/
 * as-of. Base/reporting currency rules are distinct from language."*
 *
 * ── WHY THIS EXISTS BEFORE THE SCREENS DO ───────────────────────────────────────────────────────
 *
 * Today the product writes Hebrew inline and suffixes the identifier: `titleHe`, `bodyHe`,
 * `nextStepHe`, `FIXTURE_NOTICE_HE`. `index.html` hard-codes `lang="he" dir="rtl"`. That is a
 * product with one language, and it is a reasonable place to have started.
 *
 * It is not where seventeen more screens should be built. Retrofitting a lookup after the fact
 * means reopening every screen, and the suffix convention actively resists it: `titleHe` is not a
 * key, it is a FIELD, so a second language has nowhere to go except a second field beside it.
 *
 * ── COMPLETENESS IS A COMPILE ERROR, NOT A RUNTIME FALLBACK ─────────────────────────────────────
 *
 * `satisfies` forces every entry to carry BOTH locales. A missing translation does not fall back
 * to Hebrew at runtime where nobody would notice it in an English session -- it fails to build.
 * A fallback is how a half-translated product ships looking finished.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────────────────────────
 *
 * NUMBERS, DATES AND CURRENCY. §28 keeps currency rules distinct from language, and §40 governs
 * numeric display -- a catalogue that formatted an amount would make ILS follow the interface
 * language, which is exactly the confusion §28 separates. Formatting belongs to its own module.
 *
 * SERVER TEXT. A refusal reason, a deny classification and an audit action come from the API with
 * their own vocabulary. Translating them here would put a second, drifting copy of the gate's
 * vocabulary in the client.
 */

export const LOCALES = ['he', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Hebrew is the default, and that is a product decision rather than a deployment accident. */
export const DEFAULT_LOCALE: Locale = 'he';

/** §28 pairs the language with its direction. One place, so the two can never disagree. */
export const DIRECTION: Readonly<Record<Locale, 'rtl' | 'ltr'>> = {
  he: 'rtl',
  en: 'ltr',
};

/** The name of each language IN that language -- never "Hebrew" written in English to a Hebrew reader. */
export const LOCALE_NAME: Readonly<Record<Locale, string>> = {
  he: 'עברית',
  en: 'English',
};

/**
 * The catalogue. Keys are dotted and name the SURFACE, not the sentence: `shell.signOut` survives
 * a copy change, `shell.logOutOfYourAccount` does not.
 */
const TEXT = {
  // ---- The shell chrome -------------------------------------------------------------------
  'shell.platformConsole': { he: 'קונסולת הפלטפורמה', en: 'Platform Console' },
  'shell.myWorkspace': { he: 'סביבת העבודה שלי', en: 'My Workspace' },
  'shell.signOut': { he: 'יציאה', en: 'Sign out' },
  'shell.search': { he: 'חיפוש', en: 'Search' },
  'shell.documents': { he: 'מסמכים', en: 'Documents' },
  'shell.language': { he: 'שפה', en: 'Language' },
  'shell.favourites': { he: 'המועדפים שלי', en: 'My favourites' },
  'shell.more': { he: 'עוד', en: 'More' },
  /*
   * THE ONE WORD ON THE BACK BUTTON. **Section 1, 2026-10-27.**
   *
   * `context.backTo` is the SENTENCE — *"חזרה ל"* + the screen's own name — and it is still used,
   * as the button's accessible name and tooltip. This is the visible label, which is one word on
   * purpose: the button has to be the same size in the same place on every screen.
   */
  // ---- THE PUBLIC PAGE, section 8, 2026-10-27 ------------------------------------------------
  //
  // Short. The Owner's standing rule about the product's own screens applies to the page that
  // introduces it: *"כותרות לפעולות מבלי לתת הסברים מיותרים"*. Three claims, each one thing the
  // product actually does today, and no promise that depends on something unbuilt.
  'welcome.title': { he: 'GL-POINT — ניהול פיננסי לארגון ולחברות שלו', en: 'GL-POINT — financial management for an organisation and its companies' },
  'welcome.lead': {
    he: 'סביבה אחת לארגון, לחברות שבתוכו, למשתמשים ולהרשאות — עם חיבור למערכות הנהלת החשבונות.',
    en: 'One environment for the organisation, its companies, its people and their permissions — connected to the accounting systems.',
  },
  'welcome.signIn': { he: 'כניסה', en: 'Sign in' },
  'welcome.point1Title': { he: 'ארגון וחברות', en: 'Organisation and companies' },
  'welcome.point1Body': {
    he: 'רישום החברות של הארגון, כרטיס לכל חברה והגדרות במקום אחד.',
    en: 'The organisation register of companies, a record per company and its settings in one place.',
  },
  'welcome.point2Title': { he: 'משתמשים והרשאות', en: 'Users and permissions' },
  'welcome.point2Body': {
    he: 'תפקידים, גישה לחברות לפי רמה, ויומן ביקורת לכל שינוי.',
    en: 'Roles, company access by level, and an audit journal for every change.',
  },
  'welcome.point3Title': { he: 'חיבור למערכות', en: 'Connections' },
  'welcome.point3Body': {
    he: 'חיבור למערכת הנהלת החשבונות של הארגון, עם בדיקת חיבור וקישור חברות.',
    en: "A connection to the organisation's accounting system, with a connection test and company linking.",
  },
  'welcome.signUpTitle': { he: 'פתיחת חשבון', en: 'Opening an account' },
  /*
   * ו-16, 2026-10-30. The Owner: *"הדף הציבורי יציג כניסה והרשמה. כשההרשמה סגורה, הצג הודעה קצרה
   * וברורה שהיא אינה זמינה כרגע, בלי כפתור שמוביל למבוי סתום."* So the slot has two bodies and one action, and
   * which one renders is read from the server rather than decided here.
   *
   * THE CLOSED BODY NO LONGER NAMES AN ADDRESS. It used to print the Owner's own, which was the one
   * live entry in the guardrail exemption register -- held open, dated, pending this very answer.
   * The answer asks for a short clear sentence and not a contact line, so the sentence is short and
   * the entry is closed. If a public contact line is wanted later it is estate configuration under
   * §12.11, like the five sender keys, and not a literal in a screen.
   */
  'welcome.signUpBody': {
    he: 'הרשמה עצמית אינה זמינה כרגע.',
    en: 'Self-registration is not available at the moment.',
  },
  'welcome.signUpOpenBody': {
    he: 'אפשר לפתוח ארגון חדש כאן. המחיר, מחזור החיוב ותקופת הניסיון מוצגים לפני שמתחילים.',
    en: 'You can open a new organisation here. The price, the billing cycle and the trial period are shown before you begin.',
  },
  'welcome.signUpAction': { he: 'פתיחת ארגון חדש', en: 'Open a new organisation' },

  'shell.back': { he: 'חזרה', en: 'Back' },

  // ---- THE ENVIRONMENT INDICATOR. It replaces the prototype demo ribbon. --------------------
  //
  // IT NAMES THE PLACE AND NOTHING ELSE. An earlier version of this block added "real data in a
  // staging environment", and that was a claim about DATA PROVENANCE inferred from DEPLOYMENT
  // CONTEXT -- two different facts. A test environment may hold seeded, copied or synthetic rows,
  // and being connected to a live database says nothing about where the rows in it came from.
  // Owner correction, 2026-10-18: *"Do not claim real data based solely on the environment or
  // database connection. Label demo or synthetic data separately where its provenance is known."*
  // Provenance has its own label, shown only where the provenance is actually known -- which is
  // what `FIXTURE_NOTICE_HE` already does for development fixtures.
  //
  // The prototype ribbon read "design proposal - sample data only - no connection to live data
  // sources". That sentence is TRUE of the prototype and FALSE of the product, and shipping it
  // over real customer data would be a lie printed across the top of every screen. The Owner
  // named this directly: never label real data as demo data.
  'env.staging': { he: 'סביבת בדיקות', en: 'Test environment' },
  'env.production': { he: 'סביבת ייצור', en: 'Production' },
  'env.pilot': { he: 'סביבת פיילוט', en: 'Pilot' },
  'env.unknown': { he: 'סביבה לא מזוהה', en: 'Environment unidentified' },

  // ---- THE APPROVED NAVIGATION ---------------------------------------------------------------
  //
  // The Hebrew is the prototype's own wording, kept rather than re-invented: these are the words
  // the Owner approved, and a synonym chosen here would be a silent redesign of the thing under
  // review.
  'nav.overview': { he: 'סקירה כללית', en: 'Overview' },
  'nav.customers': { he: 'לקוחות', en: 'Customers' },
  'nav.subscriptions': { he: 'מנויים ומכסות', en: 'Subscriptions and quotas' },
  'nav.billing': { he: 'מרכז חיוב', en: 'Billing centre' },
  /* 2026-11-03. The four entries of the מרכז חיוב group — see `PLATFORM_BILLING_CENTRE`. */
  'nav.billingOverview': { he: 'סקירת חיובים', en: 'Billing overview' },
  'nav.billingRun': { he: 'ביצוע חיובים', en: 'Run the billing' },
  'nav.billingDunning': { he: 'גבייה וחובות', en: 'Collections and arrears' },
  'nav.billingChangeRequests': { he: 'בקשות שינוי', en: 'Change requests' },
  'nav.supportQueue': { he: 'תור התמיכה', en: 'Support queue' },
  /* 2026-11-02. מרכז חיוב — the charges of every customer, on a screen of its own. */
  'charges.title': { he: 'מרכז חיוב', en: 'Billing centre' },
  'charges.lede': {
    he: 'כל החיובים של כל הלקוחות — מי חויב, מי נכשל, ואיזה מסמך יצא. חיוב שתוצאתו אינה ידועה מוצג ראשון, כי הוא היחיד שדורש הכרעה של אדם.',
    en: 'Every customer\u2019s charges — who was charged, who failed, and which document came out. A charge whose outcome is unknown is shown first, because it is the only one that needs a person to decide.',
  },
  'charges.reading': { he: 'את החיובים', en: 'the charges' },
  'nav.catalogue': { he: 'קטלוג ומחירונים', en: 'Catalogue and price books' },
  // The GROUP is "קטלוג ומחירונים"; these two are its members, each behind its own capability.
  'nav.cataloguePlans': { he: 'תכניות ומוצרים', en: 'Plans and products' },
  'nav.priceBooks': { he: 'ספרי מחירים', en: 'Price books' },
  'nav.connections': { he: 'חיבורים', en: 'Connections' },
  'nav.security': { he: 'אבטחה וביקורת', en: 'Security and audit' },
  'nav.health': { he: 'בריאות המערכת', en: 'System health' },
  'nav.settings': { he: 'הגדרות', en: 'Settings' },
  'nav.home': { he: 'עמוד הבית', en: 'Home' },
  // THE PICKER, NAMED BY WHAT IT DOES. `חברות` is the office register under
  // settings; this is the grant-filtered list of companies this user may OPEN.
  'nav.companies': { he: 'מעבר לחברה', en: 'Go to company' },
  'nav.tasks': { he: 'משימות', en: 'Tasks' },
  'nav.documents': { he: 'מסמכים', en: 'Documents' },
  'nav.reports': { he: 'דוחות', en: 'Reports' },
  'nav.office': { he: 'הגדרות', en: 'Settings' },

  // ---- THE CUSTOMER WORKSPACE, from the approved customer kit's own wording ------------------
  'nav.financialModules': { he: 'מודולים פיננסיים', en: 'Financial modules' },
  'nav.officeSettings': { he: 'פרטי הארגון', en: 'Organisation details' },
  'nav.officeUsers': { he: 'משתמשים', en: 'Users' },
  'nav.officeRoles': { he: 'תפקידים והרשאות', en: 'Roles and permissions' },
  'nav.officeSecurity': { he: 'אבטחה וביקורת', en: 'Security and audit' },
  'nav.connectionsAndSupport': { he: 'חיבורים ותמיכה', en: 'Connections and support' },

  // ---- THE AREA TAB STRIP, section 1, 2026-10-27 ---------------------------------------------
  //
  // NO NEW NAME IS DECLARED HERE, and that is the decision. The strip's eight tabs are screens
  // this product already names -- four in the rail above (`nav.office*`, `nav.connections`) and
  // three in the settings index (`settings.e.companies`, `settings.e.roleAuthoring`,
  // `settings.e.subscription`) -- and the area itself is `nav.orgAdmin`. A synonym invented for
  // the bar would be a second name for one screen, which is the drift this catalogue exists
  // against. `shell.back` above is the one genuinely new word: there was no label for a button
  // that says only חזרה.
  'nav.supportAccess': { he: 'הרשאות תמיכה', en: 'Support access' },
  'nav.platformAdministration': {
    he: 'ניהול הפלטפורמה · הרשאה נפרדת',
    en: 'Platform administration · separate authority',
  },

  // The eleven, in the kit's own words. Master §22 names four of them for Commercial V1.
  'nav.mod.financialStatements': { he: 'דוחות כספיים', en: 'Financial statements' },
  'nav.mod.cfoQueues': { he: 'CFO ותורים', en: 'CFO and queues' },
  'nav.mod.vatControl': { he: 'בקרת מע״מ', en: 'VAT control' },
  'nav.mod.creditCards': { he: 'כרטיסי אשראי', en: 'Credit cards' },
  'nav.mod.bankReconciliation': { he: 'התאמות בנק', en: 'Bank reconciliation' },
  'nav.mod.bankStatements': { he: 'דפי בנק', en: 'Bank statements' },
  'nav.mod.form126and856': { he: 'בקרת 126 + 856', en: 'Forms 126 and 856' },
  'nav.mod.collections': { he: 'מודול גבייה', en: 'Collections' },
  'nav.mod.qualityControl': { he: 'בקרת איכות', en: 'Quality control' },
  'nav.mod.controls': { he: 'בקרות', en: 'Controls' },
  'nav.mod.accountantLetters': { he: 'מכתבי רו״ח', en: 'Accountant letters' },

  // ---- THE CUSTOMER HOME ---------------------------------------------------------------------
  'home.greeting': { he: 'שלום', en: 'Hello' },
  'home.lead': {
    he: 'המשך החיפוש והבדיקה שלך, במקום אחד',
    en: 'Your work and your checks, in one place',
  },
  'home.office': { he: 'הארגון', en: 'Organisation' },
  /* Names the home page's identity block for a screen reader; the three labels are visible. */
  /* Section 9: the tiles can be reordered, and each control names the tile it moves. */
  /*
   * THE HANDLE'S ACCESSIBLE NAME. **Section 2, 2026-10-28.** The arrows left the screen and the
   * keyboard kept the feature: the same control is dragged with a pointer and moved with the
   * arrow keys, so its name says both. `%s` is the tile's own name, put in by the caller.
   */
  'home.noCompanies': { he: 'אין חברות ברשימה', en: 'No companies in the register' },
  'home.addCompany': { he: 'הקמת חברה', en: 'Add a company' },
  'home.moveHandle': {
    he: 'שינוי מיקום — גרירה, או חצים במקלדת',
    en: 'Reposition — drag, or arrow keys',
  },
  /**
   * The side column's accessible name. It is an `<aside>`, so a screen reader offers it as a
   * landmark to skip to or past — and an unnamed landmark in a list of landmarks is one a person
   * has to enter to identify.
   */
  'emailChange.title': { he: 'אימות כתובת הדואר', en: 'Verifying your address' },
  'emailChange.working': { he: 'מאמת…', en: 'Verifying…' },
  'emailChange.doneTitle': { he: 'הכתובת עודכנה', en: 'Your address was updated' },
  'emailChange.doneLogin': {
    he: 'מעתה הכניסה למערכת היא עם הכתובת החדשה.',
    en: 'You now sign in with the new address.',
  },
  'emailChange.doneContact': {
    he: 'כתובת הדואר עודכנה. הכניסה למערכת לא השתנתה.',
    en: 'Your email address was updated. Signing in has not changed.',
  },
  'emailChange.goSignIn': { he: 'מעבר למסך הכניסה', en: 'Go to sign-in' },
  'emailChange.noTokenTitle': { he: 'אין קישור בכתובת', en: 'No link in the address' },
  'emailChange.noTokenBody': {
    he: 'פתחו את הקישור מהודעת הדואר שנשלחה לכתובת החדשה.',
    en: 'Open the link from the message sent to the new address.',
  },
  'userCard.groupEdit': { he: 'עריכת שם ופרטי קשר', en: 'Edit name and contact details' },
  'userCard.addressElsewhere': {
    he: 'כתובת הכניסה משתנה רק על ידי בעל החשבון, עם אימות הכתובת החדשה.',
    en: 'The sign-in address is changed only by the account holder, with verification of the new address.',
  },
  // ---- הפרטים שלי — Owner section 1, 2026-10-29 -------------------------------------------
  'profile.title': { he: 'הפרטים שלי', en: 'My details' },
  'profile.detailsHeading': { he: 'שם ופרטי קשר', en: 'Name and contact details' },
  'profile.nameLabel': { he: 'שם מלא', en: 'Full name' },
  'profile.phoneLabel': { he: 'טלפון', en: 'Telephone' },
  'profile.phoneHint': {
    he: 'שדה ריק מוחק את המספר השמור.',
    en: 'An empty field removes the stored number.',
  },
  'profile.save': { he: 'שמירה', en: 'Save' },
  'profile.saving': { he: 'שומר…', en: 'Saving…' },
  'profile.savedTitle': { he: 'הפרטים נשמרו', en: 'Your details were saved' },
  'profile.savedBody': {
    he: 'השם ופרטי הקשר עודכנו.',
    en: 'Your name and contact details were updated.',
  },
  'profile.loginAddress': { he: 'כתובת הכניסה', en: 'Sign-in address' },
  'profile.contactAddress': { he: 'כתובת דואר', en: 'Email address' },
  'profile.currentAddress': { he: 'הכתובת הנוכחית', en: 'Current address' },
  'profile.loginFollows': {
    he: 'זו הכתובת שאתם נכנסים איתה. שינוי שלה משנה גם את אופן הכניסה.',
    en: 'This is the address you sign in with. Changing it changes how you sign in.',
  },
  'profile.loginSeparate': {
    he: 'הכניסה לחשבון הזה אינה לפי כתובת הדואר, ולכן שינוי כאן אינו משנה את אופן הכניסה.',
    en: 'This account does not sign in by email address, so a change here does not affect signing in.',
  },
  'profile.newAddressLabel': { he: 'כתובת חדשה', en: 'New address' },
  /*
   * ONE STATEMENT, UNDER SEVENTY CHARACTERS. The first draft of this line said three things -- a
   * link goes here, a notice goes there, and nothing changes until it is followed -- which is a
   * paragraph, and `noStandingProse.test.ts` refused it. The other two are not lost: they are the
   * CONFIRMATION a person reads after pressing the button, which is when they matter.
   */
  'profile.newAddressHint': {
    he: 'השינוי ייכנס לתוקף רק לאחר לחיצה על הקישור.',
    en: 'The change takes effect only after the link is followed.',
  },
  'profile.requestChange': { he: 'שליחת קישור לאימות', en: 'Send a verification link' },
  'profile.requesting': { he: 'שולח…', en: 'Sending…' },
  'profile.requestedTitle': { he: 'נשלח קישור לאימות', en: 'A verification link was sent' },
  'profile.requestedBody': {
    he: 'שלחנו קישור ל‑%s. הכתובת תשתנה רק לאחר שתלחצו עליו.',
    en: 'We sent a link to %s. The address changes only after you follow it.',
  },
  'profile.requestedNotice': {
    he: 'הודעה נשלחה גם לכתובת הנוכחית, כדי שתדעו אם לא אתם ביקשתם את השינוי.',
    en: 'A notice was also sent to the current address, so you know if this was not you.',
  },
  'profile.pendingTitle': { he: 'ממתין לאימות', en: 'Waiting for verification' },
  'profile.pendingBody': {
    he: 'קיימת בקשה פתוחה לשנות את הכתובת ל‑%s.',
    en: 'There is an open request to change the address to %s.',
  },
  'profile.pendingNext': {
    he: 'פתחו את הקישור שנשלח לכתובת החדשה. שליחת בקשה חדשה תבטל את הקודמת.',
    en: 'Open the link sent to the new address. A new request replaces this one.',
  },
  'profile.elsewhereHeading': { he: 'מה מנוהל במקום אחר', en: 'Managed elsewhere' },
  'profile.elsewhereSecurity': {
    he: 'סיסמה, אימות דו‑שלבי והתחברויות פעילות',
    en: 'Password, two-step verification and active sessions',
  },
  'profile.elsewherePermissions': {
    he: 'תפקידים, הרשאות, גישה לחברות ומודולים — מנוהלים על ידי מנהל הארגון.',
    en: 'Roles, permissions, company access and modules are managed by an organisation administrator.',
  },
  'home.sideRegion': { he: 'נתונים ועדכונים', en: 'Readings and updates' },
  'home.identity': { he: 'הארגון, המשתמש והחברה', en: 'Organisation, user and company' },
  'home.favourites': { he: 'מועדפים', en: 'Favourites' },
  'home.favouritesEmpty': {
    he: 'סמנו מודול בכוכב כדי שיופיע כאן.',
    en: 'Star a module to pin it here.',
  },
  'home.allModules': { he: 'כל המודולים שלי', en: 'All my modules' },
  /*
   * ── WHAT THE STAR DOES, SAID ONCE, WHERE IT IS SET. **Owner, 2026-11-01.** ────────────
   *
   *     *"להוריד מועדפים, וכל המודולים שלי גם להוריד — לא צריך את זה, אלא אם סימן בכוכב
   *     את המודול הרלוונטי ואז הוא יוצג בדף הבית."*
   *
   * The home screen no longer explains the star — it has no favourites panel and no empty
   * state, because an office that has starred nothing sees nothing there. So the sentence lives
   * HERE, beside the control it describes, which is the one place it is useful.
   */
  'home.starsExplain': {
    he: 'מודול שמסומן בכוכב יוצג בדף הבית שלך. הסימון נשמר למשתמש הזה ובין מכשירים.',
    en: 'A starred module appears on your home screen. The mark is kept for this user and across devices.',
  },
  'home.cashFlow': { he: 'תזרים במבט מהיר', en: 'Cash flow at a glance' },
  'home.today': { he: 'לטיפול היום', en: 'For attention today' },
  /*
   * ── THE STRIP AT THE FOOT OF THE WORK. **Owner, section 4, 2026-11-01.** ────────────────────
   *
   * *"הצג את „המשימות המתפרצות שלי" כרצועה קומפקטית."* His name is on the READING it belongs to
   * — `home.widgets.urgent`, which already existed — and the heading is the group word.
   *
   * **THE HEADING WAS HIS NAME FIRST, AND THE STRIP READ WRONG IN THE BROWSER:** *"משימות
   * מתפרצות שלי | לטיפול היום 2 | שהוקצו לי 0"*, where the heading claimed the whole strip was
   * one person's urgent tasks and the first reading under it is the OFFICE's day. One reading
   * named by the heading above it and one not is the card that says its own name twice.
   */
  'home.tasksStrip': { he: 'משימות', en: 'Work items' },
  'home.tasksOpenAll': { he: 'לכל המשימות', en: 'All work items' },
  'home.widgets.fx': { he: 'שערי מטבע', en: 'Exchange rates' },
  /*
   * ── *"ריביות והצמדה צריך להיות ריבית ופריים"*. **Owner, 2026-11-01.** ───────────────────────
   *
   * `והצמדה` was wrong twice over: this card carries no indexation reading and never has — the
   * index is its own card — and the two things it does carry are the policy rate and the prime.
   */
  'home.widgets.rates': { he: 'ריבית ופריים', en: 'Interest and prime' },
  /*
   * THE ROWS ON THAT CARD. The Bank of Israel's answer names nothing, so the Hebrew is here: the
   * publisher's own `key` is `policy_rate`, which is the technical code the Owner refused to see
   * on a screen, and `prime_rate` is this product's own derivation of it.
   */
  'market.series.policyRate': { he: 'בנק ישראל', en: 'Bank of Israel' },
  'market.series.primeRate': { he: 'פריים', en: 'Prime' },
  'market.series.nextDecision': { he: 'החלטה הבאה', en: 'Next decision' },
  /* The index row's name — his own reference card calls it this. Without a name the row
     renders as the card's figure, which is why it was twice the size of its neighbours. */
  'market.series.indexValue': { he: 'ערך המדד', en: 'Index value' },
  /*
   * ── THE OTHER THREE ROWS OF HIS TABLE. **Owner, 2026-11-01.** ──────────────────────────────
   *
   *     *"במדד אני מבקש שיהיה בדיוק כמו הטבלה הזאת: שינוי חודשי, שינוי שנתי."*
   *
   * `חודש המדד` is the PERIOD the index is for, which is not the day we read it — the card's own
   * date line answers that, and the two were one fact until this row existed.
   */
  'market.series.indexMonth': { he: 'חודש המדד', en: 'Index month' },
  'market.series.indexMonthly': { he: 'שינוי חודשי', en: 'Monthly change' },
  'market.series.indexYearly': { he: 'שינוי שנתי', en: 'Annual change' },
  /*
   * THE TWO COLUMNS THE MINISTRY OF ENERGY PUBLISHES, in its own words: `בנזין 95 אוקטן
   * נטול עופרת` and `תוספת בעד שירות מלא`. The first is shortened for a 190px card;
   * the second is not, because `תוספת` alone would not say what for.
   *
   * DIESEL AND 98 ARE NOT HERE because they are not under price control and the ministry
   * publishes no ceiling for them — two rows nobody published.
   */
  /*
   * HIS OWN TWO LINES: *"בנזין 7.35 / בנזין ש.מ 7.61."* `95` is dropped from the first because
   * the card is headed `מחירי דלק` and 95 is the only petrol under price control — saying it on
   * the row as well is the card repeating itself in the one place there is no room for it.
   */
  'market.series.petrol95': { he: 'בנזין', en: 'Petrol' },
  'market.series.petrol95Full': { he: 'בנזין ש.מ', en: 'Petrol, full service' },
  /*
   * ── WHY THE CARD HAS NO DIESEL. **Owner, 2026-11-01:** *"לא לשכוח להציג גם סולר."* ─────────
   *
   * BECAUSE NOBODY PUBLISHES ONE. Diesel and 98-octane are not under price control in Israel —
   * the supervision order (מחירים מרביים בתחנות תדלוק, התשס״ב-2002) covers 95-octane alone, which
   * is why the ministry's own workbook has exactly two columns and why its calculator page is
   * headed `מחיר בנזין 95 בתחנות תדלוק`. The only official diesel figure is the THEORETICAL
   * refinery-gate price in `מחירים תאורטיים של מוצרי דלק שאינם בפיקוח`, and that is a different
   * number with a different calculation — excise, marketing margin and VAT are not in it.
   *
   * THE REFERENCE HE SENT SHOWED DIESEL because it came from a commercial survey of actual pump
   * prices, not from a ceiling. That is a real option and it is his to take; what this product
   * will not do is print a refinery-gate figure under the word `סולר` on a card an accountant
   * quotes to a client.
   *
   * SO THE ABSENCE IS STATED. A card that simply has no diesel row is a card somebody asks about
   * once a month; one line answers it on the screen.
   */
  'market.fuelControlledOnly': {
    he: 'סולר ובנזין 98 אינם בפיקוח, ואין להם מחיר מרבי מפורסם.',
    en: 'Diesel and 98-octane are not price-controlled, and no maximum is published for them.',
  },
  'home.widgets.news': { he: 'עדכונים מקצועיים', en: 'Professional updates' },
  /*
   * THE WIDGETS THE OWNER ASKED FOR BY NAME. **Section 4, 2026-10-28.** *"יש עוד ויגאטים שאני
   * לא רואה אותם תוסיף לי אותם" — מדד המחירים לצרכן, מחירי דלק, שערים יציגים, ריבית ופריים,
   * משימות מתפרצות שלי.* Each says which of the four states it is in; none of them invents a
   * figure.
   */
  'home.widgets.cpi': { he: 'מדד המחירים לצרכן', en: 'Consumer price index' },
  'home.widgets.fuel': { he: 'מחירי דלק', en: 'Fuel prices' },
  'home.widgets.urgent': { he: 'משימות מתפרצות שלי', en: 'My urgent tasks' },
  /* And the three that are MEASURED, from reads this product already makes. */
  'home.widgets.attention': { he: 'חברות הדורשות טיפול', en: 'Companies needing attention' },
  'home.widgets.sources': { he: 'מקורות נתונים', en: 'Data sources' },
  'home.widgets.members': { he: 'משתמשי הארגון', en: 'Organisation users' },
  'home.widgets.subscription': { he: 'המנוי', en: 'Subscription' },
  'home.readingSources': { he: 'את מקורות הנתונים', en: 'the data sources' },
  'home.readingMembers': { he: 'את משתמשי הארגון', en: 'the organisation users' },
  'home.readingSubscription': { he: 'את פרטי המנוי', en: 'the subscription details' },
  'home.sourcesConnected': { he: 'חיבורים פעילים', en: 'connections working' },
  'home.attentionNone': { he: 'אין חברה שדורשת טיפול', en: 'No company needs attention' },
  'home.membersUnit': { he: 'משתמשים', en: 'users' },
  'home.companiesUnit': { he: 'חברות', en: 'companies' },

  'home.companiesCount': { he: 'חברות', en: 'Companies' },
  'home.openCompanies': { he: 'לחברות שלי', en: 'Go to my companies' },
  /*
   * ── THE BADGE SAID SOMETHING THAT STOPPED BEING TRUE. **Section 7, 2026-11-01.** ────────────
   *
   * It read *"נשמר במכשיר זה"*, with the explanation *"אין עדיין שמירה בשרת להעדפות משתמש"* —
   * and that was an accurate statement of the product on the day it was written. §6.9 added
   * `user.preference.update`, the home ORDER has travelled on the account since, and section 7
   * moved the star onto the same mechanism: *"הבחירה והסדר נשמרים לכל משתמש ובין מכשירים."*
   *
   * **A chip is a reading, not a decoration.** Leaving the old words beside a star that now
   * follows the person would have been the same defect as a status printed as a literal: the
   * screen asserting a property of the mechanism that the mechanism no longer has.
   */
  'home.perDevice': { he: 'נשמר לכל המכשירים', en: 'Kept across your devices' },
  'home.perDeviceWhy': {
    he: 'הסימון נשמר למשתמש הזה ומופיע גם לאחר רענון, כניסה מחדש ובמכשיר אחר.',
    en: 'The mark is kept for this user and survives a refresh, a new sign-in and a second device.',
  },

  /*
   * ── PERSONALISING THE HOME SCREEN. **Owner, section 4, 2026-11-01.** ────────────────────────
   *
   *     *"המשתמש בוחר אילו כרטיסים להציג ובאיזה סדר. הבחירה אינה מעניקה הרשאות או מנוי."*
   *
   * The second sentence is in the panel itself and not only in a comment: somebody switching a
   * module's card on is entitled to know, on the screen, that they have changed a VIEW and not
   * bought anything. The product already says `טרם מומש` on the card; this says what the switch
   * does.
   */
  'home.personalise': { he: 'התאמה אישית של דף הבית', en: 'Personalise the home screen' },
  'home.personaliseHint': {
    he: 'בחירת הכרטיסים שיוצגו, והסדר ביניהם',
    en: 'Choose which cards appear, and the order between them',
  },
  'home.personaliseScope': {
    he: 'הבחירה נשמרת למשתמש הזה ומופיעה גם לאחר רענון, כניסה מחדש ובמכשיר אחר. היא אינה מעניקה הרשאה, אינה משנה מנוי ואינה הופכת מודול לזמין.',
    en: 'The choice is kept for this user and survives a refresh, a new sign-in and a second device. It grants no permission, changes no subscription and makes no module available.',
  },
  'home.mainRegion': { he: 'מרכז העבודה', en: 'The work area' },

  /*
   * ── A READ THAT REACHED THE SERVER AND FOUND NO ROUTE. **Section 6, 2026-11-01.** ───────────
   *
   * The Owner: *"״לטיפול היום״ ו״משימות מתפרצות שלי״ הציגו שגיאות טעינה. אבחן ותקן. הבחן בין
   * רשימה ריקה לכשל קריאה, והצג הודעות קצרות ושימושיות."*
   *
   * The generic 404 wording is written for an ITEM route — *"לא הצלחנו למצוא את הפריט שאליו
   * מפנה הפעולה, ולכן לא נעשה בו שינוי"* — and it is right there and nonsense on a LIST read:
   * nothing was being changed and no item was named. What a 404 on a list route actually means
   * is that the address is not on this server, and the overwhelmingly common cause is a page
   * served by one release talking to an API from another.
   *
   * It says so, and it says what to do, because *"שגיאת טעינה"* with no next step is what the
   * Owner saw and could not act on.
   */
  'read.notFoundOnServer': {
    he: 'הבקשה הגיעה לשרת, והשרת אינו מכיר את הכתובת הזו. בדרך כלל זו אי-התאמה בין הגרסה שנטענה בדפדפן לגרסה שמותקנת בשרת: רעננו את הדף. אם זה חוזר — מדובר בשתי גרסאות שונות, ולא בנתונים חסרים.',
    en: 'The request reached the server and the server does not know this address. That is usually a mismatch between the page loaded in the browser and the release installed on the server: refresh the page. If it repeats, the two are different releases and no data is missing.',
  },

  /*
   * ── THE PROFESSIONAL UPDATES BANNER. **Owner, section 5, 2026-11-01.** ──────────────────────
   *
   *     *"העדכונים המקצועיים יהיו בבאנר אופקי מתחלף בראש אזור התוכן, עם קישור למקור ואפשרות
   *     לעצור ולעבור בין עדכונים."*
   *
   * Three controls and all three are named here, because a rotating banner with unlabelled
   * arrows is the shape that makes a screen reader read "button button button".
   */
  'home.updates.pause': { he: 'עצירת ההחלפה', en: 'Pause the rotation' },
  'home.updates.resume': { he: 'המשך ההחלפה', en: 'Resume the rotation' },
  /*
   * THE TWO ARROW LABELS ARE DELETED WITH THE ARROWS. *"תנועה אוטומטית רציפה בלי
   * חיצים."* A string nothing renders is a string a translator is paid to translate and a
   * reviewer has to read -- and the next person to find it will reasonably wonder which screen
   * it is missing from.
   */
  'home.updates.source': { he: 'למקור', en: 'Open the source' },
  'home.updates.position': { he: 'עדכון', en: 'Update' },

  /* The unit a reading is expressed in, named beside its publisher rather than inferred from the
     shape of the number. *"הצג מקור, מועד עדכון ובסיס יחידות ברור."* */
  'market.unitBasis': { he: 'יחידות', en: 'Units' },
  'home.resetLayout': { he: 'איפוס לברירת המחדל', en: 'Reset to the default layout' },
  'home.allHidden': {
    he: 'כל הכרטיסים באזור הזה מוסתרים. סמנו כרטיס בהגדרות › התאמה אישית כדי להחזירו.',
    en: 'Every card in this area is hidden. Tick one under Settings › Personalisation to bring it back.',
  },

  // ---- WHY A CAPABILITY IS NOT THERE, in the words of the business ---------------------------
  // The three figures the approved cash-flow card will hold, named so the card keeps its shape.
  // Each shows the STATE where a number would be -- never a dash, which reads as nothing, and
  // never a zero, which is a measurement.
  'cash.inBank': { he: 'יתרה בבנקים', en: 'Bank balance' },
  'cash.expectedIn': { he: 'תקבולים צפויים', en: 'Expected receipts' },
  'cash.expectedOut': { he: 'תשלומים צפויים', en: 'Expected payments' },
  'cash.chartEmpty': {
    he: 'כאן יוצג תזרים חודשי, לאחר שיחוברו מודולי הבנק והגבייה. אין נתונים להצגה.',
    en: 'A monthly cash-flow chart will appear here once the bank and collections modules are connected. There is nothing to show.',
  },
  /*
   * ── REWRITTEN WHEN THE ENGINE SHIPPED. **§6, 2026-10-29.** ───────────────────────
   *
   * It said *"מנגנון המשימות טרם נבנה"* -- true until the mechanism existed, and now a sentence
   * that tells an office its product is unfinished when what is true is that nothing is due today.
   * A test caught it: the card no longer renders a "not implemented" state, so the only place the
   * old sentence could still appear was its own string.
   */
  'today.empty': {
    he: 'אין משימות לביצוע היום.',
    en: 'Nothing is due today.',
  },

  /*
   * IT USED TO READ *"טרם חובר ספק"*, AND THAT WORD WAS THE WRONG ONE. **2026-10-28.**
   *
   * Owner section 4 separates `לא הוגדר חיבור` from `הרכיב טרם מומש`, and the first is a thing the
   * OFFICE can go and fix. "No provider connected yet" invites exactly that reading — somebody
   * looks for the screen where they would connect one. There is no such screen, no route and no
   * stored rate anywhere in this product, so the sentence says what is actually true.
   */
  'planned.marketData': {
    he: 'נתוני שוק אינם קיימים בגרסה הזו — אין מה להגדיר',
    en: 'Market data does not exist in this release — there is nothing to configure',
  },
  'planned.cashFlow': {
    he: 'דורש את מודולי הבנק והגבייה',
    en: 'Requires the bank and collections modules',
  },
  'planned.tasks': { he: 'מנגנון המשימות טרם נבנה', en: 'The task engine has not been built' },

  // ---- FAVOURITES AND THE TAB STRIP ----------------------------------------------------------
  'nav.addFavourite': { he: 'הוספה למועדפים', en: 'Add to favourites' },
  'nav.removeFavourite': { he: 'הסרה מהמועדפים', en: 'Remove from favourites' },
  'nav.noFavourites': { he: 'עדיין לא סימנת מועדפים', en: 'No favourites yet' },
  /*
   * `nav.closeTab` and `nav.tabLimit` were here and are gone with the tab strip they named
   * (`WorkTabs`, removed 2026-10-25 on the Owner's instruction). A key whose only reader has been
   * deleted is a translation somebody has to maintain for a screen nobody can reach.
   */
  'nav.recentlyUsed': { he: 'בשימוש לאחרונה', en: 'Recently used' },
  'nav.removeFromRecent': { he: 'הסרה מהרשימה', en: 'Remove from the list' },
  'nav.recentCollapse': { he: 'כיווץ הרשימה', en: 'Collapse the list' },
  'nav.recentExpand': { he: 'הרחבת הרשימה', en: 'Expand the list' },

  // ---- THE FOUR STATES, which must never collapse into one another --------------------------
  'state.notImplemented': { he: 'טרם מומש', en: 'Not yet implemented' },
  'state.notImplementedBody': {
    he: 'היכולת הזו מתוכננת ואינה קיימת בגרסה הזו.',
    en: 'This capability is planned and does not exist in this release.',
  },
  'state.configurationRequired': { he: 'נדרשת הגדרה', en: 'Configuration required' },
  'state.configurationRequiredBody': {
    he: 'היכולת קיימת אך טרם הוגדרה בסביבה הזו.',
    en: 'The capability exists but has not been configured in this environment.',
  },
  'state.failedToLoad': { he: 'תקלה בטעינה', en: 'Failed to load' },
  'state.failedToLoadBody': {
    he: 'הבקשה נשלחה ולא הצליחה. זו אינה תשובה ריקה.',
    en: 'The request was made and did not succeed. This is not an empty answer.',
  },
  'state.noDataYet': { he: 'אין עדיין נתונים', en: 'No data yet' },
  'state.noDataYetBody': {
    he: 'הבקשה הצליחה והשיבה ריק.',
    en: 'The request succeeded and returned nothing.',
  },
  'state.notMeasuredBody': {
    he: 'זמינות, ביצועים ותקלות יוצגו ממדידות אמיתיות בלבד.',
    en: 'Availability, performance and incidents will be shown from real measurements only.',
  },
  'state.retry': { he: 'ניסיון נוסף', en: 'Try again' },
  /*
   * THE FOURTH STATE. **Owner section 4, 2026-10-28.** *"יש להבחין בין אין נתונים, אין הרשאה,
   * לא הוגדר חיבור והרכיב טרם מומש."* The other three were already named here; a refusal was not,
   * so a widget had no way to say which of the four it was in.
   */
  'state.notPermitted': { he: 'אין הרשאה', en: 'Not permitted' },
  'state.notPermittedBody': {
    he: 'הנתון הזה פתוח למי שהוענקה לו ההרשאה המתאימה. הבקשה לא נשלחה.',
    en: 'This figure is open to whoever holds the matching permission. The request was not made.',
  },


  // ---- WHAT A PLANNED ENTRY IS MISSING -------------------------------------------------------
  //
  // "Not yet implemented" on its own was read, correctly, as a bigger absence than the real one:
  // the Owner asked why Connections was marked unimplemented when tenant connection screens exist.
  // They do. What is missing is the ESTATE-WIDE view. A planned entry therefore names its own
  // scope, so the menu cannot overstate a gap any more than it may understate one.
  'planned.billing': {
    he: 'מנוע החיוב טרם נבנה',
    en: 'The billing engine has not been built',
  },
  'planned.connectionsPlatformWide': {
    he: 'תצוגה כלל-מערכתית — חיבורי הלקוח פועלים בארגון עצמו',
    en: 'Estate-wide view — a customer’s own connections work inside their office',
  },
  'planned.module': { he: 'מודול שטרם נבנה', en: 'A module that has not been built' },

  // ---- THE OVERVIEW. The Hebrew is the approved prototype's own wording. ----------------------
  'overview.title': { he: 'סקירת הפלטפורמה', en: 'Platform overview' },
  'overview.lead': { he: 'תמונת מצב, פעולות וניהול', en: 'Position, actions and administration' },
  'overview.window30': { he: '30 הימים האחרונים', en: 'Last 30 days' },
  'overview.createCustomer': { he: 'הקמת לקוח', en: 'New customer' },
  'overview.customersAndActivity': { he: 'לקוחות ופעילות', en: 'Customers and activity' },
  'overview.commercial': { he: 'מצב מסחרי', en: 'Commercial position' },
  'overview.queue': { he: 'לטיפול', en: 'For attention' },
  'overview.queueDerived': {
    he: 'הפריטים כאן נגזרים ממצב הלקוחות שהשרת החזיר.',
    en: 'These items are derived from the customer states the server returned.',
  },
  'overview.queueClear': { he: 'אין כרגע פריטים לטיפול', en: 'Nothing needs attention right now' },
  'overview.recentCustomers': { he: 'לקוחות אחרונים', en: 'Recent customers' },
  'overview.viewAllCustomers': { he: 'צפייה בכל הלקוחות', en: 'View all customers' },
  'overview.healthAndIncidents': { he: 'בריאות ותקלות', en: 'Health and incidents' },
  'overview.capacity': { he: 'סיכום תפוסה ותפעול', en: 'Capacity and operations' },
  'overview.integrations': { he: 'חיבורים ויכולות', en: 'Connections and capabilities' },
  'overview.footer': {
    he: 'כל מספר במסך הזה נספר מתשובת השרת. יכולת המסומנת כמתוכננת אינה משלימה דרישת מוצר.',
    en: 'Every figure on this screen is counted from the server’s answer. Marking a capability planned does not satisfy a product requirement.',
  },
  'overview.emptyTitle': { he: 'עדיין אין לקוחות בפלטפורמה', en: 'No customers on the platform yet' },
  'overview.emptyBody': {
    he: 'הקריאה לשרת הצליחה והחזירה אפס לקוחות. זהו המצב הצפוי של פלטפורמה שטרם הוקם בה לקוח.',
    en: 'The read succeeded and returned no customers. This is the expected state of a platform before its first customer.',
  },
  'overview.createFirstCustomer': { he: 'הקמת הלקוח הראשון', en: 'Create the first customer' },

  // ---- The six metric cards, in the kit's order ----------------------------------------------
  'metric.customers': { he: 'לקוחות', en: 'Customers' },
  'metric.active': { he: 'פעילים', en: 'Active' },
  'metric.trial': { he: 'בניסיון', en: 'In trial' },
  'metric.newCustomers': { he: 'לקוחות חדשים', en: 'New customers' },
  'metric.trialConversions': { he: 'המרות מניסיון', en: 'Trial conversions' },
  'metric.churned': { he: 'נטישות', en: 'Churned' },
  'metric.companies': { he: 'חברות', en: 'Companies' },
  'metric.users': { he: 'משתמשים', en: 'Users' },
  'metric.activeModules': { he: 'מודולים פעילים', en: 'Active modules' },

  // ---- The three commercial cards ------------------------------------------------------------
  'commercial.revenue': { he: 'הכנסה חודשית ושנתית', en: 'Monthly and annual revenue' },
  'commercial.revenueNote': {
    he: 'טרם נבנה מנוע החיוב',
    en: 'The billing engine has not been built',
  },
  'commercial.contractedLabel': { he: 'סכום המחירים שסוכמו', en: 'Sum of agreed prices' },
  'commercial.contractedNote': {
    he: 'אינו הכנסה — ללא הנחות וללא תרגום תקופת חיוב',
    en: 'Not revenue — no discounts, no billing-period normalisation',
  },
  'commercial.discountsIgnored': {
    he: 'מנויים עם הנחה בתוקף שהסכום מתעלם ממנה',
    en: 'subscriptions with a discount the sum ignores',
  },
  'commercial.failedPayments': { he: 'תשלומים שנכשלו', en: 'Failed payments' },
  'commercial.failedPaymentsNote': {
    he: 'אין להציג אפס במקום נתון חסר',
    en: 'A missing figure must never be shown as a zero',
  },
  'commercial.churnImpact': { he: 'השפעת נטישות על הכנסה', en: 'Revenue impact of churn' },
  'commercial.churnImpactNote': {
    he: 'סכום המחירים במנויים שנסגרו, באותה מגבלה',
    en: 'Agreed prices on closed subscriptions, same limitation',
  },
  'state.measurementUnavailable': { he: 'מדידה אינה זמינה', en: 'Measurement unavailable' },
  'state.notMeasured': { he: 'טרם נמדד', en: 'Not yet measured' },

  // ---- The recent-customers table ------------------------------------------------------------
  'table.customer': { he: 'לקוח', en: 'Customer' },
  'table.state': { he: 'מצב', en: 'State' },
  'table.companies': { he: 'חברות', en: 'Companies' },
  'table.users': { he: 'משתמשים', en: 'Users' },
  'table.details': { he: 'פרטים', en: 'Details' },

  // ---- The action queue, every item derived from a figure the server stated -------------------
  'queue.withoutSubscription': { he: 'לקוחות ללא מנוי', en: 'Customers with no subscription' },
  'queue.withoutSubscriptionBody': {
    he: 'הוקמו ועדיין לא נמכר להם מנוי.',
    en: 'Provisioned, and nothing has been sold to them yet.',
  },
  'queue.blocked': { he: 'לקוחות חסומים מסחרית', en: 'Commercially blocked customers' },
  'queue.blockedBody': {
    he: 'פיגור בתשלום, השעיה או מנוי שפג.',
    en: 'Past due, suspended, or an expired subscription.',
  },
  'queue.unpriced': { he: 'שורות מנוי ללא מחיר', en: 'Subscription lines with no price' },
  'queue.unpricedBody': {
    he: 'שורות פעילות שאין להן מחיר, ולכן הסכומים המסחריים חלקיים.',
    en: 'Live lines carrying no price, so the commercial sums are partial.',
  },
  'queue.open': { he: 'לפרטים', en: 'Open' },

  // ---- The integration tiles -----------------------------------------------------------------
  'integration.dataSources': { he: 'חיבורי נתונים', en: 'Data connections' },
  'integration.dataSourcesState': {
    he: 'פועלים ברמת הארגון — אין תצוגה כלל-מערכתית',
    en: 'Working per office — no estate-wide view',
  },
  'integration.outboundMail': { he: 'שליחת דואר', en: 'Outbound mail' },
  'integration.clearing': { he: 'סליקה — Cardcom', en: 'Card clearing — Cardcom' },
  /*
   * ── `integration.clearingState` IS GONE. **Owner section 8, 2026-11-01.** ───────────────────
   *
   * It read *"מתוכנן — עדיין לא זמין"* and the console printed it unconditionally, so the tile
   * said the same thing on an estate with no provider, on a Staging estate running the sandbox,
   * and on one with a terminal. The condition is now read from `platform.incidents.read` and
   * rendered through `api/connectionText.ts`, which has a sentence per real state.
   */
  'integration.stateElsewhere': {
    he: 'מצב החיבור — במסך בריאות המערכת',
    en: 'Connection state — on the system status screen',
  },

  // ---- Health -------------------------------------------------------------------------------
  'health.open': { he: 'מצב המערכת', en: 'System status' },
  'health.schema': { he: 'גרסת סכימה', en: 'Schema version' },
  'health.checks': { he: 'בדיקות שרצו', en: 'Checks run' },
  'health.incidents': { he: 'תקלות פתוחות', en: 'Open incidents' },

  // ---- THE CONTEXT BAR AND THE SURFACE BADGE --------------------------------------------------
  //
  // The toolbar is CHROME: it belongs to the shell, it is the same on every screen, and it is the
  // first thing an English session reads. It was Hebrew throughout while the rail beside it was
  // English, so the one part of the product that had been translated disagreed with the one part
  // next to it. **Owner, 2026-10-23 §6.**
  'context.chain': { he: 'מיקום במערכת', en: 'Where you are' },
  /*
   * ── THREE NAMES, THREE LABELS. **Section 2, 2026-10-27.** ───────────────────────────────────
   *
   * Owner: *"להבדיל בבירור בין הארגון, המשתמש המחובר ו־החברה שנבחרה."* They are his three phrases,
   * used verbatim, and the English is `Organisation` and not `Office` -- section 2 of 2026-10-26
   * retired that noun because *"המערכת אינה מיועדת רק למשרדים"*, and a sweep that ran over the
   * Hebrew only left the English saying the retired thing to the other half of the product.
   */
  'context.tenant': { he: 'הארגון', en: 'Organisation' },
  'context.user': { he: 'המשתמש המחובר', en: 'Signed-in user' },
  'context.company': { he: 'החברה', en: 'Company' },
  'context.companyChosen': { he: 'החברה שנבחרה', en: 'Selected company' },
  /* NOT an empty slot: a name that is absent and a name that is blank look identical otherwise. */
  'context.noCompany': { he: 'לא נבחרה חברה', en: 'No company selected' },
  'context.chooseCompany': { he: 'בחירת חברה', en: 'Choose a company' },
  'context.module': { he: 'המודול', en: 'Module' },
  'context.entity': { he: 'הישות', en: 'Record' },
  'context.period': { he: 'התקופה', en: 'Period' },
  'context.allPeriods': { he: 'כל התקופות', en: 'All periods' },
  'context.locked': { he: 'נעולה', en: 'Locked' },
  // The DESTINATION's name is the screen's own, and screens are authored in Hebrew — so this
  // translates the sentence around it and never the name itself, which would be a second name for
  // a screen whose title is the first.
  'context.backTo': { he: 'חזרה ל', en: 'Back to ' },
  'shell.signingOut': { he: 'מתנתק…', en: 'Signing out…' },
  'shell.signOutFailed': {
    he: 'היציאה לא הושלמה — החיבור עדיין פעיל. נסו שוב.',
    en: 'Sign-out did not complete — the session is still active. Please try again.',
  },
  'surface.tenant': { he: 'סביבת הארגון', en: 'Organisation workspace' },
  'surface.unread': { he: 'ההקשר טרם נקרא', en: 'Context not read yet' },
  'surface.supportGrant': { he: 'גישת תמיכה פעילה', en: 'Support access is live' },

  // ---- A SCREEN THAT IS NOT YET IN ENGLISH, AND SAYS SO ---------------------------------------
  //
  // **Owner, 2026-10-23 §6:** *"מעבר לאנגלית משאיר כותרות, הסברים ופעולות בעברית. ודא ששפה וכיוון
  // עקביים במסכים שבתכולה."*
  //
  // The screens are authored in Hebrew — `titleHe`, `leadHe`, `provenanceHe` are the shell's own
  // prop names — and translating them is a separate body of work: 511 user-visible strings on the
  // four screens in scope, and roughly 195 more in the components and vocabularies they render
  // through, counted on 2026-10-23. What this round fixes is the part that is a DEFECT rather than
  // an absence: an English session flipped the document to LTR and left Hebrew text stranded in it,
  // which is neither language done properly, and said nothing about why.
  //
  // Marking the region `lang="he" dir="rtl"` is not a workaround — it is the correct markup for
  // text that IS Hebrew, and it is what a screen reader and a bidi algorithm both need. The notice
  // is what stops that being a silent gap.
  'shell.hebrewOnly.title': {
    he: 'המסך מוצג בעברית',
    en: 'This screen is not yet available in English',
  },
  'shell.hebrewOnly.body': {
    he: 'תוכן המסך כתוב בעברית בלבד בגרסה הזאת.',
    en: 'Its content is written in Hebrew only in this build, and is shown below in Hebrew, right to left. The navigation, the toolbar and About System are in English.',
  },

  // ---- About System -------------------------------------------------------------------------
  'about.title': { he: 'אודות המערכת', en: 'About System' },
  'about.release': { he: 'גרסת הפצה', en: 'Release' },
  'about.build': { he: 'מזהה בנייה', en: 'Build' },
  'about.builtAt': { he: 'נבנה בתאריך', en: 'Built at' },
  'about.environment': { he: 'סביבה', en: 'Environment' },
  'about.copyDetails': { he: 'העתקת פרטי גרסה', en: 'Copy version details' },
  'about.copied': { he: 'הועתק', en: 'Copied' },
  'about.close': { he: 'סגירה', en: 'Close' },
  'about.unavailable': { he: 'פרטי הגרסה אינם זמינים', en: 'Version information unavailable' },
  'about.releaseNotesUnavailable': {
    he: 'אין הערות גרסה עבור הגרסה הזו',
    en: 'Release notes not available for this version',
  },
  'about.fontLicence': {
    he: 'גופן Heebo — רישיון SIL Open Font 1.1',
    en: 'Heebo typeface — SIL Open Font License 1.1',
  },
  'about.updateImpact': {
    he: 'ללא שינוי מבנה במאגר הנתונים. אם ההתקנה תיכשל, החזרה לאחור היא החלפת קישור והפעלה מחדש.',
    en: 'No database schema change. If the installation fails, the recovery is a symlink flip and a restart.',
  },
  /*
   * ── WHAT A SCHEMA UPDATE SAYS, BEFORE THE BUTTON IS PRESSED. **Owner decision, 2026-10-19.** ──
   *
   * The decision allows the update path to carry the schema changes a release requires, with
   * explicit recovery limits. These three strings ARE those limits, where the operator is standing:
   * the backup is proved before anything changes, the number of migrations is named, and the
   * recovery after a migration is two moves rather than a symlink flip.
   */
  'about.updateImpactSchema': {
    he: 'העדכון הזה משנה את מבנה מאגר הנתונים. לפני כל שינוי נלקח גיבוי טרי ומוכח בשחזור לעותק בדיקה; אם הגיבוי נכשל, שום דבר אחר לא מתבצע.',
    en: 'This update CHANGES THE DATABASE SCHEMA. A fresh backup is taken first and proved by restoring it into a scratch copy; if that fails, nothing else runs.',
  },
  'about.updateSchemaPending': {
    he: 'מיגרציות שיוחלו',
    en: 'Migrations to be applied',
  },
  'about.updateSchemaUnstated': {
    he: 'הגרסה אינה מציינת את מערך המיגרציות שלה, ולכן היא מטופלת כגרסה שעשויה לשנות את המסד',
    en: 'This release does not state its migration set, so it is treated as one that may change the database',
  },
  'about.updateSchemaRecovery': {
    he: 'לאחר המיגרציה ההתאוששות היא שתי תנועות: החזרת הקישור לגרסה הקודמת ושחזור הגיבוי שנלקח כאן, כששירות עצור בשתיהן. שחזור המסד בלבד משאיר שירות שמסרב לעלות.',
    en: 'After the migration the recovery is TWO MOVES: point the release link back and restore the backup taken here, with the service stopped across both. Restoring the database alone leaves a service that refuses to boot.',
  },
  'about.updateConfirm': {
    he: 'התקן גרסה זו',
    en: 'Install this release',
  },
  'about.updateStarting': {
    he: 'מתבצע...',
    en: 'Starting...',
  },
  'about.deploymentState': {
    he: 'מצב ההתקנה',
    en: 'Deployment',
  },
  'about.verifiedIdentity': {
    he: 'התהליך הרץ מדווח',
    en: 'the running process reports',
  },
  'about.state.requested': {
    he: 'התבקשה',
    en: 'requested',
  },
  'about.state.running': {
    he: 'מתבצעת',
    en: 'running',
  },
  'about.state.verified': {
    he: 'אומתה',
    en: 'verified',
  },
  'about.state.failed': {
    he: 'נכשלה',
    en: 'failed',
  },
  'about.acceptEvidence': {
    he: 'ראיות לבדיקה שבוצעה (חובה)',
    en: 'Evidence of the testing you performed (required)',
  },
  'about.acceptAction': {
    he: 'קבל את הגרסה הזו',
    en: 'Accept this release',
  },
  'about.acceptWorking': {
    he: 'נרשם...',
    en: 'Recording...',
  },
  'about.acceptedAlready': {
    he: 'גרסה זו כבר נתקבלה באסטייט הזה.',
    en: 'This release has already been accepted on this estate.',
  },
  'about.updateAvailable': {
    he: 'קיימת גרסה חדשה שאושרה להתקנה',
    en: 'A newer approved release is available',
  },
  'about.updateApproval': {
    he: 'אסמכתא לאישור',
    en: 'Approval reference',
  },
  'about.updateHow': {
    he: 'ההתקנה מתבצעת מהמשגר במצב UPDATE. מסך זה אינו מתקין דבר.',
    en: 'Install it from the launcher in UPDATE mode. This screen installs nothing.',
  },
  'about.buildMismatch': {
    he: 'גרסת המסך וגרסת השרת שונות. יש לרענן את העמוד.',
    en: 'The interface and the server are different releases. Please refresh.',
  },

  // ---- Settings (TenantAdminScreen) — fully translated, section 7, 2026-10-26 ----------------
  //
  // The Hebrew here is the text that was already on the screen, moved and not rewritten: a
  // translation round that also edits the product's wording is two changes nobody can review
  // apart. The English is written for an office administrator, not transliterated.
  'settings.title': { he: 'הגדרות', en: 'Settings' },
  'settings.provenance': {
    he: 'ניהול הארגון נעשה בידי הארגון עצמו. הפלטפורמה אינה מחליטה מי בארגון רשאי לראות אילו חברות.',
    en: 'The office administers itself. GL-POINT does not decide which of your people may see which client.',
  },
  'settings.office.fallbackName': { he: 'הארגון', en: 'The office' },
  'settings.office.group': { he: 'פרטי הארגון', en: 'Organisation details' },
  'settings.office.groupLead': {
    he: 'השם שבו הארגון מוצג בכל המסכים',
    en: 'The name this office is shown by, on every screen',
  },
  'settings.office.nameLabel': { he: 'שם הארגון', en: 'Organisation name' },
  'settings.office.nameHint': { he: 'השם שמוצג למשתמשי הארגון.', en: 'The name shown to the users of this organisation.' },
  'settings.office.save': { he: 'שמירת השם', en: 'Save the name' },
  'settings.office.saving': { he: 'שומר…', en: 'Saving…' },
  'settings.office.savedTitle': { he: 'השם נשמר', en: 'The name was saved' },
  'settings.office.savedUnreadable': {
    he: 'השמירה נשלחה והקריאה שאחריה חזרה בלי שם. אין להניח שהשם נשמר — יש לרענן ולבדוק, ואם זה חוזר לפנות לתמיכה.',
    en: 'The save was sent and the read that followed came back with no name. Do not assume it was saved — refresh and check, and contact support if it happens again.',
  },
  'settings.office.none': {
    he: 'טרם נשמר שם תצוגה לארגון. עד שיישמר, מוצג השם שבו הארגון רשום.',
    en: 'No display name has been saved for this office yet. Until one is, the registered name is shown.',
  },
  'settings.office.conflictTitle': { he: 'השם שונה בינתיים', en: 'The name changed meanwhile' },
  'settings.office.conflictBody': {
    he: 'מישהו אחר בארגון שינה את השם בזמן העריכה, ולכן השינוי לא נשמר.',
    en: 'Somebody else in the office changed the name while you were editing, so your change was not saved.',
  },
  'settings.office.conflictNext': {
    he: 'אפשר לרענן את המסך, לראות את השם הנוכחי ולהחליט מחדש.',
    en: 'Refresh the screen, see the current name, and decide again.',
  },

  // The three sections and the entries under them.
  'signin.failed': { he: 'לא הצלחנו להתחבר. נסה שוב בעוד רגע.', en: 'We could not sign you in. Try again in a moment.' },
  'signin.working': { he: 'מתחבר…', en: 'Signing in…' },
  'signin.action': { he: 'התחברות', en: 'Sign in' },
  'field.password': { he: 'סיסמה', en: 'Password' },
  'signin.identifier': { he: 'שם משתמש או דואר אלקטרוני', en: 'Username or e-mail address' },
  'nav.sources': { he: 'מקורות נתונים', en: 'Data sources' },
  'nav.subscription': { he: 'המנוי של הארגון', en: 'Your subscription' },
  'nav.companyHome': { he: 'דף הבית של החברה', en: 'Company home' },
  'module.unknownTitle': { he: 'המודול הזה אינו קיים', en: 'No such module' },
  'module.notFoundTitle': { he: 'הדף שחיפשת לא קיים', en: 'That page does not exist' },
  'module.notFoundBody': { he: 'החלק שביקשת אינו קיים במערכת.', en: 'The part you asked for does not exist in the system.' },
  'state.unavailable': { he: 'לא זמין', en: 'Not available' },
  'module.plannedBody': { he: 'החלק הזה טרם נפתח במערכת. כשהוא ייפתח הוא יופיע כאן.', en: 'This part has not been opened yet. It will appear here when it is.' },
  'state.unavailablePlanned': { he: 'לא זמין — החלק הזה טרם נפתח במערכת', en: 'Not available — this part has not been opened yet' },
  'company.loadingTitle': { he: 'החברה עדיין נטענת', en: 'Still loading this company' },
  'company.loadingBody': { he: 'אם המצב הזה נשאר, ייתכן שהקישור מפנה לחברה שאינה זמינה לך.', en: 'If this stays as it is, the link may point at a company that is not available to you.' },
  'company.backToList': { he: 'חזרה לחברות שלי', en: 'Back to your companies' },
  'company.stateProvenance': { he: 'מצב החברה מוצג כפי שנרשם בארגון ואינו נגזר מהיעדר סימון.', en: 'The state of a company is shown as it was recorded, and is never inferred from a missing mark.' },
  'home.title': { he: 'הבית שלי', en: 'Home' },
  'home.readingCompanies': { he: 'את החברות בארגון', en: 'the companies in this organisation' },
  'signin.resetSent': { he: 'אם הכתובת הזו שייכת לחשבון — נשלחה אליה הודעה עם קישור לקביעת סיסמה חדשה. הקישור מגיע לתיבת הדואר בלבד ואינו מוצג כאן. אם לא הגיעה הודעה, ייתכן שהכתובת אינה מוכרת לנו.', en: 'If that address belongs to an account, a message with a link to set a new password has been sent to it. The link arrives in the mailbox only and is never shown here. If no message arrives, the address may not be one we know.' },
  /* Section 8: *"טופס ממורכז עם „ברוכים הבאים”"*. The PORTAL's name still comes from the
     server below it -- this is a greeting, not a second claim about which door this is. */
  'signin.welcome': { he: 'ברוכים הבאים', en: 'Welcome' },
  'signin.forgot': { he: 'שכחתי את הסיסמה', en: 'I forgot my password' },
  /*
   * ── THE CONTROL WAS THERE AND COULD NOT BE FOUND. **Owner, section 7, 2026-11-01.** ─────────
   *
   *     *"לא מצאתי כפתור לאיפוס סיסמה. ודא שיש מסלול ברור ועובד של ״שכחתי סיסמה״ במסך הכניסה."*
   *
   * It was a `btn--quiet` — no border, muted text, underlined — and DISABLED until the login
   * field had something in it. On an empty sign-in screen that is grey underlined text with no
   * affordance: the Owner read it as a sentence, which is what it looked like.
   *
   * Two changes, and the second is the one that matters. It is an outlined button, and it is
   * never disabled: a control that cannot be pressed and does not say why is the pattern this
   * product already refuses everywhere else. Pressing it with an empty field focuses the field
   * and says what is needed, which is a thing a person can act on.
   */
  'signin.forgotNeedsLogin': {
    he: 'נא להזין את שם המשתמש או כתובת הדואר, ואז ללחוץ שוב — הקישור נשלח לכתובת הרשומה.',
    en: 'Enter the login or e-mail address and press again — the link is sent to the registered address.',
  },
  'action.backTo': { he: 'חזרה ל', en: 'Back to' },
  'action.open': { he: 'פתיחה', en: 'Open' },
  'notfound.body': { he: 'ייתכן שהקישור ישן, או שהדף הועבר למקום אחר במערכת.', en: 'The link may be an old one, or the page may have moved elsewhere in the system.' },
  'placeholder.noData': { he: 'אין נתונים להצגה. אפשר לבחור תקופה בסרגל ההקשר שבראש המסך.', en: 'Nothing to show. You can choose a period in the context bar at the top of the screen.' },
  'placeholder.noSource': { he: 'אין עדיין מקור נתונים מחובר למסך הזה.', en: 'No data source is connected to this screen yet.' },
  'company.name': { he: 'שם החברה', en: 'Company name' },
  'company.noNumber': { he: 'ללא ח.פ רשום', en: 'No registered number' },
  'company.state': { he: 'מצב החברה', en: 'Company state' },
  'common.notes': { he: 'הערות', en: 'Notes' },
  'picker.empty': { he: 'אין חברות שיש לך גישה אליהן. גישה לחברה היא הענקה מפורשת, ומנהל הארגון מעניק אותה במסך הרשאות לחברות.', en: 'You have access to no companies. Access to a company is always an explicit grant, made by an organisation administrator on the company-permissions screen.' },
  'picker.noMatch': { he: 'לא נמצאו חברות התואמות לחיפוש.', en: 'No companies match that search.' },
  'picker.title': { he: 'החברות שלי', en: 'Companies you may open' },
  'picker.searchHint': { he: 'שם חברה או ח.פ', en: 'Company name or registered number' },
  'picker.reading': { he: 'את החברות שלך', en: 'the companies you may open' },
  'company.needsAttention': { he: 'דורש טיפול', en: 'Needs attention' },
  'picker.search': { he: 'חיפוש חברה', en: 'Search for a company' },
  'company.cardLink': { he: 'כרטיס בארגון', en: 'Open the company card' },
  'companies.quotaNote': { he: 'מכסה מלאה חוסמת הקמה של חברה נוספת בלבד. חברות קיימות, ההרשאות אליהן והנתונים שבהן אינם משתנים.', en: 'A full quota blocks creating one more company and nothing else. Existing companies, the permissions to them and the data in them are unchanged.' },
  'companies.quota': { he: 'המכסה שנרכשה', en: 'Purchased quota' },
  'companies.reading': { he: 'את חברות הארגון', en: 'the companies in this organisation' },
  'companies.empty': { he: 'אין עדיין חברות.', en: 'No companies yet.' },
  /* §11/§12 — the office's own money screen. */
  'billing.title': { he: 'חשבוניות וחיובים', en: 'Invoices and charges' },
  'billing.tabsLabel': { he: 'נושאי החיוב', en: 'Billing topics' },
  'billing.documentsTab': { he: 'חשבוניות וחיובים', en: 'Invoices and charges' },
  'billing.cancelTab': { he: 'ביטול מנוי', en: 'Cancel subscription' },
  'billing.readingDocuments': { he: 'קורא את מסמכי החיוב', en: 'Reading the billing documents' },
  'billing.readingCancellation': { he: 'קורא את מצב המנוי', en: 'Reading the subscription state' },
  'billing.documentsCaption': { he: 'מסמכי החיוב של הארגון', en: 'The organisation\'s billing documents' },
  'billing.documentsEmpty': { he: 'טרם הופקו מסמכי חיוב לארגון הזה.', en: 'No billing documents have been issued for this organisation yet.' },
  'billing.colDate': { he: 'תאריך', en: 'Date' },
  'billing.colNumber': { he: 'מספר מסמך', en: 'Document number' },
  'billing.colPeriod': { he: 'תקופת חיוב', en: 'Billing period' },
  'billing.colTotal': { he: 'סכום כולל מע״מ', en: 'Total incl. VAT' },
  'billing.colStatus': { he: 'מצב', en: 'State' },
  'billing.colView': { he: 'צפייה', en: 'View' },
  'billing.available': { he: 'זמין לצפייה', en: 'Available to view' },
  'billing.notAvailableYet': { he: 'טרם זמין', en: 'Not available yet' },
  'billing.statusIssued': { he: 'הופק', en: 'Issued' },
  'billing.statusPending': { he: 'טרם הופק', en: 'Not yet issued' },
  'billing.statusFailed': { he: 'הפקה נכשלה אצל הספק', en: 'Issuing failed at the provider' },
  'billing.statusVoid': { he: 'בוטל', en: 'Void' },
  /* 0041 — the office's own card, and the provider-hosted page it is entered on. */
  'card.tab': { he: 'אמצעי תשלום', en: 'Payment method' },
  'card.reading': { he: 'קורא את אמצעי התשלום', en: 'Reading the payment method' },
  'card.onFile': { he: 'הכרטיס השמור', en: 'The card on file' },
  'card.none': {
    he: 'אין אמצעי תשלום שמור לארגון הזה.',
    en: 'This organisation has no payment method on file.',
  },
  'card.noneBody': {
    he: 'הזנת הכרטיס נעשית בעמוד המאובטח של חברת הסליקה. פרטי הכרטיס אינם עוברים דרך המערכת הזאת ואינם נשמרים בה.',
    en: 'The card is entered on the clearing provider\u2019s own secure page. The card details do not pass through this system and are not stored in it.',
  },
  'card.brand': { he: 'סוג הכרטיס', en: 'Card type' },
  'card.last4': { he: 'ארבע ספרות אחרונות', en: 'Last four digits' },
  'card.expiry': { he: 'תוקף הכרטיס', en: 'Card expiry' },
  'card.tokenExpires': { he: 'תוקף ההרשאה אצל הספק', en: 'Provider authorisation expires' },
  'card.provider': { he: 'חברת הסליקה', en: 'Clearing provider' },
  'card.unknown': { he: 'לא נמסר', en: 'Not supplied' },
  'card.replace': { he: 'הזנת כרטיס חדש', en: 'Enter a new card' },
  'card.add': { he: 'הזנת אמצעי תשלום', en: 'Enter a payment method' },
  'card.leaving': {
    he: 'מעביר אותך לעמוד המאובטח של חברת הסליקה',
    en: 'Taking you to the clearing provider\u2019s secure page',
  },
  'card.unavailable': {
    he: 'לא ניתן לפתוח כרגע עמוד תשלום. אין חיבור סליקה מוגדר בסביבה הזאת, או שהספק סירב לפתוח עמוד.',
    en: 'A payment page cannot be opened right now. Either no clearing connection is configured on this estate, or the provider refused to open one.',
  },
  'card.sandboxNotice': {
    he: 'הכרטיס הזה נרשם במסלול בדיקה ולא נבדק אצל חברת סליקה. יש להזין כרטיס אמיתי לפני חיוב.',
    en: 'This card was recorded on a test path and was never checked by a clearing provider. A real card must be entered before any charge.',
  },
  /* The charge history: *"עדכון מצב התשלום"* — what was charged, and what did not resolve. */
  'card.chargesTitle': { he: 'חיובים', en: 'Charges' },
  'card.chargesReading': { he: 'קורא את החיובים', en: 'Reading the charges' },
  'card.chargesCaption': { he: 'ניסיונות החיוב של הארגון', en: 'The organisation\u2019s charge attempts' },
  'card.chargesEmpty': { he: 'טרם בוצע חיוב לארגון הזה.', en: 'No charge has been made for this organisation yet.' },
  'card.colPeriod': { he: 'תקופה', en: 'Period' },
  'card.colAmount': { he: 'סכום', en: 'Amount' },
  'card.colState': { he: 'מצב', en: 'State' },
  'card.colApproval': { he: 'אישור', en: 'Approval' },
  'card.state.in_flight': { he: 'בביצוע', en: 'In flight' },
  'card.state.charged': { he: 'חויב', en: 'Charged' },
  'card.state.declined': { he: 'נדחה', en: 'Declined' },
  'card.state.unknown': { he: 'תוצאה לא ידועה — דורש בדיקה', en: 'Outcome unknown — needs checking' },
  'card.alreadyExisted': {
    he: 'החיוב נמצא קיים אצל הספק ולא בוצע מחדש.',
    en: 'The charge was found to exist at the provider and was not made again.',
  },
  /* ── 0044 · הוספת מודול — the fourth tab ──────────────────────────────────────────────────── */
  'store.tab': { he: 'הוספת מודול', en: 'Add a module' },
  'store.reading': { he: 'קורא את המודולים שניתן להוסיף', en: 'Reading the modules available to add' },
  /*
   * ── WHAT A MODULE CARD SAYS. **The Owner, 2026-11-02, looking at the first draft.** ────────
   *
   *     *"אל תעשה את זה. שלחתי לך תמונה של הוספת CRM באייקאונט, בבקשה תעשה אותו דבר —
   *      כל הנתונים היבשים האלו לא מעניינים."*
   *
   * The first draft printed `כלול: CFO` and three figures in a row. That is a RECORD, and a
   * person deciding whether to buy something is not reading a record — the reference flow he sent
   * is a page about the product: what it does for you, in three lines, with the price.
   *
   * ── EVERY LINE BELOW IS HIS OWN DEFINITION OF THAT MODULE, NOT COPY I INVENTED ─────────────
   *
   * They are drawn from the module studies he dictated: the bank module *"holds no transactions —
   * they live at Open Finance and at the customer's accounting system"*; the credit-card module's
   * *"two posting methods"*; collections aged *"by the ERP value date"*; VAT as a *"multi-company
   * dashboard"*. A sentence promising something the study does not is the one thing a shop screen
   * may never do, because the office would be paying for it.
   *
   * ── AND A MODULE WITH NO ENTRY HERE STILL RENDERS ─────────────────────────────────────────
   *
   * It falls back to `store.lead.generic` and lists what is included. An article that licenses
   * SEVERAL modules — the opening package is four — is a bundle and says so, because a lead
   * sentence about one of its four would be describing a quarter of what is being sold.
   */
  /*
   * ארגון AND NOT משרד, AND `organisation` AND NOT `practice`. `organisationWording.test.ts`
   * caught FOUR lines of this copy on its first run, and it is right: *"המערכת אינה מיועדת רק
   * למשרדים"*. A shop screen that calls every customer a משרד is the retired noun at its loudest,
   * and the English half matters as much — that control's own header records a sweep that ran
   * over the Hebrew only and left the English saying the retired thing.
   */
  'store.lead.generic': {
    he: 'מודול נוסף לארגון, עם החיבורים שכבר קיימים אצלך.',
    en: 'Another module for the organisation, over the connections you already have.',
  },
  'store.lead.bundle': {
    he: 'חבילה: כל המודולים הבאים במחיר אחד.',
    en: 'A package: all of the following modules, at one price.',
  },
  'store.lead.vat_control': {
    he: 'משווה את ספרי החברה לדיווחי המע״מ ומראה איפה נפתח פער — לפני מועד הדיווח, לא אחריו.',
    en: 'Compares the books with what was filed for VAT and shows where a gap opened \u2014 before the filing date, not after.',
  },
  'store.point.vat_control.1': { he: 'תשומות ועסקאות מול מה שדווח בפועל', en: 'Input and output VAT against what was actually filed' },
  'store.point.vat_control.2': { he: 'מסך אחד לכל החברות שבארגון', en: 'One screen for every company in the organisation' },
  'store.point.vat_control.3': { he: 'נקרא מהמערכת של הלקוח — בלי להקליד מחדש', en: 'Read from the customer\u2019s own system \u2014 nothing retyped' },
  'store.lead.bank_statements_admin': {
    he: 'דפי הבנק נקראים ישירות מהבנק וממערכת ההנהלת חשבונות של הלקוח, ומסווגים לפי הכללים שאתה מגדיר.',
    en: 'Bank statements are read from the bank and from the customer\u2019s bookkeeping system, and classified by the rules you set.',
  },
  'store.point.bank_statements_admin.1': { he: 'סיווג לפי כללים שלך, לא לפי ברירת מחדל של מישהו אחר', en: 'Classified by your rules, not somebody else\u2019s defaults' },
  'store.point.bank_statements_admin.2': { he: 'התאמה מול הכרטסת', en: 'Reconciled against the ledger' },
  'store.point.bank_statements_admin.3': { he: 'התנועות נשארות אצל הלקוח — GLPOINT אינו שומר אותן', en: 'The transactions stay with the customer \u2014 GLPOINT does not store them' },
  'store.lead.credit_cards': {
    he: 'חיובי האשראי מגיעים מסודרים ונרשמים בשיטה שאתה עובד בה.',
    en: 'Card charges arrive sorted and are posted the way you work.',
  },
  'store.point.credit_cards.1': { he: 'פקודת יומן מפורטת לכל מועד חיוב, או סיווג בסגנון דף בנק', en: 'A detailed journal entry per charge date, or bank-style classification' },
  'store.point.credit_cards.2': { he: 'הפרדה בין חיוב של כרטיס לבין חיוב של ספק', en: 'A card charge and a supplier charge are kept apart' },
  'store.point.credit_cards.3': { he: 'כרטיס הנהלת חשבונות לכל כרטיס אשראי', en: 'A ledger account per card' },
  'store.lead.collection': {
    he: 'רואה מי חייב, כמה זמן, ושולח את המכתב — בלי לצאת מהמערכת.',
    en: 'See who owes, for how long, and send the letter \u2014 without leaving the product.',
  },
  'store.point.collection.1': { he: 'גיול חובות לפי תאריך הערך שבמערכת הלקוח', en: 'Aged by the value date in the customer\u2019s system' },
  'store.point.collection.2': { he: 'מכתבי גבייה בשם הארגון, או מהכתובת שלך', en: 'Letters in the organisation\u2019s name, or from your own address' },
  'store.point.collection.3': { he: 'מעקב אחרי מי נענה ומי לא', en: 'Track who answered and who did not' },
  'store.lead.cfo2': {
    he: 'תזרים, תחזית ורווחיות — מאותם נתונים שכבר יש ללקוח, בלי גיליון נפרד.',
    en: 'Cash flow, forecast and profitability \u2014 from the data the customer already has, with no separate spreadsheet.',
  },
  'store.point.cfo2.1': { he: 'תחזית תזרים קדימה', en: 'A forward cash-flow forecast' },
  'store.point.cfo2.2': { he: 'ניתוח רווחיות ומגמות', en: 'Profitability and trend analysis' },
  'store.point.cfo2.3': { he: 'מבוסס על ההנהלת חשבונות ועל חשבונות הבנק עצמם', en: 'Built on the bookkeeping and on the bank accounts themselves' },
  'store.lead.financial_reports': {
    he: 'מאזן, רווח והפסד וביאורים — מהכרטסת עצמה, עם נתיב ביקורת לכל מספר.',
    en: 'Balance sheet, P&L and notes \u2014 from the ledger itself, with an audit trail behind every figure.',
  },
  'store.point.financial_reports.1': { he: 'מאזן ורווח והפסד', en: 'Balance sheet and profit & loss' },
  'store.point.financial_reports.2': { he: 'ביאורים וניירות עבודה', en: 'Notes and working papers' },
  'store.point.financial_reports.3': { he: 'כל מספר ניתן למעקב עד לכרטיס', en: 'Every figure traceable to its account' },
  'store.includes': { he: 'כלול', en: 'Included' },
  'store.perMonth': { he: 'לחודש', en: 'per month' },
  'store.exVat': { he: 'לפני מע״מ', en: 'before VAT' },
  'store.withVat': { he: 'כולל מע״מ', en: 'including VAT' },
  'store.vatUnknown': {
    he: 'אין מע״מ רשום לתאריך הזה, ולכן לא מוצג סכום כולל',
    en: 'No VAT rate is recorded for this date, so no gross amount is shown',
  },
  'store.noPrice': { he: 'אין מחיר', en: 'No price' },
  'store.activate': { he: 'הפעלה', en: 'Activate' },
  'store.activating': { he: 'מפעיל', en: 'Activating' },
  'store.prorationNote': {
    he: 'ההפעלה מיידית. החיוב היחסי יופיע בחשבונית של התקופה שמסתיימת ב־',
    en: 'Activation is immediate. The prorated charge appears on the invoice for the period ending ',
  },
  'store.empty': { he: 'אין כרגע מודול להוספה', en: 'There is no module to add right now' },
  'store.emptyBody': {
    he: 'כל המודולים שבמחירון של הארגון כבר נמצאים במנוי.',
    en: 'Every article on this organisation\u2019s price book is already on the subscription.',
  },
  'store.noPriceBook': { he: 'לא ניתן להוסיף מודול כרגע', en: 'No module can be added right now' },
  'store.noPriceBookBody': {
    he: 'לארגון הזה לא משויך מחירון, ולכן אין מחיר לשום מודול. זה דבר שאנחנו מסדרים בצד הפלטפורמה.',
    en: 'This organisation has no price book, so nothing has a price. That is ours to set up.',
  },
  'store.noCard': { he: 'אין אמצעי תשלום שמור', en: 'No payment method on file' },
  'store.noCardBody': {
    he: 'אפשר להפעיל מודול גם עכשיו — ההפעלה אינה מחייבת את הכרטיס ברגע זה. אמצעי תשלום יידרש לחשבונית הבאה.',
    en: 'A module can be activated now \u2014 the activation does not charge a card. A payment method is needed for the next invoice.',
  },
  'store.bought': { he: 'המודול הופעל', en: 'The module was activated' },
  'store.boughtBody': {
    he: 'המודול פתוח מהיום. לא בוצע חיוב ברגע ההפעלה.',
    en: 'The module is open from today. No charge was made at the moment of activation.',
  },
  'store.boughtFrom': { he: 'פתוח מתאריך', en: 'Open from' },
  'store.boughtProrated': { he: 'החיוב היחסי בחשבונית לתקופה שמסתיימת ב־', en: 'Prorated charge on the invoice for the period ending' },
  'store.periodUnknown': { he: 'לא ידוע', en: 'Not known' },
  'store.refusedTitle': { he: 'ההפעלה לא בוצעה', en: 'The activation was not made' },
  /* Why an article is not for sale. The server's three words. */
  'store.block.not_for_sale': { he: 'אינו נמכר', en: 'Not for sale' },
  'store.block.module_not_ready': { he: 'המודול עוד לא נבנה', en: 'The module is not built yet' },
  'store.block.unpriced': { he: 'אין מחיר במחירון של הארגון', en: 'No price on this organisation\u2019s price book' },
  'store.block.other': { he: 'אינו זמין להפעלה', en: 'Not available to activate' },
  /* And why a purchase was refused. Each one is a different thing for a person to do. */
  'store.refusal.no_subscription': {
    he: 'למנוי של הארגון אין מצב שמאפשר הוספת מודול. אם המנוי מושעה, יש להסדיר אותו קודם.',
    en: 'This organisation\u2019s subscription is not in a state that allows adding a module. A suspended subscription has to be settled first.',
  },
  'store.refusal.no_price_book': {
    he: 'לארגון לא משויך מחירון. זה דבר שאנחנו מסדרים בצד הפלטפורמה.',
    en: 'The organisation has no price book. That is ours to set up.',
  },
  'store.refusal.unknown_sku': {
    he: 'המודול הזה אינו קיים בקטלוג. רענן את המסך.',
    en: 'This article is not in the catalogue. Reload the screen.',
  },
  'store.refusal.already_held': {
    he: 'המודול כבר נמצא במנוי של הארגון.',
    en: 'The organisation already holds this module.',
  },
  'store.refusal.not_for_sale': { he: 'המודול הזה אינו נמכר.', en: 'This article is not for sale.' },
  'store.refusal.module_not_ready': {
    he: 'המודול עוד לא נבנה, ולכן אי אפשר להפעיל אותו.',
    en: 'The module is not built yet, so it cannot be activated.',
  },
  'store.refusal.unpriced': {
    he: 'אין מחיר למודול הזה במחירון של הארגון. לא נמכר דבר בלי מחיר.',
    en: 'This article has no price on the organisation\u2019s price book. Nothing is sold without a price.',
  },
  'store.refusal.not_written': {
    he: 'ההפעלה לא נכתבה. משהו השתנה בין הבדיקה לכתיבה — רענן ונסה שוב.',
    en: 'The activation was not written. Something changed between the check and the write \u2014 reload and try again.',
  },
  'store.refusal.other': {
    he: 'השרת סירב להפעלה. לא נשמר שום שינוי.',
    en: 'The server refused the activation. Nothing was saved.',
  },
  /* The return leg. One screen for both journeys, because the question is the same question. */
  'cardReturn.title': { he: 'חזרה מעמוד התשלום', en: 'Back from the payment page' },
  'cardReturn.working': {
    he: 'בודק מול חברת הסליקה מה קרה בעמוד התשלום',
    en: 'Checking with the clearing provider what happened on the payment page',
  },
  'cardReturn.verified': { he: 'אמצעי התשלום נרשם', en: 'The payment method was recorded' },
  'cardReturn.verifiedBody': {
    he: 'התוצאה נקראה מחברת הסליקה בצד השרת ולא מהדפדפן. לא בוצע חיוב.',
    en: 'The result was read from the clearing provider on the server, not from the browser. No charge was made.',
  },
  'cardReturn.replaced': {
    he: 'הכרטיס הקודם הוחלף. חיובים שבוצעו בעבר נשמרים כפי שהיו.',
    en: 'The previous card was replaced. Charges already made are kept as they were.',
  },
  'cardReturn.continue': { he: 'המשך בהרשמה', en: 'Continue the registration' },
  'cardReturn.toBilling': { he: 'חזרה לחשבוניות וחיובים', en: 'Back to invoices and charges' },
  'cardReturn.noReference': {
    he: 'הכתובת הזאת אינה מכילה את המזהה של עמוד התשלום',
    en: 'This address carries no payment-page reference',
  },
  'cardReturn.noReferenceBody': {
    he: 'הגעת לכאן בלי המזהה שחברת הסליקה מחזירה אליו. התחל את הזנת אמצעי התשלום מחדש.',
    en: 'You reached this address without the reference the clearing provider returns to. Start entering the payment method again.',
  },
  'billing.noSubscriptionTitle': { he: 'אין מנוי לארגון הזה', en: 'This organisation has no subscription' },
  'billing.noSubscriptionBody': { he: 'ללא מנוי אין מה לבטל.', en: 'With no subscription there is nothing to cancel.' },
  'billing.accessEnds': { he: 'הגישה נשמרת עד ובכלל', en: 'Access continues through' },
  'billing.chargeImpact': { he: 'השפעת החיוב', en: 'Charge impact' },
  'billing.chargeOutstanding': { he: 'קיים חיוב שטרם הוסדר. ביטול החידוש אינו מבטל אותו.', en: 'A charge is still outstanding. Cancelling the renewal does not clear it.' },
  'billing.chargeNone': { he: 'לא יבוצע חיוב נוסף עבור תקופה חדשה.', en: 'No further charge will be made for a new period.' },
  'billing.cancelExplains': { he: 'ביטול מסיים את החידוש בלבד. הגישה נשמרת עד תום התקופה ששולמה, הנתונים לא נמחקים ואין זיכוי אוטומטי.', en: 'Cancelling ends the renewal only. Access continues to the end of the paid period, no data is deleted, and no credit is issued automatically.' },
  'billing.reasonLegend': { he: 'סיבת הביטול — שדה חובה', en: 'Reason for cancelling — required' },
  'billing.reason.too_expensive': { he: 'המחיר גבוה מדי', en: 'Too expensive' },
  'billing.reason.missing_capability': { he: 'חסרה יכולת שנדרשת לנו', en: 'A capability we need is missing' },
  'billing.reason.moved_to_another_product': { he: 'עברנו למוצר אחר', en: 'We moved to another product' },
  'billing.reason.practice_closing_or_merging': { he: 'הארגון נסגר או מתמזג', en: 'The practice is closing or merging' },
  'billing.reason.other': { he: 'סיבה אחרת', en: 'Another reason' },
  'billing.commentLabel': { he: 'הערה חופשית', en: 'Free-text comment' },
  'billing.commentOptional': { he: 'לא חובה', en: 'Optional' },
  'billing.commentTruncated': { he: 'ההערה נשמרה בקיצור. הביטול בוצע במלואו.', en: 'The comment was shortened. The cancellation itself went through in full.' },
  'billing.confirmLabel': { he: 'לאישור, הקלד CANCEL', en: 'To confirm, type CANCEL' },
  'billing.confirmHint': { he: 'באותיות גדולות, בדיוק כך: CANCEL', en: 'In capitals, exactly: CANCEL' },
  'billing.cancelSubmit': { he: 'בטל את החידוש', en: 'Cancel the renewal' },
  'billing.cancelSending': { he: 'מבטל…', en: 'Cancelling…' },
  'billing.cancelDoneTitle': { he: 'החידוש בוטל', en: 'The renewal was cancelled' },
  'billing.cancelDoneBody': { he: 'נשלח אישור לכתובת החיוב של הארגון.', en: 'A confirmation was sent to the organisation\'s billing address.' },
  'billing.cancelAlreadyTitle': { he: 'החידוש כבר בוטל', en: 'The renewal is already cancelled' },
  'billing.cancelAlreadyBody': { he: 'לא נדרשת פעולה נוספת.', en: 'No further action is needed.' },
  'billing.cancelRefusedTitle': { he: 'הביטול לא בוצע', en: 'The cancellation was not performed' },
  'billing.refusal.confirmation_mismatch': { he: 'המילה שהוקלדה אינה CANCEL באותיות גדולות. אפשר להקליד שוב.', en: 'The word typed is not CANCEL in capitals. Please type it again.' },
  'billing.refusal.reason_required': { he: 'יש לבחור סיבה לביטול.', en: 'A reason for cancelling must be chosen.' },
  'billing.refusal.unknown_reason': { he: 'הסיבה שנשלחה אינה אחת מהסיבות המוצגות. אפשר לרענן ולבחור מחדש.', en: 'The reason sent is not one of those offered. Refresh and choose again.' },
  'billing.refusal.already_cancelled': { he: 'החידוש כבר בוטל — אפשר לרענן ולראות את מועד סיום הגישה.', en: 'The renewal is already cancelled — refresh to see when access ends.' },
  'billing.refusal.state_not_cancellable': { he: 'במצב הנוכחי של המנוי לא ניתן לבטל. יש להסדיר תחילה את התשלום.', en: 'The subscription cannot be cancelled in its current state. Settle the payment first.' },
  'billing.refusal.no_subscription': { he: 'לא נמצא מנוי לארגון הזה.', en: 'No subscription was found for this organisation.' },
  'billing.cardTitle': { he: 'חשבוניות, חיובים וביטול מנוי', en: 'Invoices, charges and cancellation' },
  'billing.cardBody': { he: 'מסמכי החיוב של הארגון, וביטול החידוש במידה ונדרש.', en: 'The organisation\'s billing documents, and cancelling the renewal if needed.' },
  /* §4 — the three figures on the public offer. */
  'signup.vat': { he: 'מע״מ', en: 'VAT' },
  'signup.total': { he: 'סך הכול לתשלום', en: 'Total payable' },
  /* ITEM 1, 2026-11-03. The articles beside the plan, and what the combination costs. */
  'signup.addOns': { he: 'מודולים נוספים', en: 'Additional modules' },
  'signup.noAddOns': {
    he: 'אין כרגע מודולים נוספים להוספה לתכנית הזאת. אפשר להוסיף מודול מתוך המערכת בכל שלב.',
    en: 'No additional modules are available for this plan right now. A module can be added from inside the product at any time.',
  },
  'signup.quoting': { he: 'מחשב את הסכום…', en: 'Calculating the total…' },
  'signup.noVatRate': {
    he: 'לפני מע״מ — לא רשום שיעור מע״מ לתאריך הזה, ולכן לא מוצג סכום סופי',
    en: 'before VAT — no VAT rate is recorded for this date, so no final sum is shown',
  },
  'sub.plan': { he: 'התכנית', en: 'Plan' },
  'sub.state': { he: 'מצב המנוי', en: 'Subscription state' },
  'sub.period': { he: 'תקופת המנוי', en: 'Subscription period' },
  'sub.trialEnds': { he: 'סיום תקופת ניסיון', en: 'Trial ends' },
  'sub.noTrial': { he: 'אין תקופת ניסיון', en: 'No trial period' },
  'sub.purchased': { he: 'נרכש במנוי', en: 'Included in the subscription' },
  'sub.notPurchased': { he: 'לא נרכש במנוי', en: 'Not included in the subscription' },
  'sub.provenance': { he: 'המסך מציג את מה שנרכש ואת מה שבשימוש. סכומים, מחירים והנחות אינם חלק ממנו.', en: 'This screen shows what was purchased and what is in use. Amounts, prices and discounts are not part of it.' },
  'sub.reading': { he: 'את פרטי המנוי', en: 'the subscription details' },
  'sub.emptyTitle': { he: 'אין מנוי להצגה', en: 'No subscription to show' },
  'sub.emptyBody': { he: 'לא נמצא מנוי פעיל לארגון הזה.', en: 'No active subscription was found for this organisation.' },
  'sub.quotas': { he: 'מכסות — נרכש מול בשימוש', en: 'Quotas — purchased against in use' },
  /*
   * ══ THE SETTLEMENT PANEL. 0046, 2026-11-03. ═══════════════════════════════════════
   *
   * The Owner's §10 second stage is *"לאחר 14 יום, הסדרה בלבד"*, and 0038's rule for what that
   * word means is *"עדכון אמצעי תשלום אינו הסדרה."* Both sentences are in the wording below,
   * because an office that replaces an expired card and leaves thinking it is square is exactly
   * the office that is suspended on day fourteen.
   *
   * NO AMOUNT APPEARS IN ANY OF THESE STRINGS. The open period is priced by the server at the
   * moment of the press, from the price book and the subscription; a figure here would be the
   * screen's arithmetic about somebody else's charge.
   */
  'settle.title': { he: 'הסדרת תשלום', en: 'Settle the payment' },
  'settle.since': {
    he: 'החיוב לא הוסדר מאז {date}.',
    en: 'The charge has been unsettled since {date}.',
  },
  'settle.noAnchor': {
    he: 'קיים ניסיון חיוב שנכשל. מועד הכשל הראשון אינו מוצג משום שלא התקבל — זה אינו אומר שהחוב חדש.',
    en: 'A charge attempt has failed. The date of the first failure is not shown because it was not received — that does not mean the debt is new.',
  },
  'settle.cardIsNotSettlement': {
    he: 'עדכון כרטיס אינו הסדרה. החוב נסגר כשהתשלום מתבצע בפועל ומאומת מול הסליקה. אפשר להחליף כרטיס ולהמתין לחיוב הבא, או לבקש חיוב עכשיו.',
    en: 'Updating the card is not settlement. The debt closes when the payment actually goes through and is verified with the provider. You can replace the card and wait for the next charge, or ask to be charged now.',
  },
  'settle.payNow': { he: 'תשלום עכשיו', en: 'Pay now' },
  'settle.updateCard': { he: 'עדכון כרטיס', en: 'Update the card' },
  /*
   * WHO MAKES THE CHARGE. **2026-11-03.** The Owner: *"הלקוח אינו יכול להפעיל חיוב בכרטיס בעצמו...
   * במסך ההסדרה הלקוח יוכל לראות את החוב ולעדכן כרטיס, והחיוב יחכה לאישור שלי בפלטפורמה."* An
   * office that replaces its card and then sees nothing happen will press again somewhere or call;
   * this is the product saying what it already knows.
   */
  'settle.platformCharges': {
    he: 'החיוב מתבצע על ידי GL-POINT לאחר אישור, ולא מהמסך הזה. לאחר עדכון הכרטיס אין צורך בפעולה נוספת מצדך.',
    en: 'The charge is made by GL-POINT after approval, not from this screen. Once the card is updated nothing further is needed from you.',
  },
  'settle.intent': { he: 'תשלום החוב', en: 'Pay the debt' },
  'settle.blastRadius': {
    he: 'הכרטיס שרשום במערכת יחויב עכשיו בסכום התקופה שלא שולמה, יופק מסמך, והתקופה תיסגר כך שלא תיגבה שוב. הסכום מחושב בשרת לפי התכנית והכמויות, באותו חישוב של החיוב המתוזמן. הפעולה אינה משנה תכנית, אינה מבטלת מנוי ואינה מוחקת דבר.',
    en: 'The card on file will be charged now for the amount of the unpaid period, a document will be issued, and the period will be closed so it cannot be charged again. The amount is computed on the server from the plan and the quantities, by the same calculation the scheduled charge uses. This does not change the plan, does not cancel the subscription and deletes nothing.',
  },
  /*
   * THE RE-SELECTION SAYS SOMETHING DIFFERENT FROM THE TRIGGER. 09 §2.2 asks for an explicit
   * re-selection rather than a bare OK; a confirm button whose words are IDENTICAL to the one
   * just pressed is the bare OK with the act's name on it, and a person who meant to open the
   * dialog and read it can press the same place twice without reading anything.
   */
  'settle.confirm': { he: 'אישור ותשלום', en: 'Confirm and pay' },
  'settle.paying': { he: 'משלם…', en: 'Paying…' },
  'settle.partialNote': {
    he: 'אין אפשרות לשלם חלק מהחוב במסך הזה. אם נדרש סידור אחר, יש לפנות אל הפלטפורמה.',
    en: 'Part of the debt cannot be paid from this screen. If a different arrangement is needed, contact the platform.',
  },
  'settle.noSubscription': {
    he: 'לארגון אין מנוי, ולכן אין תקופה לגבות. יש לפנות אל הפלטפורמה.',
    en: 'This organisation has no subscription, so there is no period to charge. Contact the platform.',
  },
  'settle.nothingDue': {
    he: 'לא נמצאה תקופה לגבייה, ולכן לא בוצע חיוב. אם מוצג כאן חוב שלא הוסדר, יש לפנות אל הפלטפורמה.',
    en: 'No period was found to charge, so nothing was charged. If an unsettled debt is still shown here, contact the platform.',
  },
  'settle.documentIssued': {
    he: 'הופק מסמך. הוא יופיע ברשימת המסמכים שלמטה.',
    en: 'A document was issued. It appears in the list of documents below.',
  },
  'settle.noDocumentYet': {
    he: 'טרם הופק מסמך לתשלום הזה. זה אינו אומר שהתשלום לא בוצע.',
    en: 'No document has been issued for this payment yet. That does not mean the payment did not go through.',
  },
  /* §6.11, 2026-11-03. The tasks tab on a client's own record. */
  /* 0047. The office's path for a change it cannot make itself. */
  'change.tab': { he: 'בקשת שינוי', en: 'Request a change' },
  'card.tabTasks': { he: 'משימות', en: 'Tasks' },
  'charges.toBillingRun': { he: 'למסך ביצוע החיובים', en: 'To the billing run' },
  'charges.toChangeRequests': { he: 'לבקשות השינוי', en: 'To change requests' },
  'charges.toDunning': { he: 'למסך הגבייה', en: 'To collections' },
  'billing.readingStanding': { he: 'את מצב התשלום', en: 'the payment standing' },
  'billing.standing': { he: 'מצב התשלום', en: 'Payment standing' },
  'billing.unsettledSince': { he: 'לא הוסדר מאז', en: 'Unsettled since' },
  'billing.nothingUnsettled': { he: 'אין חיוב שלא הוסדר', en: 'Nothing is unsettled' },
  'billing.attempts': { he: 'ניסיונות חיוב שנכשלו', en: 'Failed charge attempts' },
  'billing.noAttempts': { he: 'אין', en: 'None' },
  'billing.nextCharge': { he: 'מועד החיוב הבא', en: 'Next charge' },
  'billing.noNextCharge': { he: 'אין חיוב מתוכנן במצב הנוכחי', en: 'No charge is scheduled in the current state' },
  'sub.readingQuotas': { he: 'את המכסות', en: 'the quotas' },
  'sub.quotasNoPlan': { he: 'אין מנוי פעיל, ולכן אין מכסות להציג. זה אינו מכסה של אפס.', en: 'There is no live subscription, so there are no quotas to show. That is not a quota of zero.' },
  'sub.changeTitle': { he: 'שינוי מסחרי', en: 'A commercial change' },
  /*
   * ── IT NAMED THE PLAN CHANGE, AND THE PLAN CHANGE IS ON THIS SCREEN NOW. **ITEM 4.** ────────
   * Leaving the old sentence would have been a screen telling a customer that the control above
   * it does not exist. What remains is what is still true.
   */
  'sub.changeBody': { he: 'הרחבת מכסת חברות או משתמשים נעשית מול הפלטפורמה ואינה מתבצעת במסך הזה. הוספת מודול נעשית במסך החיובים.', en: 'Raising a company or seat quota is arranged with the platform and is not done on this screen. A module is added from the billing screen.' },
  'common.persistsContactSupport': { he: 'אם המצב הזה נשאר, יש לפנות לתמיכה.', en: 'If this stays as it is, contact support.' },
  'state.notOpenedYet': { he: 'החלק הזה טרם נפתח במערכת', en: 'This part has not been opened yet' },
  'sub.changeNext': { he: 'ניתן לפנות למנהל הארגון או לתמיכה כדי לפתוח בקשת שינוי.', en: 'You can ask an organisation administrator or support to open a change request.' },
  'security.title': { he: 'אבטחה ויומן ביקורת', en: 'Security and the audit journal' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'audit.when': { he: 'מועד', en: 'When' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'audit.who': { he: 'מבצע', en: 'Who' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'audit.what': { he: 'פעולה', en: 'What' },
  'audit.outcome': { he: 'תוצאה', en: 'Outcome' },
  'audit.denialReason': { he: 'הסיבה לדחייה', en: 'Reason for the refusal' },
  'support.revokedByOrg': { he: 'בוטל בידי הארגון', en: 'Revoked by the organisation' },
  'security.provenance': { he: 'היומן מציג את אירועי הארגון הזה בלבד. אירועים של ארגונים אחרים אינם נגישים ממנו בשום דרך.', en: 'The journal shows the events of this organisation only. Events of other organisations are not reachable from it in any way.' },
  'support.reading': { he: 'את הרשאות התמיכה', en: 'the support grants' },
  'audit.reading': { he: 'את יומן הביקורת', en: 'the audit journal' },
  'audit.recent': { he: 'אירועים אחרונים', en: 'Recent events' },
  'audit.empty': { he: 'לא נרשמו עדיין אירועים בארגון.', en: 'No events have been recorded for this organisation yet.' },
  'audit.underGrant': { he: 'בוצע תחת הרשאת תמיכה', en: 'Performed under a support grant' },
  'audit.underGrantTitle': { he: 'בוצעו פעולות תחת הרשאת תמיכה', en: 'Some actions were performed under a support grant' },
  'support.title': { he: 'הרשאות תמיכה לארגון הזה', en: 'Support grants for this organisation' },
  'support.none': { he: 'אין כרגע הרשאת תמיכה פעילה לארגון הזה.', en: 'There is no active support grant for this organisation.' },
  'personal.title': { he: 'האבטחה שלי', en: 'Your account security' },
  'nav.platformConsole': { he: 'קונסולת הפלטפורמה', en: 'Platform console' },
  'personal.account': { he: 'החשבון', en: 'Account' },
  'personal.noAccount': { he: 'אין חשבון מחובר', en: 'No account is signed in' },
  'personal.mfa': { he: 'אימות דו-שלבי', en: 'Two-factor authentication' },
  'personal.noPolicy': { he: 'לא התקבלה מדיניות מהשרת', en: 'No policy was received from the server' },
  'session.active': { he: 'פעילה', en: 'Active' },
  'session.ended': { he: 'הסתיימה', en: 'Ended' },
  'common.sending': { he: 'שולח…', en: 'Sending…' },
  'personal.sessionsProvenance': { he: 'רשימת ההתחברויות מציגה מתי נפתחה כל אחת ומתי נראתה לאחרונה. היא אינה שומרת מיקום, כתובת רשת או פרטי מכשיר.', en: 'The session list shows when each one opened and when it was last seen. It stores no location, no network address and no device details.' },
  'personal.readingSessions': { he: 'את ההתחברויות של החשבון', en: 'the sessions of this account' },
  'personal.noSessionsTitle': { he: 'אין התחברויות להצגה', en: 'No sessions to show' },
  'personal.noSessionsBody': { he: 'השרת ענה, ואין לחשבון הזה אף התחברות רשומה — גם לא כזו שהסתיימה.', en: 'The server answered, and this account has no recorded session at all — not even an ended one.' },
  'personal.allRevoked': { he: 'כל ההתחברויות נותקו', en: 'Every session was ended' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'personal.whereSignedIn': { he: 'התחברויות', en: 'Sessions' },
  'personal.confirmRevoke': { he: 'הבנתי שגם ההתחברות הנוכחית תסתיים', en: 'I understand that this session will end too' },
  'placeholder.noDataFor': { he: 'אין נתונים לתקופה', en: 'No data for the period' },
  'picker.showing': { he: 'חברות שיש לך גישה אליהן', en: 'Companies you may open' },
  'companies.showing': { he: 'חברות בארגון', en: 'Companies in this organisation' },
  'company.detailsAndState': { he: 'פרטים ומצב', en: 'Details and state' },
  'company.create': { he: 'הקמת חברה', en: 'Create a company' },
  'audit.underGrantNote': { he: 'מהפעולות ביומן בוצעו בידי תמיכת הפלטפורמה תחת הרשאה שניתנה לארגון. כל פעולה כזו נרשמת תמיד.', en: 'of the journal entries were performed by platform support under a grant given by this organisation. Every such action is always recorded.' },
  'audit.showing': { he: 'אירועים אחרונים — כולל פעולות שנדחו', en: 'Recent events, refusals included' },
  'support.consent': { he: 'אישור ההרשאה', en: 'Approve the grant' },
  'support.revoke': { he: 'ביטול ההרשאה', en: 'Revoke the grant' },
  'personal.mfaEnrol': { he: 'רישום אימות דו-שלבי', en: 'Set up two-factor authentication' },
  'personal.mfaChallenge': { he: 'אימות דו-שלבי — הזנת קוד', en: 'Two-factor authentication — enter a code' },
  'personal.endedKept': { he: 'התחברות שהסתיימה נשארת ברשימה יחד עם הסיבה. זה מה שמאפשר לברר בדיעבד מה נעשה בה, ולכן היא אינה נמחקת.', en: 'An ended session stays in the list with its reason. That is what makes it possible to establish afterwards what was done in it, which is why it is never deleted.' },
  'personal.revokedNote': { he: 'התחברויות נותקו, כולל זו שממנה בוצעה הפעולה. נדרשת התחברות מחדש.', en: 'sessions were ended, including the one this was done from. You will need to sign in again.' },
  'personal.goToSignIn': { he: 'מעבר להתחברות', en: 'Go to sign-in' },
  'doc.title': { he: 'נייר השלמות', en: 'Outstanding-items paper' },
  'doc.notFoundBody': { he: 'המסמך שביקשת אינו קיים או שאינו זמין לך.', en: 'The document you asked for does not exist, or is not available to you.' },
  'doc.markIrrelevantTitle': { he: 'סימון חסם כלא רלוונטי', en: 'Mark a blocker as not relevant' },
  'doc.markIrrelevant': { he: 'סימון כלא רלוונטי', en: 'Mark as not relevant' },
  'common.reason': { he: 'סיבה', en: 'Reason' },
  'common.validUntil': { he: 'בתוקף עד', en: 'Valid until' },
  'mfa.doneTitle': { he: 'האימות הושלם', en: 'Verification complete' },
  'mfa.doneBody': { he: 'ההזדהות אושרה.', en: 'Your identity was confirmed.' },
  'mfa.badFormat': { he: 'הקוד אינו בפורמט הנכון', en: 'That code is not in the right format' },
  'mfa.wrongTitle': { he: 'הקוד אינו נכון', en: 'That code is not correct' },
  'mfa.wrongBody': { he: 'הקוד שהוזן אינו מתאים. אפשר להמתין לקוד הבא ביישומון ולנסות שוב.', en: 'The code entered does not match. You can wait for the next code in the app and try again.' },
  /*
   * THE SAME REFUSAL, WITH ADVICE THE PERSON CAN ACT ON. §2.9, 2026-10-29.
   *
   * Somebody who just failed a RECOVERY code is on that path because they cannot reach the
   * authenticator, so *wait for the next code in the app* was a next step they do not have. The
   * REASON is unchanged and still single -- nothing here says whether the code was wrong,
   * already used or from an exhausted set -- and that uniformity is what the replay defence
   * rests on. What changed is only which remedy is offered.
   */
  'mfa.wrongBodyRecovery': { he: 'הקוד שהוזן אינו מתאים. כל קוד שחזור תקף לשימוש אחד בלבד, ויש להזין קוד אחר מהרשימה ששמרת.', en: 'The code entered does not match. Each recovery code works once only — enter a different one from the list you saved.' },
  'mfa.recoveryHint': { he: 'הזן אחד מקודי השחזור ששמרת בעת הרישום. כל קוד שחזור תקף לשימוש אחד בלבד.', en: 'Enter one of the recovery codes you saved when you set this up. Each recovery code works once only.' },
  'mfa.codeHint': { he: 'הזן את הקוד המוצג כעת ביישומון האימות שלך.', en: 'Enter the code your authenticator app is showing now.' },
  'mfa.recoveryLabel': { he: 'קוד שחזור', en: 'Recovery code' },
  'mfa.codeLabel': { he: 'קוד מהיישומון', en: 'Code from the app' },
  'common.checking': { he: 'בודק…', en: 'Checking…' },
  'mfa.verify': { he: 'אימות והמשך', en: 'Verify and continue' },
  'mfa.backToCode': { he: 'חזרה לקוד מהיישומון', en: 'Back to the code from the app' },
  'mfa.useRecovery': { he: 'אין לי גישה ליישומון — שימוש בקוד שחזור', en: 'I cannot reach the app — use a recovery code' },
  'mfa.provenance': { he: 'בדיקת הקוד נעשית בשרת בלבד. הדפדפן אינו מחזיק את הסוד ואינו סופר ניסיונות.', en: 'The code is checked on the server only. The browser never holds the secret and never counts attempts.' },
  'mfa.noChallengeTitle': { he: 'אין אתגר פתוח', en: 'No verification is in progress' },
  'mfa.noChallengeBody': { he: 'הגעת למסך הזה בלי אתגר פתוח, ולכן אין מה לבדוק. יש להתחיל את ההתחברות מחדש.', en: 'You reached this screen without a verification in progress, so there is nothing to check. Start signing in again.' },
  'cred.acceptTitle': { he: 'קבלת ההזמנה', en: 'Accept the invitation' },
  'cred.acceptLead': { he: 'בחר סיסמה לחשבון שלך. ההזמנה אינה מקנה הרשאות — מנהל הארגון מעניק תפקיד וגישה לחברות בנפרד.', en: 'Choose a password for your account. An invitation grants no permissions — an organisation administrator assigns a role and company access separately.' },
  'cred.createAccount': { he: 'יצירת החשבון', en: 'Create the account' },
  'cred.createdTitle': { he: 'החשבון נוצר', en: 'Account created' },
  'cred.createdBody': { he: 'החשבון נוצר עם הסיסמה שבחרת. לא בוצעה כניסה אוטומטית — יש להתחבר עכשיו עם הסיסמה הזו.', en: 'The account was created with the password you chose. You were not signed in automatically — sign in now with that password.' },
  'cred.resetTitle': { he: 'קביעת סיסמה חדשה', en: 'Set a new password' },
  'cred.resetLead': { he: 'בחר סיסמה חדשה לחשבון שלך.', en: 'Choose a new password for your account.' },
  'cred.setPassword': { he: 'קביעת הסיסמה', en: 'Set the password' },
  'cred.updatedTitle': { he: 'הסיסמה עודכנה', en: 'Password updated' },
  'cred.updatedBody': { he: 'הסיסמה החדשה נשמרה. לא בוצעה כניסה אוטומטית, ואם מוגדר לחשבון אימות דו-שלבי — הוא עדיין נדרש. איפוס סיסמה אינו מדלג על הגורם השני.', en: 'The new password was saved. You were not signed in automatically, and if two-factor authentication is set up on the account it is still required. Resetting a password never skips the second factor.' },
  'cred.linkInvalidBody': { he: 'הקישור אינו תקף. ייתכן שפג תוקפו, שכבר נעשה בו שימוש או שבוטל. אין לנו דרך לומר במה מהם מדובר, ולכן צריך לבקש קישור חדש.', en: 'The link is not valid. It may have expired, already been used, or been revoked. We have no way to tell which, so a new link has to be requested.' },
  'cred.serverFailedBody': { he: 'הבקשה נשלחה לשרת והשרת לא השלים אותה, ולכן הקישור כלל לא נבדק ושום חשבון לא נוצר. ', en: 'The request reached the server and the server did not complete it, so the link was never checked and no account was created. ' },
  'cred.serverFailedNote': { he: 'אין להסיק מכך שהקישור פג או שאינו תקין, ואין צורך לבקש קישור חדש בשלב הזה.', en: 'Do not conclude from this that the link expired or is faulty, and there is no need to request a new one yet.' },
  'cred.tooShortBody': { he: 'הסיסמה שנבחרה קצרה מדי. אפשר לבחור סיסמה ארוכה יותר ולנסות שוב באותו מסך. ', en: 'The password chosen is too short. You can choose a longer one and try again on this same screen. ' },
  'cred.tooShortNote': { he: 'בדיקת אורך הסיסמה קודמת לבדיקת הקישור, ולכן היא אינה אומרת דבר על תקפותו.', en: 'The length check happens before the link is checked, so it says nothing about whether the link is valid.' },
  'cred.provenance': { he: 'הסיסמה נבדקת ונשמרת בשרת. הדפדפן אינו שומר אותה ואינו שולח אותה לשום מקום אחר.', en: 'The password is checked and stored on the server. The browser never keeps it and never sends it anywhere else.' },
  'cred.noLinkTitle': { he: 'אין קישור תקף', en: 'No valid link' },
  'cred.noLinkBody': { he: 'הגעת למסך הזה בלי קישור, ולכן אין מה לבדוק. יש לפתוח את הקישור שנשלח אליך בדואר.', en: 'You reached this screen without a link, so there is nothing to check. Open the link that was e-mailed to you.' },
  'cred.newPassword': { he: 'סיסמה חדשה', en: 'New password' },
  /*
   * ── IT SAYS THE RULE, NOT THAT THERE IS ONE. **2026-11-04.** ─────────────────────────────────
   *
   * This read *"הסיסמה נבדקת בשרת. אם היא קצרה מדי, נאמר לך זאת"* — a sentence about the
   * MECHANISM, which leaves a person to guess, fail and be told. The Owner met it and read the
   * refusal as a maximum: *"אם אני מקיש יותר מ-8 תווים לא נותן לי להתקדם"*. He had typed fewer
   * than twelve.
   *
   * `{min}` IS SUBSTITUTED FROM `MIN_PASSWORD_LENGTH`, the same constant the server refuses on. A
   * hint that said twelve beside a server that wanted thirteen would be worse than the sentence
   * it replaced.
   *
   * AND IT SAYS WHAT IS **NOT** REQUIRED. There is no uppercase, digit or symbol rule, and that is
   * deliberate: composition rules push people towards `Password1!`, and a long passphrase beats a
   * short complicated one. Somebody who expects to be asked for a symbol invents one and remembers
   * it worse, so the screen closes the question instead of leaving it open.
   */
  'cred.passwordHint': {
    he: 'לפחות {min} תווים. אפשר משפט או צירוף מילים — אין דרישה לאותיות גדולות, לספרות או לסימנים. הקישור יישאר תקף אם הסיסמה תידחה.',
    en: 'At least {min} characters. A phrase is fine — no uppercase, digit or symbol is required. The link stays valid if the password is refused.',
  },
  'cred.tooShortTitle': {
    he: 'הסיסמה קצרה מדי — נדרשים לפחות {min} תווים',
    en: 'That password is too short — at least {min} characters are needed',
  },
  'cred.linkInvalidTitle': { he: 'הקישור אינו תקף', en: 'The link is not valid' },
  'cred.serverFailedTitle': { he: 'הבקשה נכשלה — הקישור לא נבדק', en: 'The request failed — the link was not checked' },
  'common.supportRef': { he: 'אסמכתת תקלה למסירה לתמיכה:', en: 'Support reference code:' },
  'access.title': { he: 'הרשאות לחברות', en: 'Company permissions' },
  'access.readBody': { he: 'צפייה בנתוני החברה בלבד.', en: 'View the data of the company only.' },
  'access.writeBody': { he: 'צפייה ועדכון נתונים בחברה.', en: 'View and update data in the company.' },
  'access.approveBody': { he: 'צפייה, עדכון ואישור פעולות בחברה.', en: 'View, update and approve actions in the company.' },
  'access.company': { he: 'החברה', en: 'Company' },
  'access.companyUnavailable': { he: 'חברה שאינה זמינה לך', en: 'A company that is not available to you' },
  'access.user': { he: 'המשתמש', en: 'User' },
  'access.userUnavailable': { he: 'משתמש שאינו זמין', en: 'A user who is not available' },
  /*
   * **Owner, 2026-10-28:** *"לא מבין את הגישות, דיברנו על זה, תראה באייקונט איך זה
   * בנוי"*, with a screenshot of the reference: ONE field, named הרשאות, chosen from a list.
   * It was רמת הגישה — a term that names a mechanism rather than the thing being chosen.
   */
  'access.level': { he: 'הרשאות', en: 'Permissions' },
  'access.levelPick': { he: 'בחר הרשאה', en: 'Choose permissions' },
  'access.grantedAt': { he: 'הוענקה', en: 'Granted' },
  'access.noEnd': { he: 'ללא מועד סיום', en: 'No end date' },
  'common.remove': { he: 'הסרה', en: 'Remove' },
  'access.noUserYet': { he: 'טרם נבחר משתמש', en: 'No user chosen yet' },
  'access.noCompanyYet': { he: 'נבחר משתמש · טרם נבחרה חברה', en: 'User chosen · no company chosen yet' },
  'access.grant': { he: 'הענקת גישה', en: 'Grant access' },
  'access.provenance': { he: 'גישה לחברה היא הענקה מפורשת לכל חברה בנפרד. חברה שלא הוענקה אינה נגישה — גם לבעלים של הארגון.', en: 'Access to a company is an explicit grant, made per company. A company that was not granted is not reachable — including by the owner of the organisation.' },
  'access.noBlocklist': { he: 'אין רשימת חסימה', en: 'There is no block list' },
  'access.reading': { he: 'את המשתמשים ואת ההרשאות', en: 'the users and their permissions' },
  'access.grantedTitle': { he: 'הגישה הוענקה', en: 'Access granted' },
  'access.revokedTitle': { he: 'ההרשאה בוטלה', en: 'The permission was revoked' },
  'access.existing': { he: 'הרשאות קיימות', en: 'Existing permissions' },
  'access.empty': { he: 'לא נרשמו הרשאות לחברות.', en: 'No company permissions have been recorded.' },
  'access.grantTitle': { he: 'הענקת גישה לחברות', en: 'Grant access to companies' },
  'access.companiesToGrant': { he: 'החברות שיוענקו', en: 'Companies to grant' },
  'entity.ltd': { he: 'חברה בע״מ', en: 'Limited company' },
  'entity.licensed': { he: 'עוסק מורשה', en: 'Licensed dealer' },
  'entity.exempt': { he: 'עוסק פטור', en: 'Exempt dealer' },
  'entity.individual': { he: 'יחיד', en: 'Individual' },
  'entity.association': { he: 'עמותה', en: 'Non-profit association' },
  'entity.reporting': { he: 'ישות מדווחת', en: 'Reporting entity' },
  'entity.numberLtd': { he: 'מספר רשמי — ח״פ', en: 'Official number — company number' },
  'entity.numberDealer': { he: 'מספר רשמי — מספר עוסק', en: 'Official number — dealer number' },
  'entity.numberId': { he: 'מספר רשמי — תעודת זהות', en: 'Official number — identity number' },
  'entity.numberAssociation': { he: 'מספר רשמי — מספר עמותה', en: 'Official number — association number' },
  'entity.number': { he: 'מספר רשמי', en: 'Official number' },
  'onboard.submit': { he: 'הקמת החברה', en: 'Create the company' },
  'onboard.step1': { he: 'שלב 1 מתוך 3 — פרטי הזיהוי של החברה. אפשר להשלים את השאר אחרי שהיא תיפתח.', en: 'Step 1 of 3 — the identifying details. The rest can be completed after it is opened.' },
  'onboard.prospectNote': { he: 'חברה חדשה נפתחת במצב ״לקוח פוטנציאלי״. היא אינה נספרת כחברה פעילה עד שמצבה משתנה במפורש.', en: 'A new company opens as a prospect. It is not counted as an active company until its state is changed explicitly.' },
  'onboard.createdTitle': { he: 'החברה הוקמה', en: 'The company was created' },
  'onboard.createdBody': { he: 'החברה נפתחה במצב ״לקוח פוטנציאלי״. עדיין לא ניתנה גישה אליה לאף משתמש.', en: 'The company opened as a prospect. No user has been given access to it yet.' },
  'onboard.failedBody': { he: 'השרת לא השלים את הקמת החברה. איננו יכולים לדעת מכאן אם נוצרה חברה חלקית.', en: 'The server did not complete creating the company. From here we cannot tell whether a partial company was created.' },
  'onboard.failedNext': { he: 'חזור לרשימת החברות ובדוק אם החברה כבר קיימת לפני ניסיון נוסף — יצירה כפולה היא התוצאה הגרועה יותר.', en: 'Go back to the company list and check whether it already exists before trying again — a duplicate is the worse outcome.' },
  'onboard.notCreatedTitle': { he: 'החברה לא הוקמה', en: 'The company was not created' },
  'onboard.unreachableBody': { he: 'לא הצלחנו ליצור קשר עם השרת, ולכן לא נוצרה חברה.', en: 'We could not reach the server, so no company was created.' },
  'onboard.whatStep1': { he: 'פרטי הזיהוי — השם, סוג הישות והמספר הרשמי', en: 'The identifying details — name, entity kind and official number' },
  'onboard.whatStep2': { he: 'החברה נפתחת במצב ״לקוח פוטנציאלי״', en: 'The company opens as a prospect' },
  'onboard.whatStep3': { he: 'הענקת גישה — עד אז אף אחד בארגון אינו רואה אותה', en: 'Granting access — until then nobody in the organisation sees it' },
  'onboard.legalName': { he: 'השם המשפטי של החברה', en: 'Legal name of the company' },
  'onboard.displayName': { he: 'שם לתצוגה', en: 'Display name' },
  'onboard.entityKind': { he: 'סוג הישות', en: 'Entity kind' },
  'onboard.entityKindHint': { he: 'סוג הישות נקבע במפורש ואינו נגזר מהשם.', en: 'The entity kind is chosen explicitly and is never inferred from the name.' },
  'common.retryNothingSaved': { he: 'אפשר לנסות שוב מאוחר יותר. לא נשמר דבר.', en: 'You can try again later. Nothing was saved.' },
  'doc.draft': { he: 'הפקת טיוטה', en: 'Produce a draft' },
  'doc.retry': { he: 'ניסיון הפקה מחדש', en: 'Try producing it again' },
  'doc.blastPrefix': { he: 'הסימון יירשם על שמך עבור', en: 'The mark will be recorded in your name for' },
  'doc.blastSuffix': { he: 'ויופיע בגיליון הממצאים', en: 'and will appear in the findings sheet' },
  'mfa.backToEnrol': { he: 'חזרה למסך הרישום', en: 'Back to the setup screen' },
  'cred.retrySameScreen': { he: 'אפשר לנסות שוב באותו מסך — הקישור לא נבדק ולכן הוא עדיין תקף. אם זה חוזר, יש לפנות לתמיכה.', en: 'You can try again on this same screen — the link was never checked, so it is still valid. If this keeps happening, contact support.' },
  'access.removeTitle': { he: 'הסרת הגישה', en: 'Remove the access' },
  'access.noBlocklistBody': { he: 'כדי למנוע ממשתמש גישה לחברה — אין צורך לחסום אותה. פשוט לא מעניקים אותה. החרגה היא היעדר הרשאה, וכך אפשר לשמור על הפרדה בשל ניגוד עניינים בלי להסתמך על סימון נוסף.', en: 'To keep a user out of a company there is no need to block it. Simply do not grant it. An exclusion IS the absence of a permission, which is how a conflict-of-interest separation is kept without relying on a second mark.' },
  'access.chosen': { he: 'נבחר משתמש · חברות', en: 'User chosen · companies' },
  'access.sensitiveNote': { he: 'הענקת גישה היא פעולה רגישה. ייתכן שתידרש הזדהות נוספת לפני שהיא תתבצע, והיא נרשמת ביומן הביקורת בכל מקרה.', en: 'Granting access is a sensitive action. You may be asked to confirm your identity again before it is performed, and it is recorded in the audit journal either way.' },
  'access.perCompanyNote': { he: 'כל חברה נרשמת בנפרד וניתן לבטל כל אחת לחוד.', en: 'Each company is recorded separately and can be revoked on its own.' },
  'access.rowsWritten': { he: 'הרשאות נפרדות נרשמו, אחת לכל חברה.', en: 'separate permissions were written, one per company.' },
  'access.revokedBody': { he: 'בוטלה הרשאה אחת — לחברה אחת ולמשתמש אחד. שאר ההרשאות של אותו משתמש לא השתנו.', en: 'One permission was revoked — for one company and one user. That user other permissions are unchanged.' },
  'access.showingAll': { he: 'הרשאות — כל ההרשאות בארגון', en: 'Permissions — all of them in this organisation' },
  'access.showingForUser': { he: 'הרשאות · לפי המשתמש שנבחר', en: 'Permissions · for the chosen user' },
  'onboard.legalNameHint': { he: 'השם המלא כפי שהוא רשום. הוא שמזהה את החברה בכל מסך במערכת.', en: 'The full name as registered. It is what identifies the company on every screen in the system.' },
  'onboard.numberHint': { he: 'תשע ספרות. רווחים ומקפים יוסרו.', en: 'Nine digits. Spaces and hyphens are removed.' },
  'common.save': { he: 'שמירה', en: 'Save' },

  // ---- THE USER CARD, section 6, 2026-10-27 --------------------------------------------------
  //
  // TITLES AND LABELS, NOT PARAGRAPHS. Owner, 2026-10-27: *"המערכת צריכה להיות מקצועית עם כותרות
  // לפעולות מבלי לתת הסברים מיותרים."* What a reader needs in order to act stays on the screen;
  // the three sentences about what a level MEANS are behind the help control, because they are the
  // one thing an administrator genuinely cannot guess and the Owner asked for them by name.
  // ---- the card's fixed tabs and its collapsible groups, section 4, 2026-10-28 --------------
  'userCard.tabsAria': { he: 'לשוניות כרטיס המשתמש', en: 'User card tabs' },
  'userCard.tabDetails': { he: 'פרטים אישיים', en: 'Personal details' },
  'userCard.tabPermissions': { he: 'הרשאות', en: 'Permissions' },
  'userCard.tabCompanies': { he: 'חברות מקושרות', en: 'Linked companies' },
  'userCard.groupBasic': { he: 'פרטים בסיסיים', en: 'Basic details' },
  'userCard.groupContact': { he: 'יצירת קשר', en: 'Contact' },
  'userCard.groupAccount': { he: 'חשבון', en: 'Account' },
  'userCard.groupAccountLead': {
    he: 'מזהים בלבד. אין בכרטיס סיסמאות, אסימונים או קודי שחזור.',
    en: 'Identifiers only. No password, token or recovery code appears on this card.',
  },
  'userCard.membershipId': { he: 'מזהה חברוּת', en: 'Membership id' },

  // ---- what the roles add up to, section 5 ---------------------------------------------------
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'userCard.effective': { he: 'הרשאות בפועל', en: 'Effective permissions' },
  'userCard.effectiveNone': {
    he: 'ללא תפקיד — המשתמש אינו יכול לבצע דבר בארגון.',
    en: 'No role — this user can do nothing in the organisation.',
  },
  'userCard.capabilityCount': { he: 'הרשאות', en: 'permissions' },
  // THE FINDING, IN THE WORDS OF THE TABLE THE GATE READS. Not a design opinion: `approve` and
  // `write` carry identical permissions in `ACCESS_LEVEL_PERMITS`, so the screen says so.
  'userCard.approveSameAsWrite': {
    he: 'כיום „אישור" מתיר בדיוק את מה ש„עדכון" מתיר — אין במוצר פעולה שמוגדרת כדורשת אישור נפרד.',
    en: 'Today "approve" permits exactly what "update" permits — no action in the product is declared as requiring a separate approval.',
  },
  'action.read': { he: 'קריאה', en: 'Read' },
  'action.write': { he: 'כתיבה', en: 'Write' },
  'action.export': { he: 'ייצוא', en: 'Export' },
  'action.deliver': { he: 'שליחה', en: 'Deliver' },

  // ---- the capability domains, named once -----------------------------------------------------
  'domain.companies': { he: 'חברות ונתונים', en: 'Companies and data' },
  'domain.tenant': { he: 'ניהול הארגון', en: 'Organisation administration' },
  'domain.billing': { he: 'מנוי וחיוב', en: 'Subscription and billing' },
  'domain.security': { he: 'אבטחה', en: 'Security' },
  'domain.audit': { he: 'יומן ביקורת', en: 'Audit journal' },
  'domain.platform': { he: 'ניהול הפלטפורמה', en: 'Platform administration' },
  'domain.user': { he: 'חשבון אישי', en: 'Personal account' },
  'userCard.domainOther': { he: 'אחר', en: 'Other' },

  'userCard.title': { he: 'כרטיס משתמש', en: 'User card' },
  'userCard.details': { he: 'פרטים', en: 'Details' },
  'userCard.state': { he: 'מצב', en: 'State' },
  'userCard.seat': { he: 'תופס מושב', en: 'Uses a seat' },
  'userCard.roles': { he: 'תפקידים', en: 'Roles' },
  'userCard.rolesSaved': { he: 'התפקידים נשמרו', en: 'The roles were saved' },
  'userCard.noRoles': { he: 'ללא תפקיד', en: 'No role' },
  'userCard.noRoleRight': {
    he: 'שינוי תפקידים אינו זמין בהרשאות שלך.',
    en: 'Changing roles is not available to your permissions.',
  },
  'userCard.access': { he: 'חברות ורמות גישה', en: 'Companies and access levels' },
  'userCard.noCompanies': { he: 'ללא גישה לחברות', en: 'No company access' },
  'userCard.noAccessRight': {
    he: 'שינוי גישה אינו זמין בהרשאות שלך.',
    en: 'Changing access is not available to your permissions.',
  },
  'userCard.changeLevel': { he: 'שינוי רמה', en: 'Change level' },
  'userCard.addCompany': { he: 'הוספת חברה', en: 'Add a company' },
  'userCard.grant': { he: 'הענקת גישה', en: 'Grant access' },
  'userCard.actions': { he: 'פעולות', en: 'Actions' },
  'userCard.modules': { he: 'מודולים', en: 'Modules' },
  'userCard.readingModules': { he: 'את המודולים של הארגון', en: "the organisation's modules" },
  'userCard.noModules': { he: 'לא נרכשו מודולים', en: 'No modules were purchased' },
  'userCard.modulesNote': {
    he: 'מודול נפתח למשתמש לפי התפקיד שלו ולפי החברה שבה הוא נמצא.',
    en: 'A module opens for a user according to their role and the company they are in.',
  },
  'userCard.twoAxes': {
    he: 'רמת הגישה אינה מוסיפה יכולות שאינן בתפקיד.',
    en: 'An access level adds no capability the role does not carry.',
  },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'userCard.levelsHelp': { he: 'פירוט ההרשאות', en: 'Permission detail' },
  'userCard.levelRead': {
    he: 'צפייה — פתיחת החברה וקריאת הנתונים. בלי לשנות דבר.',
    en: 'View — open the company and read its data. Changes nothing.',
  },
  'userCard.levelWrite': {
    he: 'עדכון — הזנה ותיקון נתונים בחברה, בגבול התפקיד.',
    en: 'Update — enter and correct data in the company, within the role.',
  },
  'userCard.levelApprove': {
    he: 'אישור — אישור פעולות שדורשות אישור, בגבול התפקיד.',
    en: 'Approve — approve actions that require approval, within the role.',
  },
  'userCard.missingTitle': { he: 'המשתמש אינו ברשימה', en: 'The user is not in the list' },
  'userCard.missingBody': {
    he: 'המזהה שבכתובת אינו מופיע ברשימת המשתמשים של הארגון.',
    en: 'The identifier in the address is not in the organisation list of users.',
  },
  'access.revoke': { he: 'ביטול גישה', en: 'Revoke access' },
  'common.cancel': { he: 'ביטול', en: 'Cancel' },
  'onboard.grantAccess': { he: 'הענקת גישה לחברה', en: 'Grant access to the company' },
  'onboard.toCompanyList': { he: 'לרשימת החברות', en: 'To the company list' },
  'mfa.digitsPrefix': { he: 'קוד האימות מורכב מ-', en: 'The verification code is made of' },
  'mfa.digitsSuffix': { he: 'ספרות בלבד.', en: 'digits only.' },
  'invite.title': { he: 'הזמנת משתמש', en: 'Invite a user' },
  'act.done': { he: 'הוענק', en: 'Granted' },
  'act.refused': { he: 'נדחה — יש להשלים במסך היעוד', en: 'Refused — complete it on the destination screen' },
  'act.outstanding': { he: 'טרם הוענק — יש להשלים לאחר שהמשתמש יצטרף', en: 'Not granted yet — complete it after the user joins' },
  /*
   * SECTION 7, 2026-10-28. A choice that is RECORDED is not a choice that is outstanding, and the
   * two must not share a word: `act.outstanding` means *you chose it and nothing holds it*, which
   * is what the wizard used to report for every role and every company it was asked for. This one
   * means *the office's database is holding it and the redemption will apply it*.
   */
  'act.recorded': {
    he: 'ממתין להפעלה לאחר הצטרפות',
    en: 'Waiting to be applied when the user joins',
  },
  'act.notAsked': { he: 'לא נבחר', en: 'Not chosen' },
  /** The heading over what the SERVER says is waiting — read back, never echoed from the form. */
  'invite.pendingTitle': {
    he: 'נשמר וממתין להפעלה',
    en: 'Saved and waiting to be applied',
  },
  'invite.pendingNone': {
    he: 'השרת לא מדווח על בחירות שממתינות להפעלה',
    en: 'The server reports no choices waiting to be applied',
  },
  'invite.stepDetails': { he: 'פרטי המשתמש', en: 'User details' },
  'invite.stepRole': { he: 'תפקיד', en: 'Role' },
  'invite.stepAccess': { he: 'חברות ורמת גישה', en: 'Companies and access level' },
  'invite.stepSummary': { he: 'סיכום ואישור', en: 'Summary and confirmation' },
  /*
   * THE SETTINGS CATEGORIES, 2026-10-28. One name and ONE short line each, in the shape of the
   * reference the Owner sent. The line exists so a person can choose a card without opening it;
   * anything longer than that is the הסברים מיותרים he has now asked about three times.
   */
  /*
   * THE TOPIC TABS INSIDE A SETTINGS CATEGORY, 2026-10-28. Named for the QUESTION each screen
   * answers: תפקידים והרשאות beside הגדרת תפקידים was two jobs sharing a word, and the
   * Owner asked what the difference was -- which is the answer.
   */
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'settings.tab.roleDefinition': { he: 'הגדרת תפקידים', en: 'Role definitions' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'settings.tab.roleHolders': { he: 'שיוך תפקידים', en: 'Role assignment' },
  'settings.tab.companyRegister': { he: 'רשימת החברות', en: 'The register of companies' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'settings.tab.companyAccess': { he: 'הרשאות לחברות', en: 'Company permissions' },
  'settings.cat.organisation': { he: 'הארגון שלי', en: 'My organisation' },
  'settings.cat.organisationLead': { he: 'שם הארגון ופרטיו הרשומיים.', en: 'The organisation name and its registered details.' },
  'settings.cat.users': { he: 'משתמשים', en: 'Users' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'settings.cat.usersLead': { he: 'משתמשים, הזמנות והשבתות', en: 'Users, invitations and suspensions' },
  'settings.cat.roles': { he: 'תפקידים והרשאות', en: 'Roles and permissions' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'settings.cat.rolesLead': { he: 'הגדרת תפקידים ושיוכם', en: 'Role definition and assignment' },
  'settings.cat.companies': { he: 'חברות', en: 'Companies' },
  'settings.cat.companiesLead': { he: 'החברות שהארגון מנהל והגישה אליהן.', en: 'The companies this organisation manages, and access to them.' },
  'settings.cat.connections': { he: 'חיבורים למערכות הנהלת חשבונות', en: 'Connections to accounting systems' },
  'settings.cat.connectionsLead': { he: 'חיבור, צימוד ומצב הסנכרון.', en: 'Connection, pairing and sync state.' },
  'settings.cat.subscription': { he: 'מנוי וחשבוניות', en: 'Subscription and invoices' },
  'settings.cat.subscriptionLead': { he: 'התכנית, המודולים והמכסות.', en: 'The plan, the modules and the quotas.' },
  'settings.cat.security': { he: 'אבטחה וביקורת', en: 'Security and audit' },
  'settings.cat.securityLead': { he: 'יומן הפעולות והרשאות התמיכה.', en: 'The activity journal and support access.' },
  'users.title': { he: 'משתמשי הארגון', en: 'Users of the organisation' },
  /*
   * ── PENDING INVITATIONS, 2026-10-28 ───────────────────────────────────────────────────────────
   *
   * The Owner invited somebody, was told on a second attempt that an invitation already existed,
   * and saw only the active administrator in the list — because an invited person has no membership
   * and the list is a list of memberships. These name the section that fixes it.
   *
   * THE THREE STATES ARE NAMED SEPARATELY, deliberately: *"לטפל בהזמנה שפגה או בוטלה בצורה
   * מפורשת."* An address that cannot be re-invited and a row that silently vanished look the same
   * from outside and have different remedies.
   */
  /*
   * WHAT BECAME OF THE CHOICES MADE FOR THIS PERSON. 2026-10-28, the Owner's correction to the
   * authority fix: a refusal recorded in the database and in the audit trail was on no screen.
   * The per-outcome sentences are NOT here -- they live in `api/grantOutcomeText.ts`, beside
   * `denialText` and `auditActionText`, because each one is bound to a code the server emits and
   * that module is what a test can hold against the writer's own vocabulary.
   */
  'userCard.roleGrantsNothing': { he: 'התפקיד אינו מקנה הרשאות.', en: 'This role grants no permissions.' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'userCard.outcomesTitle': { he: 'הענקות מההזמנה', en: 'Grants from the invitation' },
  'invitations.title': { he: 'הזמנות ממתינות', en: 'Pending invitations' },
  'invitations.caption': { he: 'הזמנות', en: 'Invitations' },
  'invitations.reading': { he: 'את ההזמנות הממתינות', en: 'the pending invitations' },
  'invitations.none': {
    he: 'אין הזמנות ממתינות. כל מי שהוזמן הצטרף, או שלא נשלחה הזמנה.',
    en: 'No invitations are pending. Everyone invited has joined, or none was sent.',
  },
  'invitations.recipient': { he: 'נמען', en: 'Recipient' },
  'invitations.sent': { he: 'נשלחה', en: 'Sent' },
  'invitations.expires': { he: 'בתוקף עד', en: 'Valid until' },
  'invitations.waiting': { he: 'ממתין להפעלה', en: 'Waiting to be applied' },
  'invitations.waitingNone': { he: 'לא נבחרו תפקיד או גישה', en: 'No role or access was chosen' },
  'invitations.statusIssued': { he: 'ממתינה', en: 'Pending' },
  'invitations.statusRevoked': { he: 'בוטלה', en: 'Cancelled' },
  'invitations.statusExpired': { he: 'פג תוקפה', en: 'Expired' },
  // The row already says בוטלה or פג תוקפה; this says what to do about it, and nothing else.
  'invitations.resendOnlyIssued': { he: 'יש להזמין את הכתובת מחדש.', en: 'Invite the address again.' },
  'invite.noRoleChosen': { he: 'ללא תפקיד — המשתמש לא יוכל לעשות דבר עד שיוענק לו', en: 'No role — the user will be able to do nothing until one is granted' },
  'invite.noAccessChosen': { he: 'ללא גישה לחברות — לא יראה אף חברה עד שתוענק', en: 'No company access — they will see no company until it is granted' },
  'common.working': { he: 'מבצע…', en: 'Working…' },
  'invite.confirm': { he: 'אישור והזמנה', en: 'Confirm and invite' },
  'common.continue': { he: 'המשך', en: 'Continue' },
  'roles.reading': { he: 'את התפקידים', en: 'the roles' },
  'invite.name': { he: 'שם המשתמש', en: 'User name' },
  'invite.email': { he: 'דואר אלקטרוני', en: 'E-mail address' },
  /*
   * THE BUTTON NAMES THE GOAL; THE SCREEN NAMES THE MECHANISM. **Owner, 2026-10-27, twice:**
   * *"להציג כפתור ראשי בולט „הוספת משתמש”"* and *"בבקשה תדאג לכפתור הוספת משתמש ברורה"*.
   * What an administrator wants is to ADD somebody; what the product does is INVITE them,
   * because the person sets their own password. Both words are true and each is in the right
   * place — the control says what you are about to do, the screen says how it is done.
   */
  'invite.addUser': { he: 'הוספת משתמש', en: 'Add a user' },
  'invite.aboutToDo': { he: 'סיכום', en: 'Summary' },
  'system.hashavshevet': { he: 'חשבשבת', en: 'Hashavshevet' },
  'system.rivhit': { he: 'ריווחית', en: 'Rivhit' },
  'source.noSystemYet': { he: 'טרם נבחרה מערכת', en: 'No system chosen yet' },
  'source.createSummary': { he: 'שם ומערכת · פרטי ההתחברות במסך החיבור', en: 'Name and system · the connection details are on the connection screen' },
  'common.saving': { he: 'שומר…', en: 'Saving…' },
  'source.create': { he: 'יצירת החיבור', en: 'Create the connection' },
  'source.createdBody': { he: 'החיבור נוצר וטרם הוגדר. יש לפתוח אותו ולהשלים את פרטי ההתחברות.', en: 'The connection was created and is not configured yet. Open it and complete the connection details.' },
  'source.provenance': { he: '״נתונים נכון ל־״ הוא התאריך העסקי האחרון שקיים בנתונים — לא מועד הפנייה האחרונה למקור. פנייה שהחזירה כלום מקדמת את מועד הפנייה בלבד.', en: 'Data as of is the latest business date present in the data — not when the source was last contacted. A collection that returned nothing advances only the contact time.' },
  'source.createdTitle': { he: 'החיבור נוצר', en: 'The connection was created' },
  'source.reading': { he: 'את החיבורים שהוגדרו', en: 'the connections that were defined' },
  'source.emptyTitle': { he: 'לא הוגדרו מקורות נתונים', en: 'No data sources have been defined' },
  'source.emptyBody': { he: 'הארגון עדיין לא הגדיר אף חיבור למערכת חיצונית.', en: 'This organisation has not defined a connection to any external system yet.' },
  'source.devOnly': { he: 'מקור פיתוח — אינו מערכת אמת', en: 'A development source — not a real system' },
  'source.whatItDoesTitle': { he: 'מה הגדרת חיבור עושה בשלב הזה', en: 'What defining a connection does at this stage' },
  'source.newTitle': { he: 'הגדרת חיבור חדש', en: 'Define a new connection' },
  'source.name': { he: 'שם החיבור', en: 'Connection name' },
  'source.system': { he: 'המערכת', en: 'The system' },
  'source.emptyNext': { he: 'הטופס שלמעלה יוצר חיבור חדש.', en: 'The form above creates a new connection.' },
  'source.freshness': { he: 'טריות הנתונים', en: 'Data freshness' },
  'source.completeness': { he: 'שלמות הנתונים', en: 'Data completeness' },
  'source.linkedCompanies': { he: 'חברות מקושרות', en: 'Linked companies' },
  'roles.title': { he: 'תפקידים והרשאות', en: 'Roles and permissions' },
  'roles.removeAll': { he: 'הסרת כל התפקידים מהמשתמש', en: 'Remove every role from this user' },
  'roles.grantOne': { he: 'הענקת תפקיד אחד', en: 'Grant one role' },
  'roles.noneInvited': { he: 'ללא תפקיד — המשתמש הוזמן ללא הרשאות', en: 'No role — the user was invited with no permissions' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'roles.whatTheyMayDo': { he: 'תפקידים', en: 'Roles' },
  'roles.noneCanDoNothing': { he: 'ללא תפקיד — אינו יכול לעשות דבר', en: 'No role — they can do nothing' },
  'roles.inWhichCompanies': { he: 'באילו חברות (הרשאות לחברות)', en: 'In which companies (company permissions)' },
  'roles.noCompanies': { he: 'ללא גישה לאף חברה — לא יראה אף חברה', en: 'No company access — they will see no company' },
  'roles.provenance': { he: 'רשימת ההרשאות נגזרת מקטלוג ההרשאות של המערכת. הקטלוג הוא ההצהרה, והמסך מציג אותו כלשונו.', en: 'The permission list comes from the capability catalogue of the system. The catalogue is the declaration, and this screen quotes it.' },
  'roles.readingBoth': { he: 'את התפקידים ואת המשתמשים', en: 'the roles and the users' },
  'roles.emptyTitle': { he: 'אין תפקידים להצגה', en: 'No roles to show' },
  'roles.emptyBody': { he: 'ערכת התפקידים של הארגון נוצרת בעת ההקמה שלו.', en: 'The role set of an organisation is created when the organisation is created.' },
  'roles.grantedTitle': { he: 'התפקידים הוענקו', en: 'The roles were assigned' },
  'roles.noAccessTitle': { he: 'תפקיד אינו מקנה גישה לחברה', en: 'A role grants no company access' },
  'roles.whatCanBeGranted': { he: 'מה אפשר להעניק', en: 'What you may grant' },
  'users.emptyShort': { he: 'אין עדיין משתמשים.', en: 'No users yet.' },
  'roles.toGrant': { he: 'התפקידים שיוענקו', en: 'Roles to assign' },
  'roles.personalRole': { he: 'תפקיד שהוגדר עבור משתמש אחד.', en: 'A role defined for one person.' },
  'roles.refusalPreview': { he: 'כך ייראה סירוב הענקה', en: 'This is what a refused assignment looks like' },
  'users.name': { he: 'שם', en: 'Name' },
  'users.status': { he: 'מצב', en: 'Status' },
  'users.roles': { he: 'תפקידים', en: 'Roles' },
  'users.consumesSeat': { he: 'תופס מושב', en: 'Uses a seat' },
  'common.yes': { he: 'כן', en: 'Yes' },
  'common.no': { he: 'לא', en: 'No' },
  // The accessible name of every ⓘ mark: *הסבר על …* plus what it is about, so four marks on one
  // page are four distinguishable controls rather than four identical ones.
  'common.explanationAbout': { he: 'הסבר על ', en: 'About ' },
  'common.actions': { he: 'פעולות', en: 'Actions' },
  'nav.orgAdmin': { he: 'ניהול הארגון', en: 'Organisation administration' },
  'users.disable': { he: 'השבתה', en: 'Disable' },
  'users.reactivate': { he: 'הפעלה מחדש', en: 'Reactivate' },
  'users.resend': { he: 'שליחה חוזרת', en: 'Resend' },
  'users.revokeSessions': { he: 'ניתוק ההתחברויות', en: 'End the sessions' },
  'users.provenance': { he: 'השבתת משתמש היא שינוי מצב ולא מחיקה. ההרשאות וההיסטוריה שלו נשארים ניתנים לאיתור, והפעלה מחדש מחזירה את המצב בלבד.', en: 'Disabling a user is a change of state and not a deletion. Their permissions and history stay traceable, and reactivating restores the state and nothing else.' },
  'users.reading': { he: 'את משתמשי הארגון', en: 'the users of this organisation' },
  'users.caption': { he: 'משתמשים', en: 'Users' },
  'users.empty': { he: 'אין עדיין משתמשים בארגון.', en: 'This organisation has no users yet.' },
  'users.disabledTitle': { he: 'המשתמש הושבת', en: 'The user was disabled' },
  'users.reactivatedTitle': { he: 'המשתמש הופעל מחדש', en: 'The user was reactivated' },
  'users.sessionsRevokedTitle': { he: 'ההתחברויות נותקו', en: 'The sessions were ended' },
  'users.disableTitle': { he: 'השבתת משתמש', en: 'Disable a user' },
  'users.reactivateTitle': { he: 'הפעלת משתמש מחדש', en: 'Reactivate a user' },
  'users.resendTitle': { he: 'שליחה חוזרת של ההזמנה', en: 'Resend the invitation' },
  'users.revokeTitle': { he: 'ניתוק ההתחברויות של המשתמש', en: 'End this user sessions' },
  'users.readingSessions': { he: 'את ההתחברויות', en: 'the sessions' },
  'users.reactivateHint': { he: 'אפשר להפעיל את המשתמש מחדש מאותה שורה. ההפעלה מחזירה את המצב בלבד.', en: 'You can reactivate the user from the same row. Reactivating restores the state and nothing else.' },
  'users.sessionsNotRestored': { he: 'ההתחברויות שנותקו בעת ההשבתה אינן חוזרות. תידרש התחברות מחדש.', en: 'Sessions ended when the user was disabled do not come back. Signing in again will be required.' },
  'users.revokeReason': { he: 'סיבת הניתוק', en: 'Reason for ending the sessions' },
  'users.disableReason': { he: 'סיבת ההשבתה', en: 'Reason for disabling' },
  'users.reasonRecorded': { he: 'הסיבה נרשמת ביומן הביקורת יחד עם מי שביצע את הפעולה.', en: 'The reason is recorded in the audit journal together with who performed the action.' },
  'users.reactivateReason': { he: 'סיבת ההפעלה מחדש', en: 'Reason for reactivating' },
  'invite.selfPassword': { he: 'המשתמש יקבע סיסמה בעצמו, ועד להצטרפותו הוא מופיע ברשימה כמוזמן.', en: 'The user sets their own password, and until they join they are listed as invited.' },
  'invite.actInvited': { he: 'הזמנה — נוצרה', en: 'Invitation created' },
  'roles.grantAction': { he: 'הענקת תפקיד', en: 'Assign a role' },
  /*
   * ── SECTION 4, 2026-10-27. WHAT THE WIZARD HAD NO WORDS FOR. ─────────────────────────────────
   *
   * Owner: *"לא להפתיע בסיכום עם „ללא תפקיד” בלי שהמשתמש הבין ובחר בכך במפורש"*, *"להבהיר שדות
   * חובה בשלב שבו ממלאים אותם"*, *"להבחין בין שלב שלא מולא לבין שלב שאינו זמין בגלל הרשאות"*.
   *
   * Each of those is a sentence the screen could not say. `invite.noRoleChosen` already existed
   * and described the CONSEQUENCE -- it is kept, for the summary, and what is added here is the
   * CHOICE that has to be made before the summary can print it.
   */
  'invite.required': { he: 'שדה חובה', en: 'Required' },
  'invite.detailsNeeded': {
    he: 'יש למלא שם וכתובת דואר אלקטרוני כדי להמשיך.',
    en: 'A name and an e-mail address are needed in order to continue.',
  },
  'invite.roleDecision': {
    he: 'בחר תפקיד אחד לפחות, או סמן במפורש שההזמנה נשלחת ללא תפקיד.',
    en: 'Choose at least one role, or tick explicitly that the invitation goes without one.',
  },
  'invite.noRoleOptIn': { he: 'להזמין כעת ללא תפקיד', en: 'Invite without a role for now' },
  'invite.accessDecision': {
    he: 'בחר חברה אחת לפחות, או סמן במפורש שההזמנה נשלחת ללא גישה לחברות.',
    en: 'Choose at least one company, or tick explicitly that the invitation goes without access.',
  },
  'invite.noAccessOptIn': {
    he: 'להזמין כעת ללא גישה לחברות',
    en: 'Invite without company access for now',
  },
  /* In the SUMMARY: the same fact, said as the decision it now is rather than as a discovery. */
  'invite.noRoleConfirmed': {
    he: 'ללא תפקיד — נבחר במפורש. המשתמש לא יוכל לעשות דבר עד שיוענק לו תפקיד.',
    en: 'No role — chosen explicitly. The user will be able to do nothing until one is granted.',
  },
  'invite.noAccessConfirmed': {
    he: 'ללא גישה לחברות — נבחר במפורש. המשתמש לא יראה אף חברה עד שתוענק לו גישה.',
    en: 'No company access — chosen explicitly. The user will see no company until access is granted.',
  },
  /* The step states, as WORDS. A step marked by colour alone is a step a reader cannot rely on. */
  'invite.stepStateDone': { he: 'הושלם', en: 'Done' },
  'invite.stepStateCurrent': { he: 'השלב הנוכחי', en: 'Current step' },
  'invite.stepStateAhead': { he: 'טרם מולא', en: 'Not filled yet' },
  /* NOT the same as "not filled", and the whole point of the distinction the Owner asked for. */
  'invite.stepStateUnavailable': {
    he: 'אינו זמין בהרשאות שלך',
    en: 'Not available to your permissions',
  },
  'invite.stepsProgress': { he: 'שלבי ההזמנה', en: 'Invitation steps' },
  'invite.summaryTitle': { he: 'מה יישלח', en: 'What will be sent' },
  'invite.roleHint': { he: 'תפקיד אינו מקנה גישה לחברה.', en: 'A role grants no company access.' },
  'invite.stepsUnavailable': { he: 'חלק מהשלבים אינם זמינים להרשאות שלך. מנהל הארגון יכול להשלים אותם.', en: 'Some steps are not available to your permissions. An organisation administrator can complete them.' },
  'invite.threeActsNote': { he: 'שלוש הפעולות נפרדות בשרת ונבדקות כל אחת בנפרד, ולכן התוצאה תציג כל אחת מהן בנפרד.', en: 'The three acts are separate on the server and each is checked on its own, so the result reports each of them separately.' },
  'common.back': { he: 'חזרה', en: 'Back' },
  'source.whatItDoesBody': { he: 'ההגדרה נשמרת ונרשמת ביומן הביקורת. משיכת נתונים בפועל דורשת תוכנת חיבור למערכת הספציפית, וזו אינה כלולה בגרסה הזאת.', en: 'The definition is stored and recorded in the audit journal. Actually pulling data needs connector software for that specific system, which this build does not carry.' },
  'source.probeSaysSo': { he: 'בדיקת חיבור תאמר זאת במפורש ותנקוב בשם המערכת.', en: 'A connectivity check says so explicitly and names the system.' },
  'source.nameHint': { he: 'שם לחיבור. לא כתובת שרת.', en: 'A name for the connection. Not a server address.' },
  'source.systemHint': { he: 'נקבעת ביצירת החיבור.', en: 'Fixed when the connection is created.' },
  'source.createDoesNotConfigure': { he: 'יצירת חיבור אינה מגדירה אותו. פרטי ההתחברות נמסרים במסך החיבור עצמו. עד אז החיבור מופיע כטרם הוגדר, וזה מצב תקין.', en: 'Creating a connection does not configure it. The connection details are supplied on the connection screen itself. Until then it shows as not configured, which is a correct state.' },
  'source.summaryConnections': { he: 'חיבורים', en: 'Connections' },
  'source.summaryUnconfigured': { he: 'טרם הוגדרו', en: 'Not configured yet' },
  'source.summaryFailing': { he: 'בתקלה', en: 'Failing' },
  'source.figuresQualified': { he: 'סכומים המחושבים מהנתונים האלה יוצגו עם הסתייגות.', en: 'Figures computed from this data are shown with a qualification.' },
  'source.openConnection': { he: 'פתיחת החיבור — הגדרה, בדיקה וקישור חברות', en: 'Open the connection to configure, check and link companies' },
  'system.priority': { he: 'Priority', en: 'Priority' },
  'system.finsite': { he: 'FINSITE', en: 'FINSITE' },
  'system.sap': { he: 'SAP', en: 'SAP' },
  'invite.alreadyTitle': { he: 'לכתובת הזו כבר נשלחה הזמנה', en: 'An invitation was already sent to that address' },
  'invite.alreadyBody': { he: 'לא נוצרה הזמנה שנייה, ואין כפילות. ההזמנה הקיימת עדיין תקפה והמשתמש מופיע ברשימה כמוזמן.', en: 'No second invitation was created and there is no duplicate. The existing invitation is still valid and the user is listed as invited.' },
  'invite.alreadyAction': { he: 'לרשימת המשתמשים — שליחה חוזרת', en: 'To the users list, to resend it' },
  'settings.office.displayStale': { he: 'השם נשמר ונקרא בחזרה מהשרת. רענון התצוגה נכשל, ולכן ייתכן שהכותרת עדיין מציגה את השם הקודם. רענון הדף יסדיר זאת. אין לשמור שוב.', en: 'The name was saved and read back from the server. Refreshing the display failed, so the header may still show the previous name. Reloading the page fixes it. Do not save again.' },
  'roles.roleVsAccess': { he: 'תפקיד קובע מה מותר למשתמש לעשות. הוא אינו קובע באילו חברות מותר לו לעשות זאת. גישה לחברה היא הענקה מפורשת, לכל חברה בנפרד — גם לבעלים של הארגון.', en: 'A role decides what a user may do. It does not decide in which companies they may do it. Access to a company is an explicit grant, made per company, including for the owner of the organisation.' },
  'roles.exclusionNote': { he: 'כך אפשר להחריג חברה ממשתמש מסוים, כולל החרגה בשל ניגוד עניינים.', en: 'That is how one company is kept away from one person, including for a conflict of interest.' },
  'roles.bothNeeded': { he: 'משתמש עם תפקיד וללא גישה לחברות אינו יכול לעשות דבר, ומשתמש עם גישה לחברות וללא תפקיד אינו יכול לעשות דבר. שתי ההענקות נדרשות, והן נעשות בשני מסכים נפרדים.', en: 'A user with a role and no company access can do nothing, and a user with company access and no role can do nothing. Both grants are required, and they are made on two separate screens.' },
  'roles.cannotEscalate': { he: 'אפשר להעניק למשתמש אחר רק הרשאות שקיימות בתפקידים שלך. הרשאות שקובעות מי מקבל הרשאות שמורות לבעלים של הארגון, כדי שהגבול שנקבע לתפקיד לא יוסר בהענקה אחת.', en: 'You may grant another user only permissions your own roles already hold. The permissions that decide who receives permissions are reserved to the owner of the organisation, so the boundary set for a role cannot be removed with one grant.' },
  'roles.allOrNothing': { he: 'הבדיקה נעשית בשרת בעת ההענקה. אם היא נכשלת, לא נשמר שינוי חלקי — אף תפקיד מהבקשה לא מוענק.', en: 'The check runs on the server at the moment of granting. If it fails, no partial change is saved: not one role from the request is granted.' },
  'roles.currentRoles': { he: 'תפקידים כיום', en: 'Roles today' },
  'roles.twoPermissions': { he: 'שתי ההרשאות', en: 'Both permissions' },
  'roles.companiesGranted': { he: 'חברות שהוענקו', en: 'Companies granted' },
  'roles.firstColumnOnly': { he: 'הענקת תפקיד במסך הזה משנה את הטור הראשון בלבד. הטור השני נקבע במסך', en: 'Assigning a role on this screen changes the first column only. The second is set on' },
  'roles.grantsCount': { he: 'מקנה הרשאות', en: 'Grants permissions' },
  'roles.noCompanyAccess': { he: 'אינו מקנה גישה לאף חברה', en: 'grants access to no company' },
  'roles.unnamedSuffix': { he: 'הרשאות שאין להן עדיין שם במסך הזה', en: 'permissions this screen has no name for yet' },
  'roles.replacesExactly': { he: 'הסימון מחליף את רשימת התפקידים.', en: 'The ticks replace the list of roles.' },
  'roles.grantAudited': { he: 'הענקת תפקיד אינה מעניקה גישה לאף חברה, והיא נרשמת ביומן הביקורת יחד עם מי שביצע אותה.', en: 'Assigning a role grants access to no company, and it is recorded in the audit journal together with who did it.' },
  'roles.assignedNote': { he: 'תפקידים נרשמו למשתמש. לא הוענקה בכך גישה לאף חברה.', en: 'roles were recorded for the user. No company access was granted by it.' },
  'roles.fixtureNote': { he: 'הדוגמאות שלהלן מוצגות מנתוני פיתוח, כדי שאפשר יהיה לקרוא את הניסוח לפני שיש שרת שמחזיר אותו. הן אינן מתארות את ההרשאות של הארגון הזה.', en: 'The examples below come from development data, so the wording can be read before a server returns it. They do not describe the permissions of this organisation.' },
  'users.sessions': { he: 'התחברויות', en: 'Sessions' },
  'users.disabledNote': { he: 'לא יוכל להתחבר. ההרשאות וההיסטוריה שלו נשמרו, והמושב שלו התפנה.', en: 'can no longer sign in. Their permissions and history are kept, and their seat was released.' },
  'users.reactivatedNote': { he: 'חזר למצב פעיל עם התפקידים שהיו לו קודם. ההפעלה תופסת מושב במנוי.', en: 'is active again with the roles they had before. Reactivating uses a seat in the subscription.' },
  'users.sessionsRevokedNote': { he: 'התחברויות של המשתמש נותקו. הרשומות נשמרות ביומן יחד עם הסיבה שנרשמה.', en: 'sessions of this user were ended. The records are kept in the journal with the reason given.' },
  'users.viewingSessionsNote': { he: 'צפייה בהתחברויות של עובד אחר נרשמת ביומן הביקורת יחד עם מי שצפה בה. הרשימה מציגה מתי נפתחה כל התחברות ומתי נראתה לאחרונה, ואינה מציגה מיקום, כתובת רשת או פרטי מכשיר.', en: 'Viewing another employee sessions is recorded in the audit journal together with who viewed it. The list shows when each session opened and when it was last seen, and shows no location, no network address and no device details.' },
  'common.close': { he: 'סגירה', en: 'Close' },
  'users.disableBlast': { he: 'המשתמש לא יוכל להתחבר, ההרשאות וההיסטוריה שלו יישמרו, והמושב שלו יתפנה.', en: 'will no longer be able to sign in. Their permissions and history are kept, and their seat is released.' },
  'users.reactivateBlast': { he: 'יחזור למצב פעיל עם התפקידים שהיו לו לפני ההשבתה, ויתפוס מושב במנוי. ההתחברויות שנותקו אינן חוזרות.', en: 'returns to active with the roles they had before being disabled, and uses a seat. Sessions that were ended do not come back.' },
  'users.reactivateRolesNote': { he: 'מחזיר מצב בלבד.', en: 'Restores the state only.' },
  'users.resendBlast': { he: 'תישלח הזמנה חדשה. הקישור הקודם יפסיק לפעול באותו רגע, ולכן רק הקישור החדש יעבוד.', en: 'will be sent a new invitation. The previous link stops working at that moment, so only the new one works.' },
  'users.resendNoPermissions': { he: 'אינה מקנה הרשאות.', en: 'Grants no permissions.' },
  'users.revokeBlast': { he: 'כל ההתחברויות שלו יסתיימו והוא יידרש להתחבר מחדש. ההרשאות שלו אינן משתנות, ודבר אינו נמחק.', en: 'will have every session ended and will need to sign in again. Their permissions are unchanged and nothing is deleted.' },
  'users.revokeReasonNote': { he: 'הסיבה תירשם ביומן.', en: 'The reason is recorded in the journal.' },
  'card.title': { he: 'כרטיס החברה', en: 'Company card' },
  'card.saveDetails': { he: 'שמירת הפרטים', en: 'Save the details' },
  'card.legalName': { he: 'השם המלא', en: 'Full legal name' },
  'card.registration': { he: 'ח.פ / מספר רישום', en: 'Registration number' },
  'card.noRegistration': { he: 'ללא מספר רישום — זהות החברה אינה שלמה', en: 'No registration number — the identity of this company is incomplete' },
  'card.sourceLink': { he: 'קישור למערכת מקור', en: 'Link to a source system' },
  'card.noSourceLink': { he: 'אין קישור למערכת מקור — נתונים חיצוניים אינם נקלטים לחברה הזו', en: 'No link to a source system — no external data is collected for this company' },
  'card.tabDetails': { he: 'פרטי החברה', en: 'Company details' },
  'card.tabAccounting': { he: 'מערכת הנהלת חשבונות', en: 'Accounting system' },
  'card.tabConnection': { he: 'חיבור', en: 'Connection' },
  'card.tabAccess': { he: 'הרשאות והפעלה', en: 'Permissions and activation' },
  'card.tabStatus': { he: 'מצב ותיק עבודה', en: 'State and portfolio' },
  'card.unavailableTitle': { he: 'החברה הזו אינה זמינה', en: 'This company is not available' },
  'card.unavailableBody': { he: 'לא הצלחנו להציג את החברה שאליה מפנה הקישור.', en: 'We could not show the company this link points at.' },
  'card.usersQuoted': { he: '״משתמשים״', en: 'Users' },
  'card.groupBasics': { he: 'פרטים בסיסיים', en: 'Basic details' },
  'card.groupBasicsLead': { he: 'השמות שבהם החברה מוצגת', en: 'The names this company is shown under' },
  'card.groupIdentity': { he: 'זהות רשמית', en: 'Official identity' },
  'card.groupIdentityLead': { he: 'סוג הישות והמספר הרשמי', en: 'The entity kind and the official number' },
  'card.groupPortfolio': { he: 'שיוך תיק עבודה', en: 'Portfolio assignment' },
  'card.groupPortfolioLead': { he: 'ארגון פנימי של הארגון — אינו מעניק גישה', en: 'Internal organisation only — it grants no access' },
  'card.stateProvenance': { he: 'מצב החברה מוצג כפי שהשרת קבע אותו. הוא אינו נגזר מהיעדר סימון ואינו מנוחש מנתונים אחרים.', en: 'The state is shown as the server set it. It is never inferred from a missing mark and never guessed from other data.' },
  'card.registrationShort': { he: 'ח.פ / ע.מ', en: 'Registration no.' },
  'card.savedTitle': { he: 'פרטי החברה נשמרו', en: 'The company details were saved' },
  'card.savedBody': { he: 'השינוי נרשם ביומן הביקורת. מצב החברה לא השתנה — הוא נקבע בפעולה נפרדת.', en: 'The change was recorded in the audit journal. The state of the company is unchanged — it is set by a separate action.' },
  'card.stateChangedTitle': { he: 'מצב החברה שונה', en: 'The state of the company changed' },
  'card.portfolioAssignedTitle': { he: 'תיק העבודה שויך', en: 'The portfolio was assigned' },
  'card.portfolioBody': { he: 'שיוך תיק עבודה מארגן את החברה בתוך הארגון. הוא אינו מעניק לאיש גישה אליה.', en: 'Assigning a portfolio organises the company inside the organisation. It gives nobody access to it.' },
  'card.tabsAria': { he: 'חלקי כרטיס החברה', en: 'Sections of the company card' },
  'card.changeState': { he: 'שינוי מצב החברה', en: 'Change the state of the company' },
  'card.transition': { he: 'המעבר המבוקש', en: 'The transition requested' },
  'card.mergedInto': { he: 'החברה שאליה מוזגה', en: 'The company it was merged into' },
  'card.portfolio': { he: 'תיק עבודה', en: 'Portfolio' },
  'card.partnerId': { he: 'מזהה השותף האחראי', en: 'Responsible partner identifier' },
  'card.assignmentKind': { he: 'סוג השיוך', en: 'Assignment kind' },
  'card.identityNeedsAttention': { he: 'זהות החברה דורשת טיפול', en: 'The identity of this company needs attention' },
  'authoring.ticksAreTheRole': { he: 'מה שמסומן כאן הוא מה שהתפקיד יקנה.', en: 'What is ticked here is what the role grants.' },
  'authoring.replacesLabel': { he: 'שינוי תפקיד קיים', en: 'Editing an existing role' },
  'authoring.replacesExactly': { he: 'רשימת ההרשאות של התפקיד תוחלף בדיוק במה שמסומן כאן. הרשאה שסימונה יוסר — תוסר מכל מי שמחזיק בתפקיד.', en: 'The permission list of this role is replaced by exactly what is ticked here. A permission whose tick is removed is removed from everyone holding the role.' },
  'authoring.create': { he: 'הגדרת התפקיד', en: 'Define the role' },
  'authoring.save': { he: 'שמירת השינוי בתפקיד', en: 'Save the change to the role' },
  'authoring.createdTitle': { he: 'התפקיד הוגדר', en: 'The role was defined' },
  'authoring.updatedTitle': { he: 'התפקיד עודכן', en: 'The role was updated' },
  'authoring.emptyRoleBody': { he: 'התפקיד נשמר ואינו מקנה הרשאות. זו הגדרה תקינה, ואפשר להשלים אותה מאוחר יותר.', en: 'The role was saved and grants no permissions. That is a valid definition, and it can be completed later.' },
  'authoring.provenance': { he: 'רשימת ההרשאות היא קטלוג ההרשאות של המערכת כלשונו. המסך אינו ממציא שמות ואינו מציג הרשאה שאין לה שם בעברית.', en: 'The permission list quotes the capability catalogue of the system. This screen invents no names and shows no permission it has no name for.' },
  'authoring.whatIsARole': { he: 'תפקיד הוא רשימת ההרשאות שמתחתיו', en: 'A role is the list of permissions beneath it' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'authoring.whatYouDefine': { he: 'הרשאות התפקיד', en: 'Role permissions' },
  'authoring.addsToList': { he: 'מוסיף תפקיד לרשימת התפקידים של הארגון.', en: 'Adds a role to the list of roles of this organisation.' },
  'authoring.roleCode': { he: 'מזהה התפקיד', en: 'Role identifier' },
  'authoring.roleName': { he: 'שם התפקיד בעברית', en: 'Name of the role' },
  'authoring.roleNameHint': { he: 'השם שמופיע לצד כל משתמש שמחזיק בתפקיד.', en: 'The name shown beside every user holding this role.' },
  'authoring.capabilities': { he: 'ההרשאות שהתפקיד מקנה', en: 'The permissions this role grants' },
  'conn.title': { he: 'חיבור למקור נתונים', en: 'Data-source connection' },
  'conn.externalCompany': { he: 'החברה במערכת המקור', en: 'Company in the source system' },
  'conn.linkedToCompany': { he: 'מקושרת לחברה בארגון', en: 'Linked to a company in this organisation' },
  'conn.linkedToHidden': { he: 'מקושרת לחברה שאינך רשאי לראות', en: 'Linked to a company you may not see' },
  'conn.saveConfig': { he: 'שמירת ההגדרה', en: 'Save the configuration' },
  'conn.reading': { he: 'קורא את החיבור ואת מצבו.', en: 'the connection and its state.' },
  'conn.unavailableTitle': { he: 'החיבור הזה אינו זמין', en: 'This connection is not available' },
  'conn.unavailableBody': { he: 'לא הצלחנו להציג את החיבור שאליו מפנה הקישור.', en: 'We could not show the connection this link points at.' },
  'conn.referenceOnly': { he: 'שם בלבד. סיסמאות אינן נשמרות.', en: 'A name only. Passwords are not stored.' },
  'conn.savedTitle': { he: 'ההגדרה נשמרה', en: 'The configuration was saved' },
  'conn.savedBody': { he: 'פרטי החיבור נשמרו. הרצת בדיקת חיבור תראה אם המערכת שבצד השני עונה.', en: 'The connection details were saved. Running a connectivity check will show whether the system on the other side answers.' },
  'conn.mappedTitle': { he: 'הקישור נרשם', en: 'The link was recorded' },
  'conn.mappedBody': { he: 'החברה שבמערכת המקור מקושרת כעת לחברה בארגון, ונתוניה ייקלטו מכאן ואילך.', en: 'The company in the source system is now linked to a company here, and its data will be collected from now on.' },
  'conn.externalCaption': { he: 'חברות במערכת המקור', en: 'Companies in the source system' },
  'conn.externalEmpty': { he: 'החיבור לא דיווח על אף חברה.', en: 'The connection reported no companies.' },
  'conn.notLinked': { he: 'אינה מקושרת — נתוניה אינם נקלטים', en: 'Not linked — its data is not collected' },
  'conn.configTitle': { he: 'הגדרת החיבור', en: 'Connection configuration' },
  'conn.savingIsNotProof': { he: 'שמירת הגדרה אינה מוכיחה שהחיבור עובד — לכך יש להריץ בדיקה.', en: 'Saving a configuration does not prove the connection works — a check is what shows that.' },
  'conn.mappedAudited': { he: 'הקישור נרשם ביומן הביקורת יחד עם מי שביצע אותו ועם רמת הוודאות שנבחרה.', en: 'The link is recorded in the audit journal together with who made it and the confidence chosen.' },
  'conn.companyHere': { he: 'החברה בארגון', en: 'The company in this organisation' },
  'conn.confidence': { he: 'רמת הוודאות של הקישור', en: 'Confidence of the link' },
  'common.note': { he: 'הערה', en: 'Note' },
  'conn.noteHint': { he: 'הבסיס לקישור', en: 'What the link rests on. Stored with it in the journal.' },
  'enrol.mandatorySystem': { he: 'אימות דו-שלבי הוא דרישת יסוד של המערכת עבור חשבון מסוג זה, ואינו ניתן לביטול.', en: 'Two-factor authentication is a baseline requirement of the system for this kind of account, and cannot be switched off.' },
  'enrol.mandatoryOrg': { he: 'אימות דו-שלבי נדרש לפי מדיניות הארגון.', en: 'Two-factor authentication is required by the policy of this organisation.' },
  'enrol.optional': { he: 'אימות דו-שלבי אינו חובה עבורך, ומומלץ בכל זאת.', en: 'Two-factor authentication is not required for you, and is recommended all the same.' },
  'enrol.begin': { he: 'התחלת רישום מול השרת', en: 'Begin setting it up with the server' },
  'enrol.complete': { he: 'סיום הרישום', en: 'Finish setting it up' },
  'enrol.failedBody': { he: 'לא הצלחנו להשלים את האימות. מטעמי אבטחה השרת אינו מפרט מה נכשל, ולכן איננו יודעים ', en: 'We could not complete the verification. For security reasons the server does not say what failed, so we do not know ' },
  'enrol.failedWhich': { he: 'אם הקוד היה שגוי או שחלון הרישום הסתיים.', en: 'whether the code was wrong or the setup window had closed.' },
  'enrol.failedNext': { he: 'בדוק שהזנת את הקוד העדכני מהיישומון — הוא מתחלף כל 30 שניות. אם חלון הרישום הסתיים, ', en: 'Check that you entered the current code from the app — it changes every 30 seconds. If the setup window closed, ' },
  'enrol.failedRestart': { he: 'התחל רישום מחדש וקבל מפתח חדש.', en: 'start again and get a new key.' },
  'enrol.provenance': { he: 'הסוד וקודי השחזור מופקים בשרת. המערכת שומרת את קודי השחזור בצורה חד-כיוונית בלבד ואינה יכולה להציג אותם שוב.', en: 'The secret and the recovery codes are produced on the server. The system stores recovery codes one-way only and cannot show them again.' },
  'enrol.qrCaption': { he: 'קוד לסריקה ביישומון האימות', en: 'Code to scan with your authenticator app' },
  'enrol.startedTitle': { he: 'הרישום החל וטרם הושלם', en: 'Setup has begun and is not finished' },
  'enrol.startedBody': { he: 'השרת הנפיק סוד לרישום. הרישום ייכנס לתוקף רק לאחר הזנת קוד מהיישומון — כך אנחנו יודעים שהסוד באמת הגיע למכשיר.', en: 'The server issued a setup secret. It takes effect only once a code from the app is entered — which is how we know the secret really reached the device.' },
  'enrol.doneTitle': { he: 'הרישום הושלם', en: 'Setup is complete' },
  'enrol.doneBody': { he: 'מעכשיו תידרש להזין קוד מהיישומון בכל התחברות.', en: 'From now on you will be asked for a code from the app at every sign-in.' },
  'enrol.noRecoveryTitle': { he: 'לא התקבלו קודי שחזור', en: 'No recovery codes were returned' },
  'enrol.noRecoveryBody': { he: 'הרישום הושלם, אך השרת לא החזיר קודי שחזור להצגה. בלי קוד שחזור, אובדן הטלפון מחייב פנייה לתמיכה.', en: 'Setup finished, but the server returned no recovery codes to show. Without one, losing the phone means contacting support.' },
  'enrol.step1': { he: 'התקנת יישומון אימות', en: 'Install an authenticator app' },
  'enrol.step2': { he: 'הוספת החשבון — סריקת קוד או מפתח ידני', en: 'Add the account — scan the code or enter the key' },
  'enrol.step3': { he: 'הזנת קוד לאישור ושמירת קודי השחזור', en: 'Enter a code to confirm, and save the recovery codes' },
  'enrol.sectionAdd': { he: '1 · הוספת החשבון ליישומון', en: '1 · Add the account to the app' },
  'enrol.notStarted': { he: 'הרישום עדיין לא התחיל', en: 'Setup has not begun yet' },
  'enrol.accountName': { he: 'שם החשבון ביישומון', en: 'Account name in the app' },
  'enrol.manualKey': { he: 'מפתח ידני', en: 'Manual key' },
  'enrol.codeKind': { he: 'סוג הקוד', en: 'Code type' },
  'enrol.sectionConfirm': { he: '2 · אישור', en: '2 · Confirm' },
  'enrol.codeFromApp': { he: 'הקוד המוצג ביישומון', en: 'The code shown in the app' },
  'enrol.addThenConfirm': { he: 'יש להוסיף את החשבון ליישומון ואז להזין את הקוד בשלב האישור שלמטה.', en: 'Add the account to the app, then enter the code in the confirmation step below.' },
  'enrol.askSupportForCodes': { he: 'כדאי לפנות לתמיכה ולבקש הנפקה מחדש של קודי שחזור.', en: 'Contact support and ask for recovery codes to be issued again.' },
  'card.backToList': { he: 'חזרה לרשימת החברות', en: 'Back to the company list' },
  'card.updateScope': { he: 'עדכון פרטים אינו משנה את מצב החברה ואינו משנה הרשאות. הקישורים למערכות המקור ממשיכים לעבוד — הם מפנים לחברה ולא לשם שלה.', en: 'Updating details changes neither the state of the company nor any permission. Links to source systems keep working, because they point at the company and not at its name.' },
  'card.displayNameHint': { he: 'השם שמופיע ברשימות ובסרגל. הוא יכול להיות שונה מהשם הרשמי.', en: 'The name shown in lists and in the bar. It may differ from the official name.' },
  'card.noNumberIsReal': { he: 'אפשר להשאיר ריק.', en: 'May be left empty.' },
  'card.accountingDeclaration': { he: 'בחירת מערכת הנהלת חשבונות היא הצהרה על מקום הספרים. חיבור בפועל נעשה במסך מקורות הנתונים, והמצב מוצג שם.', en: 'Choosing an accounting system is a statement about where the books are kept. The actual connection is made on the data-sources screen, and its state is shown there.' },
  'card.toSources': { he: 'מקורות נתונים וחיבורים', en: 'Data sources and connections' },
  'card.accessIsExplicit': { he: 'גישה לחברה היא הענקה מפורשת לכל משתמש בנפרד. מי שמקים חברה מקבל עליה הענקה אוטומטית ומתועדת באותה עסקה; לכל אחר נדרשת הענקה מפורשת.', en: 'Access to a company is an explicit grant, per user. Whoever creates a company receives a recorded grant to it in the same transaction; for anybody else an explicit grant is required.' },
  'card.openForWork': { he: 'פתיחת החברה לעבודה', en: 'Open the company for work' },
  'card.openGoesThroughGate': { he: 'פתיחת החברה לעבודה תעבור דרך השער ותידחה כל עוד לא הוענקה גישה. זו אינה תקלה אלא הסדר שמונע מהרשאה להיווצר מאליה.', en: 'Opening the company goes through the gate and is refused while no access has been granted. That is not a fault but the arrangement that stops a permission creating itself.' },
  'card.stateByTransition': { he: 'מצב החברה נקבע במעבר מפורש ולא בעריכת שדה. מעבר שאינו אפשרי מהמצב הנוכחי נדחה בשרת, והתשובה כוללת את המעברים שכן אפשריים.', en: 'The state is set by an explicit transition and never by editing a field. A transition that is not possible from the current state is refused by the server, and the answer names the ones that are.' },
  'card.performTransition': { he: 'ביצוע המעבר', en: 'Perform the transition' },
  'card.stateDeletesNothing': { he: 'שינוי מצב אינו מוחק דבר ואינו מסיר הרשאות. הוא משנה את מה שמותר לעשות בחברה מכאן ואילך.', en: 'Changing the state deletes nothing and removes no permission. It changes what may be done in the company from now on.' },
  'card.portfolioIsInternal': { he: 'ארגון פנימי. אינו מעניק גישה.', en: 'Internal organisation. Grants no access.' },
  'card.assignPortfolio': { he: 'שיוך תיק העבודה', en: 'Assign the portfolio' },
  'card.stateNow': { he: 'החברה נמצאת כעת במצב', en: 'The company is now in state' },
  'authoring.noCompanyAccess': { he: 'הגדרת תפקיד קובעת מה מותר למי שמחזיק בו לעשות. היא אינה מקנה גישה לאף חברה — גם תפקיד שכולל את כל ההרשאות אינו מקנה גישה ולו לחברה אחת.', en: 'Defining a role decides what whoever holds it may do. It grants access to no company: even a role holding every permission grants access to not one company.' },
  'authoring.cannotEscalate': { he: 'אפשר לכלול בתפקיד רק הרשאות שקיימות בתפקידים שלך. הרשאות שקובעות מי מקבל הרשאות שמורות לבעלים של הארגון.', en: 'A role may include only permissions your own roles already hold. The permissions that decide who receives permissions are reserved to the owner of the organisation.' },
  'authoring.emptyIsValid': { he: 'תפקיד ללא הרשאות הוא הגדרה תקינה. כך מגדירים תפקיד לפני שמחליטים מה מותר בו.', en: 'A role with no permissions is a valid definition. It is how a role is created before deciding what it allows.' },
  'authoring.editReplaces': { he: 'בעריכת תפקיד קיים, רשימת ההרשאות שלו תוחלף בדיוק במה שמסומן כאן — הרשאה שסימונה יוסר תוסר מכל מי שמחזיק בתפקיד, מיד.', en: 'When editing an existing role, its permission list is replaced by exactly what is ticked here: a permission whose tick is removed is removed from everyone holding the role, immediately.' },
  'authoring.newRole': { he: 'תפקיד חדש', en: 'A new role' },
  'authoring.roleCodeHint': { he: 'מזהה קבוע. אינו משתנה לאחר ההגדרה.', en: 'A permanent identifier. It does not change after definition.' },
  'authoring.roleCodeFixed': { he: 'מזהה התפקיד אינו ניתן לשינוי בעריכה. שינוי שלו היה מנתק את הרשומות ביומן מהתפקיד שהן מתארות.', en: 'The role identifier cannot be changed when editing. Changing it would cut the journal records loose from the role they describe.' },
  'authoring.auditedImmediate': { he: 'ההגדרה נרשמת ביומן הביקורת יחד עם מי שביצע אותה. שינוי בתפקיד קיים חל מיד על כל מי שמחזיק בו.', en: 'The definition is recorded in the audit journal together with who made it. A change to an existing role applies immediately to everyone holding it.' },
  'authoring.savedGrants': { he: 'התפקיד נשמר ומקנה הרשאות', en: 'The role was saved and grants permissions' },
  'authoring.toAssignment': { he: 'מעבר להענקת התפקיד למשתמש', en: 'Go to assigning the role to a user' },
  'conn.backToList': { he: 'חזרה לרשימת מקורות הנתונים', en: 'Back to the data-source list' },
  'conn.linkToCompany': { he: 'קישור לחברה', en: 'Link to a company' },
  'conn.agentState': { he: 'מצב הקשר עם תוכנת החיבור', en: 'State of contact with the connector software' },
  'conn.allOrNothing': { he: 'כל הפרטים נשמרים יחד. הגדרה חלקית אינה נשמרת, כדי שחיבור לא יתנהג כאילו הוגדר בזמן שחסרים לו פרטים.', en: 'All the details are stored together. A partial configuration is not stored, so a connection never behaves as though it were configured while details are missing.' },
  'conn.probe': { he: 'בדיקת חיבור', en: 'Check the connection' },
  'conn.probeIsReadOnly': { he: 'בדיקת החיבור פונה למערכת המקור ואינה משנה בה דבר. הפנייה נרשמת ביומן הביקורת של הארגון.', en: 'A connectivity check contacts the source system and changes nothing in it. The contact is recorded in the audit journal of this organisation.' },
  'conn.externalIdVerbatim': { he: 'המזהה במערכת המקור נשמר כפי שהוא, בלי לשנות בו דבר. מזהים דומים אינם אותו מזהה, וקישור שגוי מכניס נתונים של לקוח אחד לתיק של אחר.', en: 'The identifier in the source system is stored verbatim, unchanged. Similar identifiers are not the same identifier, and a wrong link puts the data of one client into the file of another.' },
  'conn.confidenceIsClosed': { he: 'רשימה סגורה, לא אחוז.', en: 'A closed list, not a percentage.' },
  'conn.recordLink': { he: 'רישום הקישור', en: 'Record the link' },
  'enrol.step1Numbered': { he: '1 · התקנת יישומון אימות', en: '1 · Install an authenticator app' },
  'enrol.appsHint': { he: 'הרישום דורש יישומון אימות בטלפון — למשל Google Authenticator, Microsoft Authenticator או כל יישומון TOTP אחר. אם כבר מותקן אצלך יישומון כזה, אפשר לדלג לשלב הבא.', en: 'Setting this up needs an authenticator app on the phone, for example Google Authenticator, Microsoft Authenticator or any other TOTP app. If you already have one, skip to the next step.' },
  'enrol.downloadAndroid': { he: 'הורדה ל-Android', en: 'Download for Android' },
  'enrol.downloadIphone': { he: 'הורדה ל-iPhone', en: 'Download for iPhone' },
  'enrol.installFirst': { he: 'התקינו את היישומון לפני ההתחלה.', en: 'Install the app before starting.' },
  'enrol.keyOnlyOnce': { he: 'המפתח נוצר בשרת ברגע שמתחילים את הרישום, ומוצג פעם אחת בלבד. עד אז אין כאן מפתח להוסיף ליישומון.', en: 'The key is created on the server when setup begins, and shown once only. Until then there is no key here to add to the app.' },
  'enrol.scanOrManual': { he: 'סרוק את הקוד ביישומון האימות, או הוסף את החשבון ידנית בעזרת המפתח שלמטה.', en: 'Scan the code with the authenticator app, or add the account by hand using the key below.' },
  'enrol.rotatingCodePrefix': { he: 'קוד מתחלף בן', en: 'A rotating code of' },
  'enrol.rotatingCodeSuffix': { he: 'ספרות, מתחדש כל 30 שניות', en: 'digits, renewed every 30 seconds' },
  'enrol.restart': { he: 'התחלת רישום מחדש', en: 'Start setting up again' },
  'enrol.restartNote': { he: 'מנפיק מפתח חדש ומבטל את הקודם.', en: 'Issues a new key and cancels the previous one.' },
  'enrol.step3Numbered': { he: '3 · קודי השחזור — יש לשמור אותם עכשיו', en: '3 · Recovery codes — save them now' },
  'enrol.recoveryWhat': { he: 'אלה קודי השחזור שלך. כל קוד משמש פעם אחת, והם הדרך היחידה להיכנס אם אין גישה ליישומון האימות.', en: 'These are your recovery codes. Each works once, and they are the only way in if the authenticator app is out of reach.' },
  // SHORTENED, NOT REMOVED, in the sweep of 2026-10-28: the codes are stored one-way, so this is
  // the only moment the fact can help anybody, and it is a fact rather than an explanation.
  'enrol.recoveryOneWay': { he: 'לא ניתן להציג שוב — העתיקו עכשיו.', en: 'They cannot be shown again. Copy them now.' },
  'enrol.savedThem': { he: 'שמרתי — חזרה למסך האבטחה', en: 'I have saved them — back to the security screen' },
  'assistant.thisScreen': { he: 'המסך הזה', en: 'This screen' },
  /* A NAME, NOT A QUESTION. **Owner, 2026-10-28:** *"המלל של הכותרת לא צריך
     להיות שאלות, זה לא מקצועי, בבקשה תסדר את השמות כמו שצריך, משתמש, הרשאות."* */
  'assistant.limitsToggle': { he: 'מגבלות העוזר', en: 'Assistant limits' },
  'home.connectSource': { he: 'הגדרת חיבור למקור נתונים', en: 'Set up a data-source connection' },
  'assistant.askLabel': { he: 'שאלה על המערכת', en: 'A question about the system' },
  'assistant.askPlaceholder': { he: 'למשל: איך מוסיפים משתמש ונותנים לו גישה לחברה?', en: 'For example: how do I add a user and give them access to a company?' },
  'assistant.askAction': { he: 'חפש במדריך', en: 'Search the guide' },
  'assistant.noAnswerTitle': { he: 'אין לי תשובה מבוססת לשאלה הזאת', en: 'I have no grounded answer to that' },
  'assistant.noAnswerBody': { he: 'לא נמצא ערך במדריך שעונה על מה ששאלת. נסה לנסח במילים אחרות, או העתק את קוד הפנייה לתמיכה שלמטה ומסור אותו. איני מנחש תשובה.', en: 'No guide entry answers what you asked. Try different words, or copy the support reference code below and pass it on. I do not guess an answer.' },
  'assistant.withheld': { he: 'יש למדריך תשובה נוספת לשאלה הזאת, והיא דורשת הרשאה שאין לך. מנהל הארגון יכול לבצע אותה או להעניק לך את ההרשאה.', en: 'The guide holds a further answer to this question, and it needs a permission you do not have. An organisation administrator can perform it or grant you the permission.' },
  /* 2026-11-03. The three the Owner asked the assistant for. */
  'assistant.suggestTitle': { he: 'אולי התכוונת ל', en: 'Perhaps you meant' },
  'assistant.otherCategory': { he: 'אחר', en: 'Something else' },
  'assistant.contactTitle': { he: 'לא מצאת מה שחיפשת?', en: 'Did not find what you needed?' },
  'assistant.contactBody': {
    he: 'העוזר עונה רק מתוך מדריך המערכת ואינו מנחש. אפשר לכתוב אל התמיכה במילים חופשיות.',
    en: 'The assistant answers only from the system guide and does not guess. You can write to support in your own words.',
  },
  'assistant.contactAction': { he: 'כתיבה לתמיכה', en: 'Write to support' },
  'assistant.contactSubject': { he: 'פנייה מתוך GL-POINT — חשבון', en: 'A question from GL-POINT — account' },
  'assistant.contactNone': {
    he: 'לא הוגדרה כתובת תמיכה בשרת הזה. יש למסור את קוד הפנייה שלמטה למי שמתפעל את המערכת.',
    en: 'No support address is configured on this server. Pass the reference code below to whoever operates the system.',
  },
  'assistant.goThere': { he: 'למסך המתאים', en: 'Go to that screen' },
  'settings.tab.details': { he: 'פרטי הארגון', en: 'Organisation' },
  'settings.tab.people': { he: 'משתמשים והרשאות', en: 'People and permissions' },
  'settings.tab.companies': { he: 'חברות ונתונים', en: 'Companies and data' },
  'settings.tab.billing': { he: 'מנוי וחשבוניות', en: 'Subscription and invoices' },
  'settings.tab.security': { he: 'אבטחה', en: 'Security' },
  'settings.tabs.aria': { he: 'נושאי ההגדרות', en: 'Settings topics' },
  'settings.sec.office': { he: 'הארגון', en: 'The office' },
  'settings.sec.officeLead': {
    he: 'מי עובד כאן, מה מותר לכל אחד, ומה נרכש.',
    en: 'Who works here, what each of them may do, and what has been bought.',
  },
  'settings.sec.clients': { he: 'חברות ולקוחות', en: 'Companies and clients' },
  'settings.sec.clientsLead': {
    he: 'החברות עצמן, מי רשאי לפתוח כל אחת מהן, ומאיפה מגיעים הנתונים.',
    en: 'The companies themselves, who may open each one, and where their data comes from.',
  },
  'settings.sec.security': { he: 'אבטחה וחשבון', en: 'Security and account' },
  'settings.sec.securityLead': {
    he: 'מה נעשה בארגון, ומה מגן על החשבון שלך.',
    en: 'What has been done in the office, and what protects your account.',
  },
  'settings.e.users': { he: 'משתמשים', en: 'Users' },
  'settings.e.usersLead': {
    he: 'מי קיים, מי הוזמן וטרם הצטרף, ומי הושבת.',
    en: 'Who exists, who was invited and has not joined, and who was disabled.',
  },
  'settings.e.roles': { he: 'תפקידים והרשאות', en: 'Assign roles' },
  'settings.e.rolesLead': {
    he: 'שיוך תפקיד למשתמש, ומה כל תפקיד מתיר.',
    en: 'Give a person a role, and see what each role allows.',
  },
  'settings.e.roleAuthoring': { he: 'הגדרת תפקידים', en: 'Build roles' },
  'settings.e.roleAuthoringLead': {
    he: 'הרכבת תפקיד חדש מהרשאות, ועריכת תפקיד קיים.',
    en: 'Compose a new role out of permissions, and edit an existing one.',
  },
  'settings.e.subscription': { he: 'מנוי ומכסות', en: 'Subscription and quotas' },
  'settings.e.subscriptionLead': {
    he: 'מה נרכש ומה בשימוש.',
    en: 'What was bought, and what is in use.',
  },
  'settings.e.companies': { he: 'חברות', en: 'Companies' },
  'settings.e.companiesLead': {
    he: 'רשימת החברות, הקמת חברה חדשה, וכרטיס החברה.',
    en: 'The list of companies, creating a new one, and the company record.',
  },
  'settings.e.access': { he: 'הרשאות לחברות', en: 'Company access' },
  'settings.e.accessLead': {
    he: 'אילו חברות כל משתמש רשאי לפתוח. גישה היא תמיד הענקה מפורשת.',
    en: 'Which companies each person may open. Access is always granted explicitly.',
  },
  'settings.e.sources': { he: 'מקורות נתונים', en: 'Data sources' },
  'settings.e.sourcesLead': {
    he: 'החיבורים למערכות חיצוניות, מצבם וטריות הנתונים.',
    en: 'Connections to external systems, their state, and how current their data is.',
  },
  'settings.e.audit': { he: 'אבטחה ויומן ביקורת', en: 'Security and audit log' },
  'settings.e.auditLead': {
    he: 'פעולות שבוצעו, ניסיונות שנדחו, והרשאות תמיכה.',
    en: 'What was done, what was refused, and support access.',
  },
  'settings.e.personal': { he: 'אבטחת החשבון', en: 'Account security' },
  'settings.e.personalLead': {
    he: 'איפה החשבון מחובר, ניתוק התחברויות, ואימות דו-שלבי.',
    en: 'Where the account is signed in, ending sessions, and two-step verification.',
  },
  'settings.backToHome': { he: 'עמוד הבית', en: 'Home' },
  /*
   * ── A CATEGORY OF ITS OWN. **Owner, 2026-11-01.** *"העבר את ההתאמה האישית אל ההגדרות."* ─────
   *
   * The lead says whose settings these are, because it is the one card on this landing that
   * configures THE PERSON rather than the office: every other category changes something every
   * colleague sees, and this one changes one home screen.
   */
  'settings.cat.personalisation': { he: 'התאמה אישית', en: 'Personalisation' },
  'settings.cat.personalisationLead': {
    he: 'הכרטיסים שיוצגו בדף הבית שלך והסדר ביניהם',
    en: 'Which cards appear on your home screen, and the order between them',
  },

  // ---- The support assistant (section 5, 2026-10-26) ------------------------------------------
  //
  // The panel's own chrome. The ANSWERS live in `support/guide.ts`, which carries both languages
  // inline: the knowledge is a table that is reviewed as a whole, and splitting it across two
  // files would make the review two files.
  'assistant.name': { he: 'עוזר GL-POINT', en: 'GL-POINT Assistant' },
  'assistant.close': { he: 'סגירה', en: 'Close' },
  'assistant.provenance': {
    he: 'התשובות כאן לקוחות ממדריך המערכת המאושר. הן אינן נוצרות בזמן אמת ואינן מנחשות.',
    en: 'These answers are taken from the approved system guide. They are not generated and nothing here is a guess.',
  },
  'assistant.whatIsThis': { he: 'מה המסך הזה', en: 'What this screen is' },
  'assistant.noEntryTitle': {
    he: 'אין לי ערך מבוסס למסך הזה',
    en: 'I have no grounded entry for this screen',
  },
  'assistant.noEntryBody': {
    he: 'זה מה שחסר — לא שאין תשובה. אם משהו כאן אינו מתנהג כצפוי, פנה לתמיכה וציין באיזה מסך היית ומה ניסית לעשות.',
    en: 'That is what is missing — not that there is no answer. If something here is not behaving as you expect, contact support and say which screen you were on and what you were trying to do.',
  },
  'assistant.nothingForYouTitle': {
    he: 'אין ערכים שרלוונטיים להרשאות שלך',
    en: 'No entry here applies to your permissions',
  },
  'assistant.nothingForYouBody': {
    he: 'למסך הזה יש ערכים, וכולם נוגעים לפעולות שאינן בהרשאות שלך. זו אינה תקלה.',
    en: 'This screen has entries, and all of them concern actions your permissions do not cover. That is not a fault.',
  },
  'assistant.limit': {
    he: 'איני רואה את יומן הביקורת, את השרת או את הנתונים שלך, ולכן איני יכול לקבוע שמדובר בתקלת תוכנה. קביעה כזו דורשת ראיות שאין לי.',
    en: 'I cannot see the audit log, the server or your data, so I cannot decide that something is a software defect. That finding needs evidence I do not hold.',
  },
  'assistant.referenceCode': {
    he: 'בכל כשל המערכת מציגה קוד לפנייה לתמיכה. העתק אותו — הוא מאפשר למצוא את האירוע ביומן.',
    en: 'Every failure shows a support reference code. Copy it — it is what finds the event in the log.',
  },

  // ── §6 · THE TASKS MODULE ────────────────────────────────────────────────────────────
  //
  // TWO WORDS THAT ARE NOT INTERCHANGEABLE AND MUST NOT BECOME SO IN THE INTERFACE: משימה is
  // something to DO and הודעה is something to READ. The schema separates them with `item_kind`
  // for exactly that reason, and a screen that called both "התראה" would undo it where it
  // matters — in front of the person deciding what to work on.
  'tasks.title': { he: 'משימות והודעות', en: 'Tasks and notices' },
  'tasks.lead': {
    /* ארגון AND NOT משרד: `organisationWording.test.ts` holds the product to one noun for the
       environment being managed, and משרד is the retired one. It caught this line. */
    he: 'משימות והודעות של הארגון. כל הפריטים נשמרים במערכת ואינם נשלחים לאף גורם מחוץ לה.',
    en: "The office's tasks and notices. Everything here stays inside the product and is sent nowhere.",
  },
  'tasks.scope.mine': { he: 'המשימות שלי', en: 'My tasks' },
  'tasks.scope.today': { he: 'לטיפול היום', en: 'Due today' },
  'tasks.scope.all': { he: 'הכל', en: 'Everything' },
  'tasks.empty': { he: 'אין פריטים לתצוגה הזו.', en: 'Nothing in this view.' },
  'tasks.kind.task': { he: 'משימה', en: 'Task' },
  'tasks.kind.notice': { he: 'הודעה', en: 'Notice' },
  'tasks.state.open': { he: 'פתוחה', en: 'Open' },
  'tasks.state.in_progress': { he: 'בטיפול', en: 'In progress' },
  'tasks.state.done': { he: 'הושלמה', en: 'Done' },
  'tasks.state.cancelled': { he: 'בוטלה', en: 'Cancelled' },
  'tasks.origin.automatic': { he: 'נוצרה אוטומטית מכלל מועד', en: 'Raised automatically from a deadline rule' },
  'tasks.due': { he: 'לביצוע עד', en: 'Due' },
  'tasks.noDue': { he: 'בלי תאריך יעד', en: 'No due date' },
  'tasks.assignedTo': { he: 'אחראי', en: 'Assigned to' },
  'tasks.unassigned': { he: 'ללא אחראי', en: 'Unassigned' },
  'tasks.raisedBy': { he: 'פתח', en: 'Raised by' },
  'tasks.unread': { he: 'לא נקרא', en: 'Unread' },

  // ── raising one ────────────────────────────────────────────────────────────────
  'tasks.new': { he: 'משימה חדשה', en: 'New task' },
  'tasks.newTitle': { he: 'מה צריך לקרות', en: 'What needs to happen' },
  'tasks.newBody': { he: 'פרטים (לא חובה)', en: 'Details (optional)' },
  'tasks.newDue': { he: 'תאריך יעד (לא חובה)', en: 'Due date (optional)' },
  'tasks.newAssignee': { he: 'אחראי', en: 'Assign to' },
  'tasks.assignToMe': { he: 'אליי', en: 'To me' },
  'tasks.create': { he: 'פתיחת משימה', en: 'Raise it' },
  'tasks.created': { he: 'המשימה נפתחה.', en: 'The task was raised.' },

  // ── acting on one ────────────────────────────────────────────────────────────
  'tasks.open': { he: 'פתיחת הפריט', en: 'Open the item' },
  'tasks.start': { he: 'לקיחה לטיפול', en: 'Start it' },
  'tasks.complete': { he: 'סימון כהושלמה', en: 'Mark it done' },
  'tasks.cancel': { he: 'ביטול המשימה', en: 'Cancel it' },
  'tasks.comment': { he: 'הוספת תגובה', en: 'Add a comment' },
  /* The FIELD and the BUTTON had the same words, so a person could not tell the label from the
     control -- and a test looking for the second control found one. The field says what it
     holds; the button says what pressing it does. */
  'tasks.commentLabel': { he: 'תגובה', en: 'Comment' },
  'tasks.commentPlaceholder': { he: 'מה לעדכן', en: 'What is the update' },
  'tasks.comments': { he: 'תגובות', en: 'Comments' },
  'tasks.noComments': { he: 'אין תגובות.', en: 'No comments yet.' },
  'tasks.history': { he: 'היסטוריית טיפול', en: 'Handling history' },
  'tasks.bySystem': { he: 'המערכת', en: 'The product' },
  'tasks.assign': { he: 'שינוי אחראי', en: 'Change the assignee' },
  'tasks.event.created': { he: 'נפתחה', en: 'Raised' },
  'tasks.event.assigned': { he: 'הוקצתה', en: 'Assigned' },
  'tasks.event.state_changed': { he: 'שונה מצב', en: 'State changed' },
  'tasks.event.commented': { he: 'התקבלה תגובה', en: 'Commented' },
  'tasks.event.reopened': { he: 'נפתחה מחדש', en: 'Reopened' },

  // ── and the one sentence a person reads when the product refuses them ────────────────
  //
  // THE REFUSAL NAMES WHAT IS MISSING AND DOES NOT PRETEND THE CONTROL IS ABSENT. A clerk who
  // cannot cancel somebody's task needs to know that cancelling requires an authority they do not
  // hold — so they ask for it, rather than concluding the product is broken.
  'tasks.refused.notYours': {
    he: 'הפריט הזה אינו מוקצה לך ולא נפתח על ידך, ולכן טיפול בו דורש הרשאת ניהול משימות.',
    en: 'This item is neither assigned to nor raised by you, so acting on it needs the task-management capability.',
  },
  'tasks.refused.cancel': {
    he: 'ביטול משימה הוא החלטה שלא תבוצע, והוא דורש הרשאת ניהול משימות. סימון כהושלמה אינו דורש.',
    en: 'Cancelling an item is a decision that it will not be done and needs the task-management capability. Marking it done does not.',
  },
  'tasks.refused.stale': {
    he: 'מישהו אחר שינה את הפריט בינתיים. רענן את הרשימה ונסה שוב.',
    en: 'Somebody else moved this item. Reload the list and try again.',
  },

  // ── 0032 · THE PUBLIC DOOR ─────────────────────────────────────────
  //
  // THE ONE THING THIS SURFACE MUST NEVER IMPLY is that anything has happened. Until the mailed
  // link is opened there is no organisation, no user and no subscription -- and the words say so,
  // because a stranger who typed somebody else's address should be able to tell.
  'signup.title': { he: 'פתיחת ארגון חדש', en: 'Open a new organisation' },
  'signup.lead': {
    he: 'נרשמים כאן, מאמתים את כתובת הדואר, ואז בוחרים תכנית. עד שהקישור שיגיע בדואר ייפתח — לא נוצר שום דבר.',
    en: 'Register here, verify the address, then choose a plan. Until the mailed link is opened, nothing has been created.',
  },
  'signup.closed': {
    he: 'הרשמה עצמית אינה פתוחה כרגע. ניתן לפנות אלינו ונפתח את הארגון.',
    en: 'Self-service registration is not open. Contact us and we will set the organisation up.',
  },
  'signup.email': { he: 'כתובת דואר לאימות', en: 'Email address to verify' },
  'signup.legalName': { he: 'שם הארגון כפי שהוא רשום', en: 'Registered name of the organisation' },
  'signup.displayName': { he: 'שם איש הקשר (לא חובה)', en: 'Contact name (optional)' },
  'signup.submit': { he: 'שליחת קישור אימות', en: 'Send a verification link' },
  'signup.sentTitle': { he: 'נשלח קישור אימות', en: 'A verification link is on its way' },
  'signup.sentBody': {
    he: 'אם הכתובת תקינה, יגיע אליה קישור חד-פעמי. עד שהוא ייפתח לא נוצר ארגון, לא משתמש ולא מנוי.',
    en: 'If the address is valid, a one-time link will arrive. Until it is opened no organisation, user or subscription has been created.',
  },
  'signup.plansTitle': { he: 'התכניות המוצעות', en: 'The plans on offer' },
  'signup.noPlans': {
    he: 'אין כרגע תכנית שניתן לרכוש בהרשמה עצמית.',
    en: 'No plan is available for self-service purchase at the moment.',
  },
  /* ── STEP FOUR: THE PAYMENT-METHOD CHECK. ו-16, 2026-10-30. ──────────────────────────
     The Owner: *"בדיקת אמצעי תשלום אצל הספק, ורק אז תחילת הניסיון ללא חיוב."* The body says WHY it
     is asked for, because a card request with no stated reason reads as a charge. */
  'signup.payment.title': { he: 'אמצעי תשלום', en: 'Payment method' },
  'signup.payment.body': {
    he: 'לפני שתקופת הניסיון מתחילה, אמצעי התשלום נבדק אצל חברת הסליקה. לא מתבצע חיוב, ופרטי הכרטיס לא נשמרים אצלנו.',
    en: 'Before the trial starts, the payment method is checked with the payment provider. Nothing is charged, and the card details are not stored here.',
  },
  'signup.payment.check': { he: 'בדיקת אמצעי תשלום', en: 'Check the payment method' },
  'signup.payment.goToProvider': {
    he: 'המשך לעמוד התשלום המאובטח',
    en: 'Continue to the secure payment page',
  },
  'signup.payment.pageBody': {
    he: 'הכרטיס מוקלד בעמוד של חברת הסליקה. פרטי הכרטיס אינם עוברים דרך המערכת הזאת ואינם נשמרים בה, ולא מבוצע חיוב — רק בדיקה של הכרטיס.',
    en: 'The card is typed on the clearing provider\u2019s own page. The card details do not pass through this system and are not stored in it, and no charge is made \u2014 the card is only checked.',
  },
  'signup.payment.checked': {
    he: 'אמצעי התשלום נבדק. אפשר להמשיך להקמת הארגון.',
    en: 'The payment method has been checked. You can go on to create the organisation.',
  },
  /*
   * ── WHO ANSWERED, NAMED ON THE SCREEN. **2026-11-01.** ────────────────────────────────────
   *
   * The Owner, on this journey: *"הבחן בין בדיקה מדומה לבין בדיקה מול קארדקום."* The
   * sentence above says a payment method was checked and says nothing about by WHOM — so a
   * development harness answering a stub produced exactly the screen a real clearing company
   * produces. The provider is in the answer and was being discarded; it is now on the screen, so
   * a simulated check cannot look like a real one. *"אין להציג יכולת מדומה כפעילה."*
   */
  'signup.payment.checkedWith': { he: 'הבדיקה בוצעה מול: ', en: 'Checked with: ' },
  'signup.payment.required': {
    he: 'לא ניתן להקים ארגון לפני שאמצעי התשלום נבדק.',
    en: 'The organisation cannot be created before the payment method has been checked.',
  },
  'signup.price': { he: 'מחיר', en: 'Price' },
  'signup.cycle': { he: 'מחזור חיוב', en: 'Billing cycle' },
  'signup.cycle.month': { he: 'חודשי', en: 'Monthly' },
  'signup.trial': { he: 'תקופת ניסיון ללא חיוב', en: 'Free trial' },
  'signup.trialDays': { he: 'ימים', en: 'days' },
  'signup.firstCharge': { he: 'מועד החיוב הראשון', en: 'First charge' },
  'signup.kindLabel': { he: 'סוג הארגון', en: 'Kind of organisation' },
  /* ראיית חשבון AND NOT משרד רואי חשבון, and `organisationWording.test.ts` is right to have
     refused it: this label names a kind of ORGANISATION, and the product settled on ארגון for
     that. Reintroducing the retired noun here would put the ambiguity back in the one place a
     stranger meets the word first. The profession names the kind perfectly well. */
  'signup.kind.cpa_firm': { he: 'ראיית חשבון', en: 'Accounting practice' },
  'signup.kind.direct_business': { he: 'עסק עצמאי', en: 'A business, directly' },
  'signup.consent': {
    he: 'קראתי ואני מסכים למחיר, למחזור החיוב ולמועד החיוב הראשון המוצגים כאן',
    en: 'I have read and agree to the price, the billing cycle and the first charge date shown here',
  },
  'signup.create': { he: 'פתיחת הארגון', en: 'Open the organisation' },
  'signup.createdTitle': { he: 'הארגון נפתח', en: 'The organisation is open' },
  'signup.createdBody': {
    he: 'נשלח אליך קישור נוסף לקביעת סיסמה. הסיסמה נקבעת רק דרך הקישור ההוא, ולא במסך הזה.',
    en: 'A second link has been sent so you can set a password. A password is only ever set through that link, not on this screen.',
  },
  'signup.goSignIn': { he: 'למסך הכניסה', en: 'Go to sign-in' },

  // ── the mailed landing ──────────────────────────────────────────────
  'signup.verify.title': { he: 'אימות הכתובת', en: 'Verifying the address' },
  'signup.verify.working': { he: 'מאמת את הקישור...', en: 'Checking the link...' },
  'signup.verify.noTokenTitle': { he: 'אין קישור בכתובת', en: 'There is no link in this address' },
  'signup.verify.noTokenBody': {
    he: 'הגעת לכאן בלי הקישור שנשלח בדואר. לא נוסה לאמת שום דבר.',
    en: 'You arrived without the mailed link. Nothing has been attempted.',
  },
  'signup.verify.verified': { he: 'הכתובת אומתה', en: 'The address is verified' },

  // ── 0030/0031 · REPORTING DEADLINES ────────────────────────────
  //
  // THREE WORDS THAT MUST NOT BLUR INTO ONE ANOTHER ON THIS SURFACE:
  //
  //   תבנית   a platform row with no filing day. It reminds nobody, of anything, ever.
  //   מאומת   somebody established the day against a source and recorded both.
  //   פעיל    the rule may now raise reminders -- and only a verified rule can be.
  //
  // The database refuses the fourth combination (active and unverified), so the interface never has
  // to explain it. What it DOES have to explain is that a template is not a gap in the product: it
  // is this product declining to assert a filing date on its own authority.
  'deadlines.title': { he: 'מועדי דיווח', en: 'Reporting deadlines' },
  'deadlines.lead': {
    he: 'מועדי הדיווח שהארגון מתחזק. כל מועד נשמר עם המקור שלו ועם מועד התחולה, ורק כלל מאומת יכול להיות פעיל.',
    en: 'The deadlines this office maintains. Each one keeps its source and the date it applies from, and only a verified rule can be active.',
  },
  'deadlines.templateNote': {
    he: 'תבנית היא שורה שלא נקבע בה יום דיווח. המערכת אינה קובעת מועדי דיווח מטעמה; הארגון קובע אותם מול המקור הרשמי ומוסיף את האסמכתא.',
    en: 'A template is a row with no filing day. The product does not assert filing dates of its own; the office establishes them against the official source and records the attribution.',
  },
  'deadlines.empty': { he: 'אין כללי מועד לתצוגה.', en: 'No deadline rules to show.' },
  'deadlines.mark.template': { he: 'תבנית', en: 'Template' },
  'deadlines.mark.own': { he: 'כלל של הארגון', en: 'The office own rule' },
  'deadlines.mark.verified': { he: 'מאומת', en: 'Verified' },
  'deadlines.mark.unverified': { he: 'לא מאומת', en: 'Unverified' },
  'deadlines.mark.active': { he: 'פעיל', en: 'Active' },
  'deadlines.mark.inactive': { he: 'לא פעיל', en: 'Inactive' },
  'deadlines.field.name': { he: 'שם הדיווח', en: 'What the report is called' },
  'deadlines.field.period': { he: 'תקופת הדיווח', en: 'Reporting period' },
  'deadlines.field.day': { he: 'יום הדיווח בחודש', en: 'Day of the month the report is due' },
  'deadlines.field.monthOffset': {
    he: 'כמה חודשים אחרי סוף התקופה',
    en: 'How many months after the period closes',
  },
  'deadlines.field.lead': {
    he: 'כמה ימים לפני להציג תזכורת',
    en: 'Days of warning before the due date',
  },
  'deadlines.field.source': { he: 'המקור למועד', en: 'Where the date came from' },
  'deadlines.field.effectiveFrom': { he: 'תחולה מתאריך', en: 'Applies from' },
  'deadlines.field.effectiveTo': {
    he: 'בתוקף עד תאריך (לא חובה)',
    en: 'In force until (optional)',
  },
  'deadlines.field.verification': { he: 'מצב האימות', en: 'Verification' },
  'deadlines.field.activeToggle': {
    he: 'כלל פעיל — ירים תזכורות',
    en: 'Active - it will raise reminders',
  },
  'deadlines.period.monthly': { he: 'חודשי', en: 'Monthly' },
  'deadlines.period.bi_monthly': { he: 'דו-חודשי', en: 'Every two months' },
  'deadlines.period.quarterly': { he: 'רבעוני', en: 'Quarterly' },
  'deadlines.period.annual': { he: 'שנתי', en: 'Annual' },
  'deadlines.noDay': { he: 'טרם נקבע יום דיווח', en: 'No filing day established' },
  'deadlines.noSource': { he: 'ללא מקור רשום', en: 'No source recorded' },
  'deadlines.edit': { he: 'עריכת הכלל', en: 'Edit the rule' },
  'deadlines.editClose': { he: 'סגירה', en: 'Close' },
  'deadlines.save': { he: 'שמירת הכלל', en: 'Save the rule' },
  'deadlines.saved': { he: 'הכלל נשמר.', en: 'The rule was saved.' },
  'deadlines.verifyHint': {
    he: 'סימון כמאומת דורש יום דיווח, מקור ותאריך תחולה. כלל שאינו מאומת אינו יכול להיות פעיל.',
    en: 'Marking a rule verified needs a filing day, a source and a date it applies from. An unverified rule cannot be active.',
  },

  // ── one client answer: which of those reports THIS company files ────────────
  //
  // THE ABSENCE OF A ROW IS THE ANSWER, so this surface lists every rule the office has and not
  // only the ones already decided. An office looking at a new client must be able to see what it
  // has NOT yet decided -- which is the whole list, on the day the client is onboarded.
  'deadlines.company.title': {
    he: 'מועדי דיווח של החברה',
    en: 'This client reporting deadlines',
  },
  'deadlines.company.lead': {
    he: 'אילו דיווחים החברה הזו מדווחת, ובמה היא שונה מכלל הארגון. כל עוד לא סומן דיווח — לא תוצג עליו שום תזכורת.',
    en: 'Which reports this client files, and where it differs from the office rule. Until a report is marked, no reminder is raised for it.',
  },
  'deadlines.company.enabled': { he: 'מדווחת', en: 'Files it' },
  'deadlines.company.disabled': { he: 'לא מדווחת', en: 'Does not file it' },
  'deadlines.company.enable': { he: 'סימון כמדווחת', en: 'Mark as filed by this client' },
  'deadlines.company.disable': { he: 'ביטול הסימון', en: 'Stop filing it' },
  'deadlines.company.override': {
    he: 'החברה הזו שונה מכלל הארגון',
    en: 'This client differs from the office rule',
  },
  'deadlines.company.overrideHint': {
    he: 'שדה שנשאר ריק משמעו כמו בכלל הארגון.',
    en: 'A field left empty means as the office rule says.',
  },
  'deadlines.company.note': { he: 'למה כך בחברה הזו', en: 'Why this client is set this way' },
  'deadlines.company.saved': { he: 'ההגדרה נשמרה.', en: 'The setting was saved.' },
  'deadlines.company.ruleNotActive': {
    he: 'כלל הארגון אינו פעיל, ולכן גם אם החברה מסומנת לא תוצג תזכורת.',
    en: 'The office rule is not active, so even a marked client receives no reminder yet.',
  },
  'deadlines.company.enabledAt': { he: 'סומן בתאריך', en: 'Marked on' },

  // ── the settings index entry ─────────────────────────────────────
  'settings.cat.deadlines': { he: 'מועדי דיווח', en: 'Reporting deadlines' },
  'settings.cat.deadlinesLead': {
    he: 'איזה דיווחים הארגון מתזמן, באיזה יום ועל סמך איזה מקור.',
    en: 'Which reports the office tracks, on what day, and on whose authority.',
  },

  // ── §4 · THE MARKET READINGS ───────────────────────────────────────────────────
  //
  // THREE ABSENCES THAT ARE NOT THE SAME ABSENCE, and the Owner separated them by name:
  // *"כשל טעינה או היעדר חיבור לא יוצגו כאפס."* Nobody has chosen a publisher; a publisher was
  // chosen and has produced nothing; and the last attempt failed. A single "אין נתונים" for all
  // three would send one person looking for a setting that exists and another for one that does not.
  'market.notConfigured': {
    he: 'לא נבחר מקור לנתון הזה. היכולת קיימת והמקור הוא הגדרה.',
    en: 'No publisher has been chosen for this reading. The capability exists and the source is a setting.',
  },
  'market.noReadingYet': {
    he: 'מקור הוגדר וטרם התקבל ממנו נתון.',
    en: 'A source is configured and has not yet returned a reading.',
  },
  'market.lastAttemptFailed': {
    he: 'הניסיון האחרון לפנות למקור לא הצליח. הסיבה רשומה למפעיל.',
    en: 'The last attempt to reach the source failed. The reason is recorded for an operator.',
  },
  'market.noAnswer': {
    he: 'הקריאה לנתוני השוק לא הושלמה.',
    en: 'The market read did not complete.',
  },
  /*
   * ── THE PUBLISHER'S OWN DATE, IN THE WORDS HE ASKED FOR. **Owner, 2026-11-01.** ────────────
   *
   *     *"צריך להיות נכון ליום.. DD/MM/YY"*
   *
   * `נכון ל-` with a hanging hyphen was the half-phrase; `נכון ליום` is the whole one and is a
   * character shorter on the screen.
   */
  'market.asOf': { he: 'נכון ליום', en: 'as of' },
  /*
   * ── TWO DATES, AND THEY WERE TOLD APART BY NOTHING. **Owner, 2026-11-01.** ──────────────────
   *
   *     *"צריך להבחין בין תאריך הפרסום לבין מועד הסנכרון."*
   *
   * The strip printed a bare `29.10.2026` beside each headline and, when the feed was stale, a
   * second bare date in a chip. Both are real and they answer different questions — WHEN THE
   * INSTITUTE PUBLISHED IT, and WHEN WE LAST MANAGED TO ASK — and a reader with two unlabelled
   * dates in one line has to guess which is which, with the plausible guess being the wrong one:
   * a date beside a headline reads as "this is how old this news is", and that is only true of
   * the first.
   *
   * ONE WORD EACH, because this is a line and not a card. `market.asOf` above is the provenance
   * wording for a NUMERIC reading, where "as of" is right; a news item is published, not valid
   * as of.
   */
  'market.publishedOn': { he: 'פורסם', en: 'Published' },
  'market.syncedOn': { he: 'סונכרן', en: 'Synced' },
  /*
   * ── THE STALE MARK, CUT TO ITS TWO FACTS. **Owner, 2026-11-01.** ───────────────────────────
   *
   * It read *"הנתון ישן מחלון הריענון של המקור. הפנייה המוצלחת האחרונה:"* — two sentences and
   * nineteen words, on a 190px card, above a date. He quoted it back among the lines he wants
   * gone: *"לא צריך את כל המלל הזה."*
   *
   * **BOTH FACTS SURVIVE, AND THAT IS THE CONSTRAINT.** The state is a warning and it is not
   * dropped: *"נתון ישן"* says the reading is behind its source's own window, and the date says
   * when we last managed to ask. What goes is the EXPLANATION of what a refresh window is —
   * which is the product teaching rather than reporting, and the chip's amber already says this
   * is not an ordinary line.
   */
  'market.stale': { he: 'נתון ישן · עדכון אחרון', en: 'Stale · last update' },
  /*
   * `%` AND `₪` ARE NOT KEYS HERE, AND A CONTROL IS WHY. `identity-language.test.ts` requires a
   * Hebrew label to contain a Hebrew letter -- that is how it catches a `he` field filled in
   * with the English -- and it refused both symbols.
   *
   * ADDING THEM TO THAT TEST'S EXCEPTION LIST WOULD HAVE BEEN THE WRONG ANSWER: the list is for
   * product names and acronyms that are genuinely the same WORD in both languages. A unit symbol
   * is a third thing -- not language at all, the same glyph in every locale, and a translator
   * handed `%` has nothing to do. The two symbols live in the component that renders them; only
   * the units that are PROSE are keys here, where a translator can reach them.
   */
  'market.unit.points': { he: 'נקודות', en: 'points' },
  /*
   * ── THE SHEKEL IN LETTERS, AND THE LITRE ABBREVIATED. **Owner, 2026-11-01.** ───────────
   *
   *     *"במקום המילה לליטר, אפשר לרשום 7.35 ש\"ח/ל'"*, and then *"עדיף הסימן ש\"ח."*
   *
   * `₪ לליטר` was two words where the trade writes one token. This is how a fuel price is
   * written on a receipt, on a pump and in a ledger, and the card is 190px wide.
   *
   * IT IS THE ONLY UNIT THAT CHANGES. `%` and `נקודות` are read the way they already are, and
   * the currency card's `₪` stays a symbol beside a rate — a rate is not a price per something.
   */
  'market.unit.ilsPerLitre': { he: 'ש\"ח/ל׳', en: 'ILS/L' },
  /*
   * ── PER WHAT. **Owner, section 2, 2026-11-01.** *"בסיס יחידות ברור."* ──────────────────────
   *
   * `יחידות: ₪` answers *in what* and not *per what*, and for an exchange rate the second
   * question is the one a reader actually has. The collector divides every entry by its OWN
   * `unit`, so the row is already shekels per ONE unit of that currency — the yen included,
   * which the publisher quotes per hundred.
   */
  'market.perCurrencyUnit': { he: 'ליחידת מטבע אחת', en: 'per one unit of the currency' },

  /*
   * ── THE CURRENCY NAMES. **Owner, section 2.** *"עם שם המטבע והקוד שלו."* ────────────────────
   *
   * The Bank of Israel's answer carries no name in any language — only `key` — so these are a
   * translation and belong here rather than in a collector. Four, for the four codes the source
   * has ever returned to this product; an unknown code renders as the code, which is what the
   * card did for all of them until today.
   */
  /*
   * ── AND THE PICKER'S OWN TWO LINES. **Owner, 2026-11-01.** *"אני רוצה לבחור איזה שער."* ─────
   */
  'home.currencyChoice': { he: 'שערי מטבע בדף הבית', en: 'Exchange rates on the home screen' },
  'home.currencyChoiceHint': {
    he: 'בחרו אילו שערים יוצגו בכרטיס. ללא בחירה יוצגו ארבעת השערים שהמקור מפרסם.',
    en: 'Choose which rates the card carries. With no choice, the four the source publishes are shown.',
  },
  'market.currency.usd': { he: 'דולר ארה״ב', en: 'US dollar' },
  'market.currency.eur': { he: 'אירו', en: 'Euro' },
  'market.currency.gbp': { he: 'לירה שטרלינג', en: 'Pound sterling' },
  'market.currency.jpy': { he: 'ין יפני', en: 'Japanese yen' },

  /*
   * ── THE WORD BESIDE THE ARROW. **Owner, section 3.** *"עלייה וירידה בצבע ובחץ."* ────────────
   *
   * Neither the arrow nor the colour reaches a screen reader: `▲` is announced as *"black
   * up-pointing triangle"* by some and skipped by others. The glyph is `aria-hidden` and one of
   * these stands beside it.
   *
   * `flat` IS A MEASUREMENT AND NOT AN ABSENCE. *"אין להמציא שינוי או להסיק אותו מאפס"* cuts
   * both ways: a publisher that said nothing moved has said something, and a row with no figure
   * at all renders no mark — no word, no glyph and no colour.
   */
  'market.change.up': { he: 'עלייה', en: 'Up' },
  'market.change.down': { he: 'ירידה', en: 'Down' },
  'market.change.flat': { he: 'ללא שינוי', en: 'Unchanged' },
} as const satisfies Record<string, Readonly<Record<Locale, string>>>;

export type TextKey = keyof typeof TEXT;

/** The whole catalogue, readable by the completeness test and by the provider. */
export const CATALOGUE: Readonly<Record<TextKey, Readonly<Record<Locale, string>>>> = TEXT;

/**
 * One string, in one language.
 *
 * NO FALLBACK ARGUMENT and no missing-key branch: `TextKey` is a union of the keys that exist, so
 * a typo is a compile error rather than a key rendered raw into the interface.
 */
export function text(key: TextKey, locale: Locale): string {
  return TEXT[key][locale];
}
